<template>
  <div class="help-center-root" :class="{ 'help-mobile': isMobileView, 'help-tab-support': activeTab === 'support', 'help-tab-feedback': activeTab === 'feedback', 'help-tab-insights': activeTab === 'insights', 'help-tab-announcements': activeTab === 'announcements' }">
    <div class="help-header-bar">
      <div class="help-tabs">
        <!-- non-admin tab order: content first (Help Articles +
             Company Docs — what customers come here to read), then
             Support Tickets (the action surface). admins still land
             on Support by default via the navbar help-icon → see
             created() below. admin-only tabs (Feedback Review /
             Assistant Insights / Announcements) trail after. -->
        <button class="help-tab" :class="{ active: activeTab === 'articles' }" @click="switchTab('articles')">Help Articles</button>
        <button v-if="showCompanyDocsTab" class="help-tab" :class="{ active: activeTab === 'company' }" @click="switchTab('company')">Company Docs</button>
        <button class="help-tab" :class="{ active: activeTab === 'support' }" @click="switchTab('support')">Support Tickets</button>
        <button v-if="isAdmin" class="help-tab help-tab-admin" :class="{ active: activeTab === 'feedback' }" @click="switchTab('feedback')">Feedback Review</button>
        <button v-if="isAdmin" class="help-tab help-tab-admin" :class="{ active: activeTab === 'insights' }" @click="switchTab('insights')">Assistant Insights</button>
        <button v-if="isAdmin" class="help-tab help-tab-admin" :class="{ active: activeTab === 'announcements' }" @click="switchTab('announcements')">Announcements</button>
      </div>
    </div>

    <!-- articles toolbar -->
    <div v-if="activeTab === 'articles'" class="help-toolbar">
      <div class="help-search-fields" :style="{ width: (articlesSidebarWidth - 16) + 'px' }">
        <b-form-input
          ref="searchInput"
          v-model="searchText"
          placeholder="Search articles..."
          size="sm"
          @keyup.enter="handleSearch"
        ></b-form-input>
        <b-button size="sm" variant="primary" @click="handleSearch" :disabled="loadingArticles">
          Search
        </b-button>
      </div>
      <div class="help-toolbar-spacer"></div>
      <b-button v-if="isAdmin" size="sm" variant="outline-secondary" @click="openLeaderboardModal" title="Article Leaderboard" class="help-leaderboard-btn">
        <i class="fa fa-trophy leaderboard-icon-only" style="display:none;"></i><span class="leaderboard-label-full"><i class="fa fa-trophy" style="margin-right: 4px;"></i>Article Leaderboard</span>
      </b-button>
    </div>

    <!-- company documents toolbar -->
    <div v-if="activeTab === 'company'" class="help-toolbar">
      <div class="help-search-fields" :style="{ width: (companySidebarWidth - 16) + 'px' }">
        <b-form-input
          v-model="searchText"
          placeholder="Search documents..."
          size="sm"
          @keyup.enter="handleSearch"
        ></b-form-input>
        <b-button size="sm" variant="primary" @click="handleSearch" :disabled="loadingCompanyDocs">
          Search
        </b-button>
      </div>
      <div class="help-toolbar-spacer"></div>
      <b-button
        v-if="canManageDocs"
        ref="manageDocsBtn"
        size="sm"
        variant="outline-primary"
        @click="openDocManageModal"
        title="Manage Documents"
        class="help-manage-docs-btn"
      >
        <i class="fa fa-cog manage-icon-only" style="display:none;"></i><span class="manage-label-full"><i class="fa fa-cog" style="margin-right: 4px;"></i>Manage Documents</span>
      </b-button>
    </div>

    <!-- HELP ARTICLES TAB -->
    <div v-if="activeTab === 'articles'" class="help-body" :class="{ 'mobile-show-detail': mobileShowDetail }">
      <div class="help-sidebar" :style="{ width: articlesSidebarWidth + 'px' }">
        <div v-if="activeSearch" class="help-search-indicator">
          <span>Results for "<strong>{{ activeSearch }}</strong>" ({{ visibleArticles.length }})</span>
          <button class="help-search-clear" @click="clearSearch">&times;</button>
        </div>
        <div v-if="loadingArticles" class="help-empty-state">
          Loading articles...
        </div>
        <div v-else-if="visibleArticles.length === 0 && activeSearch" class="help-empty-state help-empty-state-wide">
          <div class="help-no-results">
            <div class="help-no-results-header">
              <i class="fa fa-search help-no-results-icon" aria-hidden="true"></i>
              <p class="help-no-results-title">
                No articles found for <span class="help-no-results-query">"{{ activeSearch }}"</span>
              </p>
              <p class="help-no-results-sub">Let's get you unstuck.</p>
            </div>

            <!-- primary path: ask Fleet Assistant (only when FA is available for this user) -->
            <div v-if="canUseFleetAssistant" class="help-try-fa">
              <button type="button" class="help-try-fa-pill" @click="askFleetAssistant">
                <svg class="fa-chat-svg" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd" aria-hidden="true">
                  <path d="M6 3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-4l-5 4v-4H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3zM8.5 9a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 9a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM15.5 9a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z"/>
                </svg>
                Ask Fleet Assistant
              </button>
              <p class="help-try-fa-hint">It can search help articles, your company documents, and live fleet data in one place.</p>
            </div>

            <!-- secondary path: suggest a new topic -->
            <div v-if="!topicSuggestionSubmitted" class="suggest-topic-panel">
              <div class="callout-head">
                <!-- anchor icon: same outer rounded-square + inset white border
                     pattern as Uploads/FA Paused, with a white lightbulb
                     inside so the shape language stays consistent across
                     callouts while the glyph reads as "idea/suggestion". -->
                <svg class="callout-anchor-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <rect x="1" y="1" width="14" height="14" rx="2.5" ry="2.5" fill="currentColor"/>
                  <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="1.6" ry="1.6" fill="none" stroke="#fff" stroke-width="0.7"/>
                  <path fill="#fff" d="M8 4.2a2.6 2.6 0 0 0-2.6 2.6c0 .9.4 1.7 1.1 2.2v1.3h3V9c.7-.5 1.1-1.3 1.1-2.2A2.6 2.6 0 0 0 8 4.2z"/>
                  <rect fill="#fff" x="6.5" y="10.5" width="3" height="0.6" rx="0.3"/>
                  <rect fill="#fff" x="6.9" y="11.3" width="2.2" height="0.5" rx="0.25"/>
                </svg>
                <span class="fa-badge idea">Suggest a Topic</span>
              </div>
              <div class="callout-body">
                <p>Think we should cover this topic? Tell us a bit more and we'll look into it.</p>
                <b-form-textarea
                  v-model="topicSuggestionComment"
                  placeholder="What were you hoping to find? The more you share, the better article we can write."
                  rows="3"
                  max-rows="6"
                  class="help-suggest-textarea"
                ></b-form-textarea>
                <div class="suggest-topic-action">
                  <button type="button" class="callout-cta-btn" @click="submitTopicSuggestion" :disabled="topicSuggestionSubmitting">
                    <span v-if="topicSuggestionSubmitting"><b-spinner small></b-spinner> Sending...</span>
                    <span v-else>Suggest This Topic</span>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="suggestion-submitted-alert">
              <div class="callout-head">
                <!-- anchor icon: same outer rounded-square + inset white border
                     pattern used by Uploads/FA Paused, with a white checkmark
                     in place of the parking-sign P so the shape language stays
                     consistent across positive and paused states. -->
                <svg class="callout-anchor-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <rect x="1" y="1" width="14" height="14" rx="2.5" ry="2.5" fill="currentColor"/>
                  <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="1.6" ry="1.6" fill="none" stroke="#fff" stroke-width="0.7"/>
                  <path fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M4.8 8.2 L7 10.4 L11.3 5.8"/>
                </svg>
                <span class="fa-badge success">Suggestion Submitted</span>
              </div>
              <div class="callout-body">
                <p>Thanks for the suggestion — we'll review it and get an article out if we can. Your help makes this better for everyone.</p>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="visibleArticles.length === 0" class="help-empty-state">
          No articles found for your context.
        </div>
        <!-- directory mode: grouped by article_type -->
        <template v-else-if="isDirectoryMode">
          <div v-for="group in articleDirectory" :key="group.type" class="help-directory-group">
            <div class="help-directory-header" @click="$set(directoryCollapsed, group.type, !directoryCollapsed[group.type])">
              <i :class="directoryCollapsed[group.type] ? 'fa fa-chevron-right' : 'fa fa-chevron-down'"></i>
              {{ group.label }}
              <span class="help-directory-count">{{ group.articles.length }}</span>
            </div>
            <div v-show="!directoryCollapsed[group.type]">
              <button
                v-for="article in group.articles"
                :key="article.doc_id"
                type="button"
                class="help-list-item"
                :class="{ active: selectedDocId === article.doc_id }"
                @click="selectArticle(article)"
              >
                <div class="help-item-title">{{ article.title }}</div>
                <div class="help-item-summary">{{ article.summary }}</div>
                <div v-if="article.view_count" class="help-item-views" :title="article.view_count + ' views'">
                  <i class="fa fa-eye"></i> {{ article.view_count }}
                </div>
              </button>
            </div>
          </div>
        </template>
        <!-- search mode: flat list -->
        <template v-else>
          <button
            v-for="article in visibleArticles"
            :key="article.doc_id"
            type="button"
            class="help-list-item"
            :class="{ active: selectedDocId === article.doc_id }"
            @click="selectArticle(article)"
          >
            <div class="help-item-title">{{ article.title }}</div>
            <div class="help-item-summary">{{ article.summary }}</div>
            <div class="help-item-meta">
              <span class="help-badge type">{{ article.article_type || 'faq' }}</span>
              <span v-if="article.tenant_scope === 'tenant'" class="help-badge tenant">tenant</span>
              <span v-if="article.view_count" class="help-item-views" :title="article.view_count + ' views'">
                <i class="fa fa-eye"></i> {{ article.view_count }}
              </span>
            </div>
          </button>
        </template>
      </div>
      <div class="help-divider" @mousedown="onDividerMouseDown('articlesSidebarWidth', $event)"></div>
      <div class="help-content-pane">
        <button class="help-mobile-back" @click="mobileBack">
          <i class="fa fa-chevron-left"></i> Back to articles
        </button>
        <div v-if="loadingArticle" class="help-empty-state">Loading article...</div>
        <div v-else-if="!selectedArticle" class="help-empty-state">
          <div class="help-empty-welcome">
            <p class="help-empty-welcome-text">Browse articles or search for a topic to get started.</p>
            <div class="help-empty-actions">
              <button type="button" class="help-action-pill" @click="focusSearch">
                <i class="fa fa-search" style="margin-right: 6px;"></i> Search
              </button>
              <button v-for="topic in suggestedTopics" :key="topic" type="button" class="help-action-pill" @click="searchTopic(topic)">
                {{ topic }}
              </button>
            </div>
          </div>
        </div>
        <template v-else>
          <div class="help-article-header" :class="{ 'help-article-header--collapsed': isHeaderCollapsed }">
            <h2 class="help-article-title">{{ selectedArticle.title }}</h2>
            <div class="help-article-meta">
              <span v-if="selectedArticle.article_type" class="help-badge type">{{ selectedArticle.article_type }}</span>
              <span v-if="selectedArticle.tenant_scope === 'tenant'" class="help-badge tenant">tenant</span>
              <span v-if="selectedArticle.updated_at" class="help-article-date">Updated {{ formatUploadDate(selectedArticle.updated_at) }}</span>
              <span v-if="selectedArticle.view_count" class="help-article-views" :title="selectedArticle.view_count + ' ' + (selectedArticle.view_count === 1 ? 'view' : 'views')">
                <i class="fa fa-eye"></i> {{ selectedArticle.view_count }} {{ selectedArticle.view_count === 1 ? 'view' : 'views' }}
              </span>
            </div>
            <p v-if="selectedArticle.summary" class="help-article-summary">{{ selectedArticle.summary }}</p>
          </div>
          <div class="help-article-body" @scroll="onArticleScroll">
            <div class="help-content" v-html="renderContent(articleContent)" @click="handleRenderedLinkClick"></div>
            <div class="help-feedback">
              <span class="mr-2">Was this helpful?</span>
              <b-button
                size="sm"
                :variant="feedbackValue === true ? 'success' : 'outline-success'"
                @click="submitFeedback(true)"
                :disabled="feedbackSubmitting"
              >
                Yes
              </b-button>
              <b-button
                size="sm"
                :variant="feedbackValue === false ? 'danger' : 'outline-danger'"
                class="ml-2"
                @click="submitFeedback(false)"
                :disabled="feedbackSubmitting"
              >
                No
              </b-button>
            </div>
            <div v-if="feedbackValue !== null && !feedbackCommentSubmitted" class="help-feedback-comment">
              <label class="help-feedback-comment-label">Anything to add? (optional)</label>
              <b-form-textarea
                ref="feedbackCommentInput"
                v-model="feedbackComment"
                placeholder="e.g. What could we improve?"
                rows="2"
                max-rows="4"
                size="sm"
                class="help-feedback-comment-input"
              ></b-form-textarea>
              <b-button size="sm" variant="secondary" @click="submitComment" :disabled="feedbackSubmitting">
                Submit comment
              </b-button>
            </div>
            <div v-else-if="feedbackCommentSubmitted" class="help-feedback-comment-done">
              Thanks for your feedback.
            </div>

            <div v-if="isAdmin && feedbackSummary && feedbackSummary.total > 0" class="help-feedback-admin">
              <div class="help-feedback-summary">
                <span class="fb-stat yes">{{ feedbackSummary.yes_count }} Yes</span>
                <span class="fb-stat no">{{ feedbackSummary.no_count }} No</span>
                <span class="fb-stat pct">({{ feedbackSummary.helpful_pct }}% helpful)</span>
                <a v-if="isAdmin" href="#" class="fb-toggle" @click.prevent="feedbackDetailExpanded = !feedbackDetailExpanded">
                  {{ feedbackDetailExpanded ? 'Hide details' : 'Show details' }}
                </a>
              </div>
              <div v-if="feedbackDetailExpanded" class="help-feedback-detail">
                <table class="fb-detail-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Vote</th>
                      <th>Comment</th>
                      <th>Date</th>
                      <th>Page</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(entry, idx) in feedbackSummary.entries" :key="idx">
                      <td>{{ entry.user_id }}</td>
                      <td>
                        <span :class="entry.helpful ? 'fb-vote-yes' : 'fb-vote-no'">{{ entry.helpful ? 'Yes' : 'No' }}</span>
                      </td>
                      <td class="fb-comment-cell">{{ entry.comment || '—' }}</td>
                      <td>{{ formatFeedbackDate(entry.created_at) }}</td>
                      <td>{{ entry.route_name || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- COMPANY DOCUMENTS TAB -->
    <div v-if="activeTab === 'company'" class="help-body" :class="{ 'mobile-show-detail': mobileShowDetail }">
      <div class="help-sidebar" :style="{ width: companySidebarWidth + 'px' }">
        <div v-if="loadingCompanyDocs" class="help-empty-state">
          Loading documents...
        </div>
        <div v-else-if="companyDocs.length === 0" class="help-empty-state">
          <div v-if="isMobileView && canManageDocs" class="help-empty-welcome">
            <p class="help-empty-welcome-text">Upload company documents for your team to browse and download, like handbooks and policies. Fleet Assistant also uses these when answering questions.</p>
            <div class="help-empty-actions">
              <button type="button" class="help-action-pill" @click="guideThenManage">
                <i class="fa fa-plus-circle" style="margin-right: 6px;"></i> Add documents...
              </button>
            </div>
          </div>
          <template v-else>No company documents available.</template>
        </div>
        <template v-else>
          <button
            v-for="doc in companyDocs"
            :key="doc.doc_id"
            type="button"
            class="help-list-item"
            :class="{ active: selectedCompanyDocId === doc.doc_id }"
            @click="selectCompanyDoc(doc)"
          >
            <div class="help-item-title">{{ doc.display_name }}</div>
            <div v-if="doc.purpose" class="help-item-summary">{{ doc.purpose }}</div>
            <div class="help-item-meta">
              <span v-if="doc.doc_type" class="help-badge type">{{ doc.doc_type }}</span>
              <span v-if="doc.audience" class="help-badge tenant">{{ doc.audience }}</span>
            </div>
          </button>
        </template>
      </div>
      <div class="help-divider" @mousedown="onDividerMouseDown('companySidebarWidth', $event)"></div>
      <div class="help-content-pane">
        <button class="help-mobile-back" @click="mobileBack">
          <i class="fa fa-chevron-left"></i> Back to documents
        </button>
        <div v-if="loadingCompanyDoc" class="help-empty-state">Loading document...</div>
        <div v-else-if="!selectedCompanyDoc" class="help-empty-state">
          <div v-if="canManageDocs && companyDocs.length === 0" class="help-empty-welcome">
            <p class="help-empty-welcome-text">Upload company documents for your team to browse and download, like handbooks and policies. Fleet Assistant also uses these when answering questions.</p>
            <div class="help-empty-actions">
              <button type="button" class="help-action-pill" @click="guideThenManage">
                <i class="fa fa-plus-circle" style="margin-right: 6px;"></i> Add documents...
              </button>
            </div>
          </div>
          <template v-else>Select a document from the list.</template>
        </div>
        <template v-else>
          <div class="help-article-body">
            <div class="company-doc-header">
              <div class="company-doc-title-row">
                <h2 class="company-doc-title">{{ selectedCompanyDoc.display_name }}</h2>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="downloadCompanyDoc"
                  :disabled="downloadingDoc"
                  title="Download original file"
                  class="company-doc-download-btn"
                >
                  <i class="fa fa-download"></i> Download
                </b-button>
              </div>
              <div class="company-doc-meta">
                <span v-if="selectedCompanyDoc.doc_type" class="help-badge type">{{ selectedCompanyDoc.doc_type }}</span>
                <span v-if="selectedCompanyDoc.audience" class="help-badge tenant">{{ selectedCompanyDoc.audience }}</span>
                <span v-if="selectedCompanyDoc.uploaded_at" class="company-doc-date">
                  Uploaded {{ formatUploadDate(selectedCompanyDoc.uploaded_at) }}
                </span>
              </div>
              <div v-if="selectedCompanyDoc.purpose" class="company-doc-purpose">{{ selectedCompanyDoc.purpose }}</div>
            </div>
            <pre class="company-doc-text">{{ companyDocContent }}</pre>
          </div>
        </template>
      </div>
    </div>

    <!-- SUPPORT TICKETS TAB -->
    <div v-if="activeTab === 'support'" class="help-support-body">
      <support-ticket-details
        v-if="supportTicketId"
        :key="'ticket-' + supportTicketId"
        @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)"
      />
      <support-ticket-list v-else @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
    </div>

    <!-- FEEDBACK REVIEW TAB (admin only) -->
    <div v-if="activeTab === 'feedback'" class="help-feedback-review-body">
      <!-- sub-tab buttons -->
      <div class="fb-review-subtabs">
        <button class="help-tab" :class="{ active: feedbackSubTab === 'fa_response' }" @click="switchFeedbackSubTab('fa_response')">FA Responses</button>
        <button class="help-tab" :class="{ active: feedbackSubTab === 'help_article' }" @click="switchFeedbackSubTab('help_article')">Help Articles</button>
        <button class="help-tab" :class="{ active: feedbackSubTab === 'topic_suggestion' }" @click="switchFeedbackSubTab('topic_suggestion')">Topic Suggestions</button>
      </div>

      <!-- filter row -->
      <div class="fb-review-filters">
        <div class="fb-filter-group">
          <label>From</label>
          <date-picker v-model="fbFilterFromDate" :config="fbDatePickerConfig" placeholder="Start date" style="width: 130px; display: inline-block; font-size: 13px;" />
        </div>
        <div class="fb-filter-group">
          <label>To</label>
          <date-picker v-model="fbFilterToDate" :config="fbDatePickerConfig" placeholder="End date" style="width: 130px; display: inline-block; font-size: 13px;" />
        </div>
        <div class="fb-filter-group">
          <label>Rating</label>
          <b-form-select v-model="fbFilterRating" size="sm" style="width: 120px;">
            <option value="">All</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="concern">Concern</option>
          </b-form-select>
        </div>
        <div class="fb-filter-group">
          <label>Status</label>
          <b-form-select v-model="fbFilterStatus" size="sm" style="width: 140px;">
            <option value="open">Open (new + reviewed)</option>
            <option value="">All</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="actioned">Actioned</option>
          </b-form-select>
        </div>
        <div class="fb-filter-group">
          <label>&nbsp;</label>
          <b-form-checkbox v-model="fbShowDeleted" size="sm" style="font-size: 13px; white-space: nowrap;">Show deleted</b-form-checkbox>
        </div>
        <div v-if="isAdmin" class="fb-filter-group">
          <label>Account</label>
          <b-form-select v-model="fbFilterTenant" size="sm" style="width: 180px;" :options="feedbackTenantOptions"></b-form-select>
        </div>
        <b-button size="sm" variant="primary" @click="loadFeedbackReview" :disabled="feedbackReviewLoading" style="align-self: flex-end;">
          <span v-if="feedbackReviewLoading"><b-spinner small></b-spinner></span>
          <span v-else>Search</span>
        </b-button>
      </div>

      <!-- stats summary -->
      <div v-if="feedbackReviewStats" class="fb-review-stats">
        <span class="fb-review-stat"><strong>{{ feedbackReviewStats.total || 0 }}</strong> total</span>
        <span class="fb-review-stat positive"><i class="fa fa-thumbs-up"></i> {{ (feedbackReviewStats.by_rating && feedbackReviewStats.by_rating.positive) || 0 }}</span>
        <span class="fb-review-stat negative"><i class="fa fa-thumbs-down"></i> {{ (feedbackReviewStats.by_rating && feedbackReviewStats.by_rating.negative) || 0 }}</span>
        <span class="fb-review-stat concern"><i class="fa fa-flag"></i> {{ (feedbackReviewStats.by_rating && feedbackReviewStats.by_rating.concern) || 0 }}</span>
      </div>

      <!-- results table -->
      <div v-if="feedbackReviewLoading" class="help-empty-state" style="min-height: 120px;">Loading feedback...</div>
      <div v-else-if="feedbackReviewItems.length === 0" class="help-empty-state" style="min-height: 120px;">
        No feedback found. Adjust filters and search.
      </div>
      <div v-else class="fb-review-table-wrap">
        <b-table
          :items="feedbackReviewItems"
          :fields="feedbackReviewFields"
          small
          striped
          hover
          responsive
          :per-page="fbPerPage"
          :current-page="fbCurrentPage"
          :tbody-tr-class="feedbackRowClass"
          class="fb-review-table"
        >
          <template slot="created_at" slot-scope="row">
            <span class="stl-dt" :title="formatFeedbackDate(row.item.created_at)">{{ formatFeedbackDateCompact(row.item.created_at) }}</span>
          </template>
          <template slot="tenant_id" slot-scope="row">
            {{ getTenantLabel(row.item.tenant_id) }}
          </template>
          <template slot="rating" slot-scope="row">
            <!-- tag-styled rating (mirrors the priority tags used on the
                 support ticket list — same pill shape, same color family).
                 shared `fb-tag` base class so future status / type / handled-by
                 tags can reuse it without redeclaring the box geometry. -->
            <span v-if="getRatingTag(row.item.rating)"
                  class="fb-tag"
                  :class="getRatingTag(row.item.rating).variant"
                  :title="getRatingTag(row.item.rating).label"
                  :aria-label="getRatingTag(row.item.rating).label">
              <i class="fa" :class="getRatingTag(row.item.rating).icon" aria-hidden="true"></i>
              <span class="fb-tag-label">{{ getRatingTag(row.item.rating).label }}</span>
            </span>
            <span v-else class="fb-tag fb-tag-muted">{{ row.item.rating || '—' }}</span>
          </template>
          <template slot="context_preview" slot-scope="row">
            <span class="fb-context-preview" :title="row.item.context_preview || ''">{{ truncateText(row.item.context_preview || '', 60) }}</span>
          </template>
          <template slot="comment" slot-scope="row">
            <span class="fb-comment-preview" :title="row.item.comment || ''">{{ truncateText(row.item.comment || '', 80) }}</span>
          </template>
          <template slot="status" slot-scope="row">
            <span class="help-badge" :class="feedbackStatusClass(row.item.status)">{{ row.item.status || 'new' }}</span>
            <span v-if="row.item.deleted_at" class="fb-deleted-tag" title="Soft-deleted — hidden from default view">deleted</span>
          </template>
          <template slot="handled_by" slot-scope="row">
            <span v-if="row.item.reviewed_by" class="fb-handled-pill" :style="{ background: reviewerPillColor(row.item.reviewed_by) }" :title="row.item.reviewed_by + ' · ' + (row.item.status || 'new') + ' · ' + formatFeedbackDate(row.item.reviewed_at)">
              {{ reviewerDisplayName(row.item.reviewed_by) }}
            </span>
            <span v-else class="fb-handled-empty">—</span>
          </template>
          <template slot="actions" slot-scope="row">
            <div class="fb-action-btns">
              <b-button size="sm" variant="outline-primary" @click="openFeedbackDetail(row.item)" title="View details">
                View
              </b-button>
              <b-button v-if="(row.item.status || 'new') !== 'reviewed'" size="sm" variant="outline-success" @click="updateFeedbackStatus(row.item, 'reviewed')" :disabled="row.item._updating" title="Mark reviewed">
                Reviewed
              </b-button>
              <b-button v-if="(row.item.status || 'new') !== 'actioned'" size="sm" variant="outline-warning" @click="updateFeedbackStatus(row.item, 'actioned')" :disabled="row.item._updating" title="Mark actioned">
                Actioned
              </b-button>
              <b-button v-if="!row.item.deleted_at" size="sm" variant="outline-danger" class="fb-trash-btn" @click="confirmDeleteFeedback(row.item)" :disabled="row.item._updating" title="Delete this feedback">
                <i class="fa fa-trash"></i>
              </b-button>
              <b-button v-if="row.item.deleted_at" size="sm" variant="outline-secondary" class="fb-restore-btn" @click="restoreFeedback(row.item)" :disabled="row.item._updating" title="Restore this feedback">
                <i class="fa fa-undo"></i>
              </b-button>
            </div>
          </template>
        </b-table>
        <div v-if="feedbackReviewItems.length > fbPerPage" class="fb-review-pagination">
          <b-pagination v-model="fbCurrentPage" :total-rows="feedbackReviewItems.length" :per-page="fbPerPage" size="sm" align="center"></b-pagination>
        </div>
      </div>
    </div>

    <!-- Assistant Insights tab body (admin-only) — FI-1600 -->
    <div v-if="activeTab === 'insights' && isAdmin" class="help-insights-body">
      <!-- breadcrumb + back navigation for drill-down views -->
      <div class="fa-breadcrumb">
        <a href="#" @click.prevent="faNavigate({ view: 'fleet' })" :class="{ 'fa-bc-current': faView === 'fleet' }">Fleet</a>
        <template v-if="faSelectedTenant">
          <span class="fa-bc-sep">›</span>
          <a href="#" @click.prevent="faNavigate({ view: 'tenant', tenant: faSelectedTenant })" :class="{ 'fa-bc-current': faView === 'tenant' }">{{ faTenantLabel(faSelectedTenant) }}</a>
        </template>
        <template v-if="faSelectedUser">
          <span class="fa-bc-sep">›</span>
          <a href="#" @click.prevent="faNavigate({ view: 'user', tenant: faSelectedTenant, user: faSelectedUser })" :class="{ 'fa-bc-current': faView === 'user' }">{{ faSelectedUser }}</a>
        </template>
        <template v-if="faSelectedSession && faView === 'session'">
          <span class="fa-bc-sep">›</span>
          <span class="fa-bc-current">Conversation</span>
        </template>

        <!-- month picker — drives all stats / tables / sessions to the
             selected period. hidden on the conversation view since a
             session is already time-pinned. -->
        <div v-if="faView !== 'session'" class="fa-period-picker">
          <label for="fa-period-select" class="fa-period-label">Month</label>
          <b-form-select
            id="fa-period-select"
            size="sm"
            :value="faSelectedPeriod"
            :options="faPeriodOptions"
            @change="faSetPeriod"
            class="fa-period-select"
          />
        </div>
      </div>

      <FaFleetView
        v-if="faView === 'fleet'"
        :rows="faFleetRows"
        :loading="faFleetLoading"
        :loaded-at="faFleetLoadedAt"
        :loaded-rows="faFleetLoadedCount"
        :total-tenants="faFleetTotalTenants"
        @refresh="loadFaFleet(true)"
        @open-tenant="(tid) => faNavigate({ view: 'tenant', tenant: tid })"/>

      <FaTenantView
        v-else-if="faView === 'tenant'"
        :tenant-data="faTenantData"
        :loading="faTenantLoading"
        @open-user="({ tenant_id, user_id }) => faNavigate({ view: 'user', tenant: tenant_id, user: user_id })"
        @open-feedback="(tid) => faOpenFeedbackForTenant(tid)"/>

      <FaUserView
        v-else-if="faView === 'user'"
        :user-data="faUserData"
        :loading="faUserLoading"
        @open-session="(opts) => faNavigate({ view: 'session', tenant: opts.tenant_id, user: opts.user_id, session: opts.session_id, archived: opts.archived })"/>

      <FaConversationView
        v-else-if="faView === 'session'"
        :session-data="faSessionData"
        :loading="faSessionLoading"/>
    </div>

    <!-- Announcements tab body (admin-only) — FI-1432.
         list-as-landing / detail-as-subpage flow, same pattern as support
         tickets so it translates to mobile unchanged. drafts show up in
         the list with a Draft badge and are editable; published rows are
         read-only (view + retract). -->
    <div v-if="activeTab === 'announcements' && isAdmin" class="help-nc-body">

      <!-- LIST VIEW: landing page — all announcements with status badge,
           plus "New Announcement" button that opens the detail page in
           create mode. -->
      <div v-if="announcementView === 'list'" class="nc-list">
        <div class="nc-list-header">
          <b-button variant="primary" size="sm" @click="openAnnouncementForNew">
            <i class="fa fa-plus"></i> New Announcement
          </b-button>
          <div class="nc-stats-row nc-list-stats">
            <b-form-radio-group v-model="statsWindowDays" :options="statsWindowOptions" buttons size="sm" @change="loadSystemStats" />
            <span v-if="systemStats" class="nc-stat"><strong>{{ systemStats.total_sent || 0 }}</strong> sent</span>
            <span v-if="systemStats" class="nc-stat"><strong>{{ systemStats.read_pct || 0 }}%</strong> read</span>
            <span v-if="systemStats" class="nc-stat"><strong>{{ systemStats.action_pct || 0 }}%</strong> actioned</span>
            <span v-if="systemStats" class="nc-stat"><strong>{{ systemStats.avg_time_to_read || '—' }}</strong> avg time-to-read</span>
            <span v-if="statsLoading" class="nc-stat"><b-spinner small></b-spinner></span>
          </div>
        </div>

        <!-- filter pills: scope the list down without losing the global
             "everything" entry point. counters surface scope size at a
             glance so an empty service-alerts pill (no outages) doesn't
             require a click to discover. "Active banners" is the most
             common during-incident filter. -->
        <div class="nc-list-filter-row" role="tablist" aria-label="Filter announcements">
          <button
            v-for="f in announcementListFilters"
            :key="f.value"
            type="button"
            class="nc-list-filter"
            :class="{ active: announcementListFilter === f.value, 'nc-list-filter--accent-warning': f.accent === 'warning' }"
            :aria-selected="announcementListFilter === f.value"
            role="tab"
            @click="announcementListFilter = f.value"
          >
            <i v-if="f.icon" class="fa" :class="f.icon" aria-hidden="true"></i>
            {{ f.text }}
            <span class="nc-list-filter-count">{{ announcementListCounts[f.value] || 0 }}</span>
          </button>
        </div>

        <!-- horizontal-scroll wrapper for narrow viewports — same
             .table-responsive shape Bootstrap-Vue's `responsive` prop
             generates around b-tables, so the announcement table reads
             the same on mobile as the support ticket list and FA
             Insights tables (canonical pattern). -->
        <div class="table-responsive">
        <table class="nc-announcement-table">
          <thead>
            <tr>
              <th class="nc-th-status">Status</th>
              <!-- "Date" tracks the announcement's effective time —
                   published_at when it's gone out, created_at while it's
                   still a draft. matches what the notification list shows
                   recipients (fan-out time, not draft-creation time). -->
              <th class="nc-th-sortable" @click="setStatsSort('effective_at')">
                Date <i class="fa" :class="sortIconFor('effective_at')"></i>
              </th>
              <th class="nc-th-sortable" @click="setStatsSort('title')">
                Title <i class="fa" :class="sortIconFor('title')"></i>
              </th>
              <th class="nc-th-sortable" @click="setStatsSort('created_by')">
                Author <i class="fa" :class="sortIconFor('created_by')"></i>
              </th>
              <th>Target</th>
              <th>Roles</th>
              <th class="nc-th-sortable" @click="setStatsSort('recipient_count')">
                Recipients <i class="fa" :class="sortIconFor('recipient_count')"></i>
              </th>
              <th class="nc-th-sortable" @click="setStatsSort('read_count')">
                Read <i class="fa" :class="sortIconFor('read_count')"></i>
              </th>
              <th class="nc-th-sortable" @click="setStatsSort('action_count')">
                Actioned <i class="fa" :class="sortIconFor('action_count')"></i>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!announcementList.length && !announcementListLoading">
              <td colspan="10" class="nc-empty-state">
                <div class="nc-empty-puck" aria-hidden="true">
                  <i class="fa fa-bullhorn"></i>
                </div>
                <div class="nc-empty-title">No announcements yet</div>
                <div class="nc-empty-hint">
                  Use <strong>New Announcement</strong> to publish a product update or post a service alert with a site-wide banner.
                </div>
              </td>
            </tr>
            <!-- skeleton rows during INITIAL load only. once the list
                 has data (e.g. after navigating back from a detail page
                 while loadAnnouncementList re-fetches), keep showing the
                 stale data and let the new payload swap in silently —
                 skeletons stacked on top of cached rows reads as a
                 layout glitch, not progress. -->
            <tr v-for="i in ((announcementListLoading && !announcementList.length) ? 5 : 0)" :key="'skel-' + i" class="nc-skeleton-row" aria-hidden="true">
              <td><span class="nc-skel nc-skel-pill"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 92px"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 60%"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 80px"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 110px"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 90px"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 40px"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 60px"></span></td>
              <td><span class="nc-skel nc-skel-text" style="width: 60px"></span></td>
              <td>
                <span class="nc-skel nc-skel-btn"></span>
                <span class="nc-skel nc-skel-btn"></span>
              </td>
            </tr>
            <tr
              v-for="a in sortedAnnouncementList"
              :key="a.announcement_id"
              class="nc-row-clickable"
              :class="{ 'nc-row-draft': isDraft(a) }"
              @click="openAnnouncementDetail(a)"
            >
              <td>
                <span class="nc-status-badge" :class="isDraft(a) ? 'nc-status-draft' : 'nc-status-published'">
                  <i class="fa" :class="isDraft(a) ? 'fa-pencil' : 'fa-paper-plane-o'" aria-hidden="true"></i>
                  {{ isDraft(a) ? 'Draft' : 'Published' }}
                </span>
              </td>
              <td>
                <div class="nc-date-cell">
                  <span class="nc-date-primary">{{ formatAnnouncementDate(a.published_at || a.created_at) }}</span>
                  <span v-if="isDraft(a)" class="nc-date-hint">draft</span>
                </div>
              </td>
              <td>
                <!-- title cell: small kind puck (service_alert vs plain
                     announcement) + title, with an optional banner-state
                     sub-line for service_alert rows that carry a banner.
                     surfacing kind here lets admins scan for outage rows
                     without an extra column; the banner sub-line lives
                     here too rather than in the Status cell so the row's
                     "what is this?" identity stays grouped. -->
                <div class="nc-title-stack">
                  <div class="nc-title-row">
                    <span
                      class="nc-title-puck"
                      :class="rowPuckClass(a)"
                      :title="rowPuckTitle(a)"
                      aria-hidden="true"
                    >
                      <span v-if="_isSvgIconWrap(iconForRow(a))" class="ni-svg" v-html="_svgIconHtmlWrap(iconForRow(a))" aria-hidden="true"></span>
                      <i v-else class="fa" :class="iconForRow(a)"></i>
                    </span>
                    <span class="nc-title-cell" :class="{ 'nc-title-untitled': !a.title }">
                      {{ a.title || (isDraft(a) ? '(untitled draft)' : '—') }}
                    </span>
                  </div>
                  <div v-if="a.banner === true" class="nc-title-sub-wrap">
                    <span
                      class="nc-title-sub"
                      :class="bannerStatusClass(a)"
                      :title="bannerStatusTitle(a)"
                    >
                      <i class="fa fa-bullhorn" aria-hidden="true"></i>
                      {{ bannerStatusLabel(a) }}
                    </span>
                  </div>
                </div>
              </td>
              <td :title="a.created_by || ''">
                <!-- prefer the enriched display name (FI-1432 v3 baked
                     created_by_name onto each row at create time + lazy-
                     enrich on read for legacy rows) so admins see
                     "Chaz Watson" rather than a username slug. -->
                <span class="nc-author-name">{{ a.created_by_name || authorLabel(a.created_by) }}</span>
              </td>
              <td>
                <span class="nc-target-cell">
                  <i class="fa" :class="a.target === 'tenant' ? 'fa-building-o' : 'fa-globe'" aria-hidden="true"></i>
                  {{ a.target === 'tenant' ? tenantName(a.tenant_id) : 'All customers' }}
                </span>
              </td>
              <td :title="(a.roles && a.roles.length) ? a.roles.map(roleLabel).join(', ') : 'Everyone in the target audience'">
                <span v-if="a.roles && a.roles.length" class="nc-role-pill">{{ rolesSummary(a.roles) }}</span>
                <span v-else class="nc-role-pill nc-role-pill-everyone">Everyone</span>
              </td>
              <td>{{ isDraft(a) ? '—' : (a.recipient_count || 0) }}</td>
              <td>{{ isDraft(a) ? '—' : formatCount(a.read_count, a.recipient_count) }}</td>
              <td>{{ isDraft(a) ? '—' : formatCount(a.action_count, a.recipient_count) }}</td>
              <!-- per-row actions as buttons. draft rows get Edit + Delete
                   (delete hard-removes the canonical draft — nothing was
                   ever fanned out). published rows get View + Retract
                   (retract moves the row back to draft, dropping per-user
                   rows so unread readers stop seeing it). -->
              <td class="nc-actions-cell" @click.stop>
                <!-- per-row actions stay focused on Edit/View + Delete/
                     Retract. "Expire banner now" lives on the read-only
                     detail page (in the dedicated Banner state panel)
                     so admins land on the announcement first, see who's
                     affected, and clear the banner with full context. -->
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="nc-action-btn"
                  @click="openAnnouncementDetail(a)"
                >{{ isDraft(a) ? 'Edit' : 'View' }}</b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  class="nc-action-btn"
                  :title="isDraft(a) ? 'Delete this draft' : 'Retract this announcement'"
                  @click="promptDeleteAnnouncement(a)"
                >{{ isDraft(a) ? 'Delete' : 'Retract' }}</b-button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <!-- DETAIL VIEW: compose/edit (drafts + new) or read-only (published).
           compose modes carry their own header (Draft/New pill + a "what
           page am I on?" heading + last-saved tag). read mode skips this
           header entirely — the back button moves into the read-only
           container's left page margin (matches announcement_detail.vue,
           the user-facing route, so the admin's read view aligns with
           what customers see). -->
      <div
        v-if="announcementView === 'detail'"
        class="nc-detail"
        :class="{ 'nc-detail--read': announcementDetailMode === 'read' }"
      >
        <div v-if="announcementDetailMode !== 'read'" class="nc-detail-header">
          <button class="nc-back-btn" @click="backToAnnouncementList" title="Back to announcements">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
          </button>
          <div class="nc-detail-title-row">
            <span class="nc-status-badge nc-status-draft">
              <i class="fa fa-pencil" aria-hidden="true"></i>
              {{ announcementDraftId ? 'Draft' : 'New' }}
            </span>
            <h3 class="nc-detail-heading">
              {{ announcementDraftId ? 'Edit draft' : 'New announcement' }}
            </h3>
            <span v-if="announcementDraftLastSavedLabel" class="nc-draft-saved">
              <i class="fa fa-check" aria-hidden="true"></i>
              {{ announcementDraftLastSavedLabel }}
            </span>
          </div>
        </div>

        <!-- COMPOSE / EDIT FORM (drafts + new). Grouped into three
             sections so the form reads as a logical sequence:
               1. Audience  — who receives this (target + roles)
               2. Settings  — what kind of notification + delivery options
               3. Content   — the actual title and body
             Each section is a soft-bordered card with a small section
             header so admins can quickly find a knob without scanning. -->
        <div v-if="announcementDetailMode !== 'read'" class="nc-compose">
          <!-- AUDIENCE -->
          <section class="nc-compose-section">
            <header class="nc-compose-section-header">
              <span class="nc-compose-section-eyebrow">1 · Audience</span>
              <h4 class="nc-compose-section-title">Who receives this</h4>
              <p class="nc-compose-section-hint">Choose a tenant scope and (optionally) narrow it to specific roles.</p>
            </header>
            <div class="nc-field nc-field-inline">
              <label>To</label>
              <div class="nc-to-row">
                <b-form-radio-group v-model="announcementTarget" :options="announcementTargetOptions" size="sm" />
                <b-form-select
                  v-if="announcementTarget === 'tenant'"
                  v-model="announcementTenantId"
                  :options="announcementTenantOptions"
                  size="sm"
                  class="nc-to-tenant-select"
                />
              </div>
            </div>
            <div class="nc-field">
              <label>
                Roles
                <span class="nc-roles-hint">({{ announcementRoles.length ? announcementRoles.length + ' selected' : 'everyone' }})</span>
              </label>
              <div class="nc-roles-row">
                <button
                  type="button"
                  class="nc-role-shortcut"
                  :class="{ active: isMugglesActive }"
                  :disabled="rolesShortcutRunning"
                  @click="toggleMugglesRoles"
                  title="Shortcut: every role except Sys Admin"
                >
                  <i class="fa fa-magic fa-flip-horizontal" aria-hidden="true"></i>
                  Muggles
                </button>
                <button
                  v-for="r in announcementRoleOptions"
                  :key="r.value"
                  type="button"
                  class="nc-role-chip"
                  :class="{ active: announcementRoles.indexOf(r.value) !== -1, 'nc-role-chip-pulsing': pulsingRoles.indexOf(r.value) !== -1 }"
                  @click="toggleAnnouncementRole(r.value)"
                >
                  <i class="fa" :class="r.icon" aria-hidden="true"></i>
                  {{ r.text }}
                </button>
                <button
                  v-if="announcementRoles.length > 0 && !rolesShortcutRunning"
                  type="button"
                  class="nc-role-clear"
                  @click="announcementRoles = []"
                  title="Clear role filter so the announcement goes to everyone in the target"
                >
                  Clear
                </button>
              </div>
            </div>
          </section>

          <!-- SETTINGS -->
          <section class="nc-compose-section">
            <header class="nc-compose-section-header">
              <span class="nc-compose-section-eyebrow">2 · Settings</span>
              <h4 class="nc-compose-section-title">Type &amp; delivery</h4>
              <p class="nc-compose-section-hint">Pick a kind, then optionally enable a banner and email delivery.</p>
            </header>
            <div class="nc-field nc-field-inline">
              <label>Type</label>
              <div class="nc-to-row">
                <!-- kind switch: standard announcement vs. service alert
                     (outage/degradation). service alerts fan out as a
                     distinct notification type with amber visuals and
                     route through the service-alerts email pref. -->
                <b-form-radio-group v-model="announcementKind" :options="announcementKindOptions" size="sm" @change="_invalidateResetSnapshot" />
                <span v-if="announcementKind === 'service_alert'" class="nc-kind-hint">
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                  For outages and service-impacting issues. Delivered to users with Service Alerts email on (default on).
                </span>
                <span v-else-if="announcementKind === 'service_resolved'" class="nc-kind-hint nc-kind-hint--resolved">
                  <i class="fa fa-check-circle" aria-hidden="true"></i>
                  For closing out an active service alert. Same recipients as service alerts; renders green so it reads as "all clear" at a glance.
                </span>
              </div>
            </div>
            <!-- per-row icon override. defaults to the kind's standard
                 glyph (bullhorn / warning triangle); admins can pick a
                 different symbol for variety (rocket for a launch, wrench
                 for maintenance, gift for a holiday). the puck COLOR
                 still tracks `kind` so the urgency read isn't lost. -->
            <div class="nc-field nc-field-inline">
              <label>Icon</label>
              <div class="nc-icon-stack">
                <div class="nc-icon-current-row">
                  <!-- preview chip — same pastel inverted-outline puck
                       family used everywhere else, sized to match the
                       composer's row baseline. clicking it expands the
                       full grid. -->
                  <button
                    type="button"
                    class="nc-icon-current"
                    :class="composerIconChipClass"
                    :title="showIconPicker ? 'Hide icon picker' : 'Pick a different icon'"
                    @click="showIconPicker = !showIconPicker"
                  >
                    <span v-if="_isSvgIconWrap(composerIcon)" class="ni-svg" v-html="_svgIconHtmlWrap(composerIcon)" aria-hidden="true"></span>
                    <i v-else class="fa" :class="composerIcon" aria-hidden="true"></i>
                  </button>
                  <span class="nc-icon-current-meta">
                    <span class="nc-icon-current-name">{{ composerIconLabel }}</span>
                    <button
                      type="button"
                      class="nc-icon-toggle-link"
                      @click="showIconPicker = !showIconPicker"
                    >{{ showIconPicker ? 'Done' : (isComposerIconDefault ? 'Pick a different icon' : 'Change') }}</button>
                    <button
                      v-if="!isComposerIconDefault"
                      type="button"
                      class="nc-icon-reset-link"
                      @click="_resetComposerIcon"
                      title="Reset to the default for this type"
                    >Reset to default</button>
                    <!-- design tool: browse the full FA library + preview
                         under each category palette. used to evaluate
                         candidates for the curated set; only icons on the
                         allowlist can actually be picked for the row. -->
                    <button
                      type="button"
                      class="nc-icon-explore-link"
                      @click="showIconExplorer = true"
                      title="Browse every standard icon and preview against each category palette"
                    >
                      <i class="fa fa-search" aria-hidden="true"></i>
                      Browse all icons
                    </button>
                  </span>
                </div>
                <div v-if="showIconPicker" class="nc-icon-picker">
                  <div
                    v-for="g in announcementIconGroups"
                    :key="g.label"
                    class="nc-icon-group"
                  >
                    <div class="nc-icon-group-header">
                      <span class="nc-icon-group-label">{{ g.label }}</span>
                      <span class="nc-icon-group-hint">{{ g.hint }}</span>
                    </div>
                    <div class="nc-icon-grid">
                      <button
                        v-for="opt in g.icons"
                        :key="opt.value"
                        type="button"
                        class="nc-icon-tile"
                        :class="[
                          { 'nc-icon-tile--active': announcementIcon === opt.value || (!announcementIcon && opt.value === composerIcon) },
                          g.family,
                        ]"
                        :title="opt.desc"
                        @click="_pickIcon(opt.value)"
                      >
                        <span class="nc-icon-tile-puck">
                          <span v-if="_isSvgIconWrap(opt.value)" class="ni-svg" v-html="_svgIconHtmlWrap(opt.value)" aria-hidden="true"></span>
                          <i v-else class="fa" :class="opt.value" aria-hidden="true"></i>
                        </span>
                        <span class="nc-icon-tile-label">{{ opt.label }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- service-alert-only: show a site-wide banner until the user
                 reads or closes it. gated to service_alert so plain
                 announcements never pollute the banner slot. -->
            <div v-if="announcementKind === 'service_alert'" class="nc-field nc-field-inline">
              <label>Banner</label>
              <div class="nc-to-row">
                <b-form-checkbox v-model="announcementBanner" switch size="sm" @change="_invalidateResetSnapshot">
                  Show a site-wide banner until dismissed
                </b-form-checkbox>
                <span class="nc-banner-hint">
                  Appears at the top of every page for recipients. Closing or reading the banner marks the alert as read and hides it.
                </span>
              </div>
            </div>
            <!-- banner auto-expiry: only meaningful when banner is on.
                 quick-set chips compute "now + N hours" so admins can
                 schedule a typical outage window without picking a date.
                 the datetime-local input is the escape hatch for an
                 exact time. selected chip syncs to the input + vice
                 versa via _bannerExpiryPreset / _selectBannerPreset. -->
            <div v-if="announcementKind === 'service_alert' && announcementBanner" class="nc-field nc-field-inline">
              <label>Banner expires</label>
              <div class="nc-banner-expires-stack">
                <div class="nc-banner-expires-presets">
                  <button
                    v-for="p in bannerExpiryPresetOptions"
                    :key="p.value"
                    type="button"
                    class="nc-banner-expires-preset"
                    :class="{ active: _isBannerExpiryPresetActive(p.value) }"
                    @click="_selectBannerExpiryPreset(p.value)"
                    :title="p.title"
                  >{{ p.text }}</button>
                  <button
                    type="button"
                    class="nc-banner-expires-preset"
                    :class="{ active: _isBannerExpiryPresetActive('custom') }"
                    @click="_selectBannerExpiryPreset('custom')"
                    title="Pick a specific time"
                  >Pick time</button>
                  <button
                    v-if="announcementBannerExpiresAtLocal"
                    type="button"
                    class="nc-banner-expires-preset nc-banner-expires-clear"
                    @click="_selectBannerExpiryPreset('none')"
                    title="No scheduled expiry — admins can clear the banner manually later"
                  >Clear</button>
                </div>
                <div v-if="announcementBannerExpiresAtLocal || _bannerExpiryPresetMode === 'custom'" class="nc-banner-expires-row">
                  <b-form-input
                    type="datetime-local"
                    v-model="announcementBannerExpiresAtLocal"
                    size="sm"
                    class="nc-banner-expires-input"
                    @change="_onBannerExpiryInputChange"
                  />
                  <span v-if="announcementBannerExpiresAtLocal" class="nc-banner-expires-relative">
                    {{ _bannerExpiresRelativeLabel(announcementBannerExpiresAtLocal) }}
                  </span>
                </div>
                <span class="nc-banner-hint">
                  Optional. Leave unset for no auto-expiry — admins can clear the banner manually from the detail page. When set, the banner clears for everyone at that time; the in-app notification stays in the dropdown.
                </span>
              </div>
            </div>
            <!-- opt-in email fan-out. -->
            <div class="nc-field nc-field-inline">
              <label>Email</label>
              <div class="nc-to-row">
                <b-form-checkbox v-model="announcementEmail" switch size="sm" @change="_invalidateResetSnapshot">
                  Also send as email
                </b-form-checkbox>
                <span class="nc-banner-hint">
                  Off by default — only an in-app notification is created. When on, recipients whose email preference for this channel is enabled also receive a copy by email.
                </span>
              </div>
            </div>
          </section>

          <!-- CONTENT -->
          <section class="nc-compose-section">
            <header class="nc-compose-section-header">
              <span class="nc-compose-section-eyebrow">3 · Content</span>
              <h4 class="nc-compose-section-title">Title &amp; body</h4>
              <p class="nc-compose-section-hint">The title shows in the dropdown card and on the detail page; the body is the long-form copy.</p>
            </header>
          <div class="nc-field">
            <label>Title</label>
            <b-form-input v-model="announcementTitle" placeholder="Announcement title" size="sm" />
          </div>
          <div class="nc-field">
            <div class="nc-body-label-row">
              <label>Body</label>
              <!-- format switch: rich-text (toast-ui markdown/WYSIWYG) vs
                   raw HTML (plain textarea + iframe preview). lives inline
                   with the Body label so it reads as a property of this
                   field rather than a top-level form control. -->
              <b-form-radio-group
                v-model="announcementFormat"
                :options="announcementFormatOptions"
                size="sm"
                button-variant="outline-secondary"
                buttons
                class="nc-format-toggle"
                @change="_invalidateResetSnapshot"
              />
            </div>
            <div v-show="announcementFormat !== 'html'" class="nc-editor-wrap">
              <ToastEditor
                ref="announcementEditor"
                :options="toastEditorOptions"
                initial-edit-type="wysiwyg"
                height="360px"
                @change="_invalidateResetSnapshot"
              />
            </div>
            <div v-if="announcementFormat === 'html'" class="nc-html-wrap">
              <textarea
                v-model="announcementHtml"
                class="nc-html-source-input"
                spellcheck="false"
                placeholder="<!doctype html>\n<!-- paste or author slick marketing HTML here -->\n<section style=&quot;padding: 40px;&quot;>\n  <h1>Your headline</h1>\n  <p>Your body copy.</p>\n</section>"
                @input="_invalidateResetSnapshot"
              ></textarea>
              <div class="nc-html-hint">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                Recipients see this HTML rendered in an isolated frame — your &lt;style&gt; tags and inline CSS won't leak into the app.
              </div>
            </div>
          </div>
          </section><!-- /CONTENT -->
          <div class="nc-actions">
            <b-button
              class="nc-publish-btn"
              variant="primary"
              size="sm"
              :disabled="announcementPublishing || !announcementTitle.trim()"
              @click="publishAnnouncement"
            >
              <span v-if="announcementPublishing"><b-spinner small></b-spinner> Publishing...</span>
              <span v-else>Publish</span>
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="announcementDraftSaving || announcementPublishing"
              @click="saveAnnouncementDraft"
              title="Save as a draft you can come back to"
            >
              <span v-if="announcementDraftSaving"><b-spinner small></b-spinner> Saving...</span>
              <span v-else><i class="fa fa-save"></i> Save draft</span>
            </b-button>
            <div class="nc-actions-secondary">
              <b-button variant="outline-success" size="sm" @click="openAnnouncementPreview" title="Preview how recipients will see this announcement">
                <i class="fa fa-eye"></i> Preview
              </b-button>
              <b-button variant="outline-success" size="sm" @click="openMarkdownPreview" :title="announcementFormat === 'html' ? 'Show the HTML source that will be saved' : 'Show the markdown source that will be saved'">
                <i class="fa fa-code"></i>
                {{ announcementFormat === 'html' ? 'View HTML' : 'View Markdown' }}
              </b-button>
              <b-button
                v-if="announcementResetSnapshot"
                class="nc-restore-btn"
                variant="outline-primary"
                size="sm"
                @click="restoreAnnouncementForm"
                title="Put back what you just cleared"
              >
                <i class="fa fa-undo"></i> Restore
              </b-button>
              <b-button
                v-else
                variant="outline-danger"
                size="sm"
                @click="resetAnnouncementForm"
                title="Clear the form"
              >
                Reset
              </b-button>
            </div>
          </div>
          <b-alert v-if="announcementFlash" :variant="announcementFlashVariant" show dismissible @dismissed="announcementFlash=null" class="mt-3">{{ announcementFlash }}</b-alert>
        </div>

        <!-- READ-ONLY VIEW for published announcements: shows the
             delivered content + per-announcement stats + retract action.
             Published rows are immutable — to change them, retract and
             recreate. -->
        <div v-else class="nc-readonly">
          <!-- circular back button absolute-positioned into the left page
               margin so the puck/title align flush with the content
               column. mirrors announcement_detail.vue (the user-facing
               route) — a responsive @media collapses it inline below
               1060px when there isn't enough margin to float into. -->
          <button
            class="nc-readonly-back-btn"
            @click="backToAnnouncementList"
            title="Back to announcements"
            aria-label="Back to announcements"
          >
            <i class="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <div v-if="announcementDetailLoading" class="text-center py-3">
            <b-spinner small></b-spinner> Loading...
          </div>
          <div v-else-if="announcementDetail" class="nc-readonly-stack">
            <!-- HERO: kind-specific elevated puck + title + meta line.
                 mirrors announcement_detail.vue (the user-facing route)
                 so the admin's read view feels like the same page they
                 publish into. service_alert gets the amber warning puck;
                 announcement gets the purple bullhorn. -->
            <div class="nc-readonly-hero">
              <div
                class="nc-readonly-puck"
                :class="readonlyPuckClass"
                :title="readonlyPuckTitle"
                aria-hidden="true"
              >
                <span v-if="_isSvgIconWrap(readonlyPuckGlyph)" class="ni-svg" v-html="_svgIconHtmlWrap(readonlyPuckGlyph)" aria-hidden="true"></span>
                <i v-else class="fa" :class="readonlyPuckGlyph"></i>
              </div>
              <div class="nc-readonly-hero-stack">
                <h2 class="nc-readonly-title">{{ announcementDetail.title || '(untitled)' }}</h2>
                <div class="nc-readonly-meta">
                  <span class="nc-readonly-meta-item">
                    <i class="fa fa-clock-o" aria-hidden="true"></i>
                    {{ formatAnnouncementDateLong(announcementDetail.published_at || announcementDetail.created_at) }}
                  </span>
                  <span class="nc-readonly-meta-sep">·</span>
                  <span class="nc-readonly-meta-item" :title="announcementDetail.created_by || ''">
                    <i class="fa fa-user-o" aria-hidden="true"></i>
                    {{ announcementDetail.created_by_name || authorLabel(announcementDetail.created_by) }}
                  </span>
                </div>
                <!-- pill row: target + roles + kind + email-flag if applicable.
                     same visual family (.nc-preview-badge) as the compose
                     preview so both surfaces feel consistent. -->
                <div class="nc-readonly-pills">
                  <span class="nc-preview-badge" :class="announcementDetail.target === 'tenant' ? 'nc-preview-badge-tenant' : 'nc-preview-badge-global'">
                    <i class="fa" :class="announcementDetail.target === 'tenant' ? 'fa-building-o' : 'fa-globe'"></i>
                    {{ announcementDetail.target === 'tenant' ? ('Customer: ' + tenantName(announcementDetail.tenant_id)) : 'All customers' }}
                  </span>
                  <span v-if="announcementDetail.roles && announcementDetail.roles.length" class="nc-preview-badge nc-preview-badge-roles" :title="announcementDetail.roles.map(roleLabel).join(', ')">
                    <i class="fa fa-users"></i>
                    {{ rolesSummary(announcementDetail.roles) }}
                  </span>
                  <span v-else class="nc-preview-badge nc-preview-badge-author">
                    <i class="fa fa-users"></i>
                    All roles
                  </span>
                  <span class="nc-preview-badge" :class="readonlyBadgeClass">
                    <span v-if="_isSvgIconWrap(readonlyPuckGlyph)" class="ni-svg" v-html="_svgIconHtmlWrap(readonlyPuckGlyph)" aria-hidden="true"></span>
                    <i v-else class="fa" :class="readonlyPuckGlyph"></i>
                    {{ readonlyPuckTitle }}
                  </span>
                  <span v-if="announcementDetail.email" class="nc-preview-badge nc-preview-badge-email">
                    <i class="fa fa-envelope-o"></i>
                    Emailed
                  </span>
                </div>
              </div>
            </div>

            <!-- BANNER STATE panel (service_alert only) — sits above the
                 stats card on the read-only detail page because during an
                 outage the banner status is the actionable thing the
                 admin lands here to manage; recipient/read/action counts
                 are secondary context they read after triaging the alert. -->
            <div
              v-if="announcementDetail.kind === 'service_alert'"
              class="nc-readonly-banner-panel"
              :class="bannerPanelClass(announcementDetail)"
            >
              <div class="nc-readonly-banner-icon" aria-hidden="true">
                <i class="fa" :class="bannerPanelIcon(announcementDetail)"></i>
              </div>
              <div class="nc-readonly-banner-stack">
                <span class="nc-readonly-banner-status">{{ bannerPanelStatus(announcementDetail) }}</span>
                <!-- when an expiry is set on an active banner, surface the
                     specific time + relative offset on its own line so it
                     reads as data, not buried in prose. cleared/expired
                     banners get the same treatment via bannerPanelTimeLabel. -->
                <span
                  v-if="bannerPanelTimeLabel(announcementDetail)"
                  class="nc-readonly-banner-time"
                >
                  <i class="fa fa-clock-o" aria-hidden="true"></i>
                  {{ bannerPanelTimeLabel(announcementDetail) }}
                </span>
                <span class="nc-readonly-banner-hint">{{ bannerPanelHint(announcementDetail) }}</span>
              </div>
              <div class="nc-readonly-banner-actions">
                <b-button
                  v-if="bannerIsActive(announcementDetail)"
                  size="sm"
                  variant="warning"
                  class="nc-readonly-banner-btn"
                  @click="promptExpireBanner(announcementDetail)"
                >
                  <i class="fa fa-times-circle" aria-hidden="true"></i>
                  Expire banner now
                </b-button>
              </div>
            </div>

            <!-- STATS card: three headline numbers with progress meters. -->
            <div class="nc-readonly-stats">
              <div class="nc-readonly-stat">
                <span class="nc-readonly-stat-num">{{ formatStatNum(announcementDetail.recipient_count || 0) }}</span>
                <span class="nc-readonly-stat-label">{{ (announcementDetail.recipient_count || 0) === 1 ? 'recipient' : 'recipients' }}</span>
              </div>
              <div class="nc-readonly-stat-divider" aria-hidden="true"></div>
              <div class="nc-readonly-stat nc-readonly-stat-with-bar">
                <div class="nc-readonly-stat-row">
                  <span class="nc-readonly-stat-num">{{ announcementDetail.read_pct || 0 }}<span class="nc-readonly-stat-pct-suffix">%</span></span>
                  <span class="nc-readonly-stat-fraction">{{ announcementDetail.read_count || 0 }} / {{ announcementDetail.recipient_count || 0 }}</span>
                </div>
                <span class="nc-readonly-stat-label">read</span>
                <div class="nc-readonly-stat-bar" aria-hidden="true">
                  <div class="nc-readonly-stat-bar-fill nc-readonly-stat-bar-fill--read" :style="{ width: (announcementDetail.read_pct || 0) + '%' }"></div>
                </div>
              </div>
              <div class="nc-readonly-stat-divider" aria-hidden="true"></div>
              <div class="nc-readonly-stat nc-readonly-stat-with-bar">
                <div class="nc-readonly-stat-row">
                  <span class="nc-readonly-stat-num">{{ announcementDetail.action_pct || 0 }}<span class="nc-readonly-stat-pct-suffix">%</span></span>
                  <span class="nc-readonly-stat-fraction">{{ announcementDetail.action_count || 0 }} / {{ announcementDetail.recipient_count || 0 }}</span>
                </div>
                <span class="nc-readonly-stat-label">actioned</span>
                <div class="nc-readonly-stat-bar" aria-hidden="true">
                  <div class="nc-readonly-stat-bar-fill nc-readonly-stat-bar-fill--actioned" :style="{ width: (announcementDetail.action_pct || 0) + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- BODY: dividing rule, then markdown or html-iframe content.
                 .nc-readonly-body wrapper gives the body its own padding
                 rhythm so it doesn't bump up against the stats card. -->
            <div class="nc-readonly-body-wrap">
              <iframe
                v-if="isAnnouncementDetailHtml"
                class="nc-detail-iframe"
                :srcdoc="announcementDetailSrcdoc"
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                @load="_resizeAnnouncementIframe"
              ></iframe>
              <div v-else class="markdown-content markdown-page nc-detail-body" v-html="renderedAnnouncementBody"></div>
            </div>

            <!-- footer actions: retract sits alone on the right. expire
                 banner is in the dedicated panel above; no need to
                 duplicate here. -->
            <div class="nc-readonly-actions">
              <b-button variant="outline-danger" size="sm" :disabled="announcementDeleting" @click="promptDeleteAnnouncement(announcementDetail)">
                <b-spinner v-if="announcementDeleting" small class="mr-1"></b-spinner>
                <i v-else class="fa fa-undo" aria-hidden="true"></i>
                {{ announcementDeleting ? 'Retracting…' : 'Retract announcement' }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Retract-or-discard confirm modal — FI-1432. Two flows sharing
         one modal, branched on the row's status:
           · draft     → hard delete (discard)
           · published → retract: drop per-user notifications and flip
                         the canonical row back to draft so the admin
                         can edit and republish.
         Title / body / button labels swap to match so the admin knows
         which action they're confirming. -->
    <b-modal
      :visible="!!announcementToDelete"
      :title="announcementToDelete && isDraft(announcementToDelete) ? 'Delete this draft?' : 'Retract this announcement?'"
      size="md"
      ok-variant="danger"
      cancel-title="Keep it"
      :ok-disabled="announcementDeleting"
      :cancel-disabled="announcementDeleting"
      :no-close-on-backdrop="announcementDeleting"
      :no-close-on-esc="announcementDeleting"
      :hide-header-close="announcementDeleting"
      @hidden="announcementToDelete = null"
      @ok.prevent="confirmDeleteAnnouncement"
    >
      <p v-if="announcementToDelete" class="mb-2">
        <strong>{{ announcementToDelete.title || '(untitled draft)' }}</strong>
      </p>
      <template v-if="announcementToDelete && isDraft(announcementToDelete)">
        <p class="text-muted small mb-0">
          This will permanently remove the draft. It was never published, so nobody will be notified.
        </p>
      </template>
      <template v-else>
        <p class="text-muted small mb-1">
          This will move the announcement back to Drafts and drop it from the dropdown for anyone who hasn't opened it yet.
        </p>
        <p class="text-muted small mb-0">
          You can edit and republish it from the Drafts list. Readers who already saw it won't be notified of the retraction.
        </p>
      </template>
      <!-- replace just the OK button content so we keep b-modal's default
           footer layout (cancel left, ok right) but slip in a spinner +
           in-flight label while the request is mid-air. -->
      <template #modal-ok>
        <b-spinner v-if="announcementDeleting" small class="mr-1"></b-spinner>
        <template v-if="announcementToDelete && isDraft(announcementToDelete)">{{ announcementDeleting ? 'Deleting…' : 'Delete' }}</template>
        <template v-else>{{ announcementDeleting ? 'Retracting…' : 'Retract' }}</template>
      </template>
    </b-modal>

    <!-- Expire-banner confirm modal. Only ever fires from a published
         service_alert with banner=true. clears the persistent banner
         for everyone (read or unread) on their next list-poll tick;
         the in-app notification itself stays in the dropdown so users
         can still see "outage details" after the banner is gone.
         Different button colour from Retract — this is reversible-ish
         (admin can clear the expiry to re-show) and far less destructive
         than retract, so it's a "warning" not a "danger". -->
    <b-modal
      :visible="!!bannerToExpire"
      title="Expire this banner now?"
      size="md"
      ok-title="Expire banner"
      ok-variant="warning"
      cancel-title="Keep it showing"
      @hidden="bannerToExpire = null"
      @ok.prevent="confirmExpireBanner"
    >
      <p v-if="bannerToExpire" class="mb-2">
        <strong>{{ bannerToExpire.title || '(untitled)' }}</strong>
      </p>
      <p class="text-muted small mb-1">
        The site-wide banner will clear for everyone within ~2 minutes (the next list-poll tick), regardless of whether they've dismissed it.
      </p>
      <p class="text-muted small mb-0">
        The notification stays in the dropdown so users can still open it. You can re-show the banner later by editing the announcement and clearing its expiry.
      </p>
    </b-modal>

    <!-- Preview modal (how recipients will see it) — FI-1432.
         mirrors the FA chat reading pane / help article pattern:
         .markdown-content typography, muted pastel badges (doc-badge
         family), subtle meta row. -->
    <!-- no-enforce-focus lets keyboard focus leave the modal so admins
         can type into Fleet Assistant while the preview is open (e.g.
         clicking the Try Fleet Assistant CTA inside the preview pops FA
         on top — bootstrap's default focus-trap would otherwise steal
         every keystroke back to the modal and FA's input would appear
         frozen). the preview isn't a form, so trapping focus adds no
         safety value here.

         a11y trade-off: focus-trapping a modal is a WCAG 2.4.3 (focus
         order) pattern. turning it off here is acceptable because
         (1) this modal is admin-only, (2) its content is a read-only
         preview iframe with no interactive controls outside the
         CTAs we handle ourselves, and (3) the one escape hatch we
         care about (typing into FA) is otherwise blocked. do NOT
         copy this flag to any modal with form fields or other
         interactive content. -->
    <b-modal
      v-model="showAnnouncementPreviewModal"
      title="Preview announcement"
      size="lg"
      hide-footer
      no-enforce-focus
      body-class="nc-preview-modal-body"
    >
      <!-- service_alert + banner only: mirrors service_alert_banner.vue
           visually (amber strip, icon puck, bold title, truncated
           preview line) so the admin sees how the top-of-page banner
           will read before publishing. -->
      <div v-if="announcementKind === 'service_alert' && announcementBanner" class="nc-banner-preview-wrap">
        <div class="nc-preview-section-label">Top-of-page banner</div>
        <div class="nc-banner-preview" role="presentation">
          <span v-if="_isSvgIconWrap(composerIcon)" class="nc-banner-preview-icon ni-svg" v-html="_svgIconHtmlWrap(composerIcon)" aria-hidden="true"></span>
          <i v-else class="fa nc-banner-preview-icon" :class="composerIcon" aria-hidden="true"></i>
          <span class="nc-banner-preview-body">
            <span class="nc-banner-preview-title">{{ announcementPreviewTitle }}</span>
            <span v-if="announcementBannerPreviewText" class="nc-banner-preview-text">{{ announcementBannerPreviewText }}</span>
          </span>
          <span class="nc-banner-preview-read">Read more <i class="fa fa-angle-right" aria-hidden="true"></i></span>
        </div>
        <div class="nc-preview-section-label nc-preview-section-label--card">Notification detail</div>
      </div>

      <div class="nc-preview-card">
        <div class="nc-preview-header">
          <div class="nc-preview-title">{{ announcementPreviewTitle }}</div>
          <div class="nc-preview-meta">
            <span class="nc-preview-badge nc-preview-badge-target" :class="announcementTarget === 'global' ? 'nc-preview-badge-global' : 'nc-preview-badge-tenant'">
              <i class="fa" :class="announcementTarget === 'global' ? 'fa-globe' : 'fa-building-o'" aria-hidden="true"></i>
              {{ announcementTarget === 'global' ? 'All customers' : previewTenantLabel }}
            </span>
            <span v-if="announcementRoles && announcementRoles.length" class="nc-preview-badge nc-preview-badge-roles" :title="announcementRoles.map(roleLabel).join(', ')">
              <i class="fa fa-users" aria-hidden="true"></i>
              {{ rolesSummary(announcementRoles) }}
            </span>
            <span class="nc-preview-badge nc-preview-badge-author">
              <i class="fa fa-user-o" aria-hidden="true"></i>
              {{ (user && user.full_name) || (user && user.username) || 'Initek' }}
            </span>
            <span class="nc-preview-time">Now</span>
          </div>
        </div>
        <!-- html format: isolated iframe so author CSS can't leak. the
             srcdoc computed wraps the author's HTML in a minimal
             doctype with a base reset so pasted marketing templates
             look right without boilerplate. markdown format falls
             through to the shared markdown-content styling. -->
        <template v-if="announcementFormat === 'html'">
          <div v-if="!(announcementHtml && announcementHtml.trim())" class="nc-preview-empty">
            <i class="fa fa-file-text-o" aria-hidden="true"></i>
            Nothing to preview yet — paste some HTML in the body.
          </div>
          <iframe
            v-else
            class="nc-preview-iframe"
            :srcdoc="announcementPreviewSrcdoc"
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            @load="_resizeAnnouncementIframe"
          ></iframe>
        </template>
        <template v-else>
          <div v-if="!announcementPreviewHtml" class="nc-preview-empty">
            <i class="fa fa-file-text-o" aria-hidden="true"></i>
            Nothing to preview yet — type something in the body.
          </div>
          <div v-else class="markdown-content markdown-page nc-preview-body" v-html="announcementPreviewHtml"></div>
        </template>
      </div>
      <div class="nc-preview-hint">
        <i class="fa fa-eye" aria-hidden="true"></i>
        Recipients see the title + a short preview in their profile dropdown; clicking opens this full view.
      </div>
    </b-modal>

    <!-- Markdown source modal — FI-1432 -->
    <b-modal
      v-model="showMarkdownPreviewModal"
      :title="announcementFormat === 'html' ? 'HTML source' : 'Markdown source'"
      size="lg"
      hide-footer
    >
      <div class="nc-preview-note text-muted small mb-2 d-flex align-items-center justify-content-between">
        <span>Read-only view of the {{ announcementFormat === 'html' ? 'HTML' : 'markdown' }} that will be stored on publish.</span>
        <b-button size="sm" variant="outline-secondary" @click="copyMarkdownToClipboard">
          <i class="fa fa-copy"></i> Copy
        </b-button>
      </div>
      <pre class="nc-markdown-source">{{ markdownPreviewText || '(empty)' }}</pre>
    </b-modal>

    <!-- Icon Explorer Modal — admin design tool. shows every FA icon
         loaded by the app, lets the user filter by name and preview
         against any of the three category palettes (or a custom hex).
         clicking an icon ON the allowlist adopts it for the announcement;
         clicking an icon OFF the allowlist just stages it in the inspector
         so the admin can copy the class name to request adding it. -->
    <b-modal
      v-model="showIconExplorer"
      title="Icon explorer"
      size="xl"
      scrollable
      hide-footer
      @shown="_onIconExplorerShown"
      @hidden="_onIconExplorerHidden"
    >
      <div class="icon-explorer">
        <!-- sticky controls bar — search + palette buttons stay pinned at
             the top of the scrollable modal so admins can keep adjusting
             filters while scrolling through 700+ tiles. -->
        <div class="icon-explorer-sticky">
          <p class="icon-explorer-intro text-muted small mb-2">
            Browse every standard icon. Allowlisted icons (the curated set) are picked for the announcement on click. Others are inspect-only — copy the class to request adding it.
          </p>
          <div class="icon-explorer-controls">
          <b-form-input
            ref="iconExplorerSearchInput"
            v-model="iconExplorerSearch"
            placeholder="Filter by name (e.g. clock, plug, heart)..."
            size="sm"
            class="icon-explorer-search"
          />
          <div class="icon-explorer-color-picker">
            <span class="icon-explorer-color-label">Preview palette:</span>
            <button
              v-for="opt in iconExplorerColorOptions"
              :key="opt.label"
              type="button"
              class="icon-explorer-color-btn"
              :class="{ active: iconExplorerColor === opt.color }"
              :style="{ background: opt.bg, borderColor: opt.color, color: opt.color }"
              @click="_setIconExplorerPreset(opt)"
            >
              <i class="fa fa-circle" aria-hidden="true"></i>
              {{ opt.label }}
            </button>
            <label class="icon-explorer-custom-wrap" title="Custom hex">
              <span class="icon-explorer-color-label">Custom:</span>
              <input
                type="color"
                v-model="iconExplorerColor"
                class="icon-explorer-color-input"
                aria-label="Custom preview color"
              />
            </label>
          </div>
        </div>
        </div>

        <div v-if="iconExplorerSelected" class="icon-explorer-selected">
          <!-- dual-size preview: detail-page puck (48px) on the left,
               list/dropdown puck (28px) on the right with size labels
               so admins can evaluate how a glyph reads at both common
               sizes before adopting. both pucks share the same palette
               tokens so only the dimensions differ. -->
          <div class="icon-explorer-selected-pucks">
            <div class="icon-explorer-puck-stack">
              <span class="icon-explorer-puck icon-explorer-puck--detail" :style="explorerPuckStyle">
                <span v-if="_isSvgIconWrap(iconExplorerSelected)" class="ni-svg" v-html="_svgIconHtmlWrap(iconExplorerSelected)" aria-hidden="true"></span>
                <i v-else class="fa" :class="iconExplorerSelected" aria-hidden="true"></i>
              </span>
              <span class="icon-explorer-puck-size-label">Detail</span>
            </div>
            <div class="icon-explorer-puck-stack">
              <span class="icon-explorer-puck icon-explorer-puck--list" :style="explorerPuckStyleList">
                <span v-if="_isSvgIconWrap(iconExplorerSelected)" class="ni-svg" v-html="_svgIconHtmlWrap(iconExplorerSelected)" aria-hidden="true"></span>
                <i v-else class="fa" :class="iconExplorerSelected" aria-hidden="true"></i>
              </span>
              <span class="icon-explorer-puck-size-label">List</span>
            </div>
          </div>
          <div class="icon-explorer-selected-meta">
            <code class="icon-explorer-selected-name">{{ iconExplorerSelected }}</code>
            <span v-if="_isAllowedIconWrap(iconExplorerSelected)" class="icon-explorer-allow-badge icon-explorer-allow-badge--yes">
              <i class="fa fa-check-circle"></i> on allowlist
            </span>
            <span v-else class="icon-explorer-allow-badge icon-explorer-allow-badge--no">
              <i class="fa fa-circle-o"></i> not on allowlist
            </span>
          </div>
          <div class="icon-explorer-selected-actions">
            <b-button size="sm" variant="outline-secondary" @click="_copyIconClass(iconExplorerSelected)">
              <i class="fa fa-copy"></i>
              {{ iconExplorerCopied === iconExplorerSelected ? 'Copied' : 'Copy class' }}
            </b-button>
            <b-button
              v-if="_isAllowedIconWrap(iconExplorerSelected)"
              size="sm"
              variant="outline-primary"
              @click="_adoptExplorerIcon"
            >
              Use this icon
            </b-button>
          </div>
        </div>

        <div v-if="!iconExplorerAllIcons.length" class="icon-explorer-empty">
          Couldn't enumerate icons from the loaded stylesheet.
        </div>
        <div v-else-if="!iconExplorerFiltered.length" class="icon-explorer-empty">
          No icons match "{{ iconExplorerSearch }}".
        </div>
        <div v-else class="icon-explorer-grid">
          <button
            v-for="ic in iconExplorerFiltered"
            :key="ic"
            type="button"
            class="icon-explorer-tile"
            :class="{ active: iconExplorerSelected === ic, 'icon-explorer-tile--allowed': _isAllowedIconWrap(ic) }"
            :title="ic"
            @click="_exploreIcon(ic)"
          >
            <span class="icon-explorer-puck icon-explorer-puck--detail" :style="explorerPuckStyle">
              <span v-if="_isSvgIconWrap(ic)" class="ni-svg" v-html="_svgIconHtmlWrap(ic)" aria-hidden="true"></span>
              <i v-else class="fa" :class="ic" aria-hidden="true"></i>
            </span>
          </button>
        </div>
        <div class="icon-explorer-footer text-muted small mt-2">
          {{ iconExplorerFiltered.length }} of {{ iconExplorerAllIcons.length }} icon{{ iconExplorerAllIcons.length === 1 ? '' : 's' }}
        </div>
      </div>
    </b-modal>

    <!-- Article Leaderboard Modal -->
    <b-modal
      v-model="showLeaderboardModal"
      title="Article Feedback Leaderboard"
      size="lg"
      hide-footer
    >
      <div v-if="leaderboardLoading" class="text-center py-3"><b-spinner small></b-spinner> Loading leaderboard...</div>
      <div v-else-if="leaderboardArticles.length === 0" class="text-muted text-center py-3">No feedback data available yet.</div>
      <div v-else class="leaderboard-table-wrap">
        <b-table
          :items="leaderboardArticles"
          :fields="leaderboardFields"
          :sort-by.sync="leaderboardSortBy"
          :sort-desc.sync="leaderboardSortDesc"
          small
          striped
          hover
          responsive
          sort-icon-left
          class="leaderboard-table"
        >
          <template slot="title" slot-scope="row">
            <a href="#" @click.prevent="leaderboardNavigate(row.item)">{{ row.item.title }}</a>
          </template>
          <template slot="article_type" slot-scope="row">
            <span class="help-badge type">{{ row.item.article_type || 'faq' }}</span>
          </template>
          <template slot="helpful_pct" slot-scope="row">
            <span :class="leaderboardPctClass(row.item.helpful_pct)">{{ row.item.helpful_pct != null ? row.item.helpful_pct + '%' : '-' }}</span>
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- Comment Inbox Modal -->
    <b-modal
      v-model="showCommentInboxModal"
      title="Feedback Comments"
      size="lg"
      hide-footer
    >
      <div v-if="commentInboxLoading" class="text-center py-3"><b-spinner small></b-spinner> Loading comments...</div>
      <div v-else-if="commentInboxItems.length === 0" class="text-muted text-center py-3">No comments to review.</div>
      <table v-else class="fb-inbox-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>User</th>
            <th>Article</th>
            <th style="text-align: center;">Rating</th>
            <th>Comment</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in commentInboxItems" :key="idx" :class="{ 'row-reviewed': item.status === 'reviewed' || item.status === 'actioned' }">
            <td style="white-space: nowrap;">{{ formatFeedbackDate(item.created_at) }}</td>
            <td>{{ item.user_id || '-' }}</td>
            <td>{{ item.article_title || item.doc_id || '-' }}</td>
            <td style="text-align: center;">
              <i v-if="item.helpful === true" class="fa fa-thumbs-up" style="color: #28a745;"></i>
              <i v-else class="fa fa-thumbs-down" style="color: #dc3545;"></i>
            </td>
            <td class="fb-comment-cell">{{ item.comment || '-' }}</td>
            <td><span class="help-badge" :class="feedbackStatusClass(item.status)">{{ item.status || 'new' }}</span></td>
            <td>
              <b-button v-if="(item.status || 'new') !== 'reviewed'" size="sm" variant="outline-success" @click="markCommentReviewed(item)" :disabled="item._updating">
                Mark Reviewed
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Feedback Detail Modal -->
    <b-modal
      v-model="showFeedbackDetailModal"
      title="Feedback Detail"
      size="lg"
      @hidden="onFeedbackDetailClosed"
      hide-footer
    >
      <template v-if="feedbackDetailItem">
        <!-- fa_response context -->
        <template v-if="feedbackDetailItem.feedback_type === 'fa_response' && feedbackDetailItem.context">
          <div class="fb-detail-section">
            <label>User Question</label>
            <div class="fb-detail-box">{{ feedbackDetailItem.context.user_message || '-' }}</div>
          </div>
          <div class="fb-detail-section">
            <label>Assistant Response</label>
            <div class="fb-detail-box" style="white-space: pre-wrap;">{{ feedbackDetailItem.context.assistant_response || '-' }}</div>
          </div>
          <div class="fb-detail-meta-grid">
            <div v-if="feedbackDetailItem.context.model"><strong>Model:</strong> {{ feedbackDetailItem.context.model }}</div>
            <div v-if="feedbackDetailItem.context.tokens"><strong>Tokens:</strong> {{ feedbackDetailItem.context.tokens }}</div>
            <div v-if="feedbackDetailItem.context.cost"><strong>Cost:</strong> ${{ feedbackDetailItem.context.cost }}</div>
            <div v-if="feedbackDetailItem.context.route_name"><strong>Route:</strong> {{ feedbackDetailItem.context.route_name }}</div>
            <div v-if="feedbackDetailItem.context.session_id">
              <strong>Session ID:</strong>
              <code class="fb-session-id" @click="copyToClipboard(feedbackDetailItem.context.session_id)" title="Click to copy">{{ feedbackDetailItem.context.session_id }}</code>
            </div>
          </div>
          <!-- action buttons -->
          <div class="fb-detail-section" style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">
            <b-button v-if="feedbackDetailItem.context.session_id" size="sm" variant="outline-primary" @click="toggleConversationSession()" :disabled="conversationLoading">
              <span v-if="conversationLoading"><b-spinner small></b-spinner> Loading...</span>
              <span v-else-if="conversationMessages.length > 0"><i class="fa fa-eye-slash" style="margin-right: 4px;"></i>Hide Full Session</span>
              <span v-else><i class="fa fa-comments-o" style="margin-right: 4px;"></i>View Full Session</span>
            </b-button>
            <b-button size="sm" :variant="feedbackExported ? 'success' : 'outline-success'" @click="exportFeedbackDetail">
              <i :class="feedbackExportIsFile ? 'fa fa-download' : 'fa fa-clipboard'" style="margin-right: 4px;"></i>{{ feedbackExported ? (feedbackExportIsFile ? 'Downloaded!' : 'Copied!') : (feedbackExportIsFile ? 'Export to File' : 'Export to Clipboard') }}
            </b-button>
            <!-- conversation viewer -->
            <div v-if="conversationMessages.length > 0" class="fb-conversation-viewer" style="background: #f9f9f9; padding: 16px; border-radius: 8px; max-height: 500px; overflow-y: auto;">
              <div v-for="(msg, mIdx) in conversationMessages" :key="mIdx" style="margin-bottom: 15px;">
                <!-- user bubble: right-aligned, blue -->
                <div v-if="msg.role === 'user'" style="text-align: right;">
                  <div style="display: inline-block; background-color: #007ac7; color: white; padding: 10px 15px; border-radius: 18px; max-width: 70%; word-wrap: break-word;">
                    {{ msg.content }}
                  </div>
                </div>
                <!-- assistant bubble: left-aligned, grey, markdown rendered -->
                <div v-else style="text-align: left;">
                  <div style="display: inline-block; background-color: #e9ecef; color: #333; padding: 10px 15px; border-radius: 18px; max-width: 70%; word-wrap: break-word;" class="markdown-content" v-html="renderConversationMarkdown(msg.content)"></div>
                  <div class="fb-chat-meta">
                    <span v-if="msg.model">{{ msg.model }}</span>
                    <span v-if="msg.tokens">{{ msg.tokens }} tokens</span>
                    <span v-if="msg.cost">${{ typeof msg.cost === 'number' ? '$' + msg.cost.toFixed(4) : msg.cost }}</span>
                    <span v-if="msg.tool_calls && msg.tool_calls.length > 0" class="fb-tool-calls">
                      <i class="fa fa-wrench" style="margin-right: 2px;"></i>
                      <span v-for="(tc, tcIdx) in msg.tool_calls" :key="tcIdx" class="fb-tool-call-pill">
                        {{ tc.name }}<span v-if="tc.duration_ms" style="color: #888;"> {{ tc.duration_ms }}ms</span><span v-if="tcIdx < msg.tool_calls.length - 1">, </span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- help_article context -->
        <template v-else-if="feedbackDetailItem.feedback_type === 'help_article' && feedbackDetailItem.context">
          <div class="fb-detail-section">
            <label>Article</label>
            <div class="fb-detail-box">{{ feedbackDetailItem.context.article_title || feedbackDetailItem.context.doc_id || '-' }}</div>
          </div>
          <div v-if="feedbackDetailItem.context.route_name" class="fb-detail-section">
            <label>Route</label>
            <div>{{ feedbackDetailItem.context.route_name }}</div>
          </div>
        </template>

        <!-- topic_suggestion context -->
        <template v-else-if="feedbackDetailItem.feedback_type === 'topic_suggestion' && feedbackDetailItem.context">
          <div class="fb-detail-section">
            <label>Search Term</label>
            <div class="fb-detail-box">{{ feedbackDetailItem.context.search_query || '-' }}</div>
          </div>
        </template>

        <!-- generic fallback -->
        <template v-else>
          <div v-if="feedbackDetailItem.context_preview" class="fb-detail-section">
            <label>Context</label>
            <div class="fb-detail-box">{{ feedbackDetailItem.context_preview }}</div>
          </div>
        </template>

        <!-- common fields -->
        <div class="fb-detail-section" style="margin-top: 12px;">
          <label>Comment</label>
          <div class="fb-detail-box">{{ feedbackDetailItem.comment || 'No comment provided.' }}</div>
        </div>
        <div class="fb-detail-section">
          <label>Rating</label>
          <span v-if="feedbackDetailItem.rating === 'positive'" style="color: #28a745; font-weight: 600;"><i class="fa fa-thumbs-up"></i> Positive</span>
          <span v-else-if="feedbackDetailItem.rating === 'negative'" style="color: #b58100; font-weight: 600;"><i class="fa fa-thumbs-down"></i> Negative</span>
          <span v-else-if="feedbackDetailItem.rating === 'concern'" style="color: #dc3545; font-weight: 600;"><i class="fa fa-flag"></i> Concern</span>
          <span v-else style="color: #dc3545; font-weight: 600;"><i class="fa fa-flag"></i> Concern</span>
        </div>
        <div class="fb-detail-section">
          <label>Status</label>
          <span class="help-badge" :class="feedbackStatusClass(feedbackDetailItem.status)">{{ feedbackDetailItem.status || 'new' }}</span>
          <span v-if="feedbackDetailItem.reviewed_by" class="ml-2" style="font-size: 12px; color: #888;">by {{ feedbackDetailItem.reviewed_by }}</span>
        </div>
      </template>
    </b-modal>

    <!-- Manage Company Documents Modal (reuses FA settings modal) -->
    <b-modal
      v-model="showDocManageModal"
      title="Manage Company Documents"
      size="lg"
      hide-footer
      @hidden="onDocManageModalClosed"
    >
      <p style="font-size: 13px; color: #666; margin-bottom: 12px;">
        Upload company documents like employee handbooks, policies, or procedures. These documents are available for your team to browse and download in the Company Documents tab, and Fleet Assistant uses them as reference when answering questions. Upload PDF, DOCX, or TXT files. Max 25 MB per file. Max 25 documents.
      </p>

      <!-- over-budget warning: document categorization requires AI, so uploads are blocked -->
      <b-alert v-if="managedBudgetOverLimit" show variant="warning" class="mb-3 uploads-disabled-alert">
        <div class="callout-head">
          <!-- anchor icon: full parking-sign P rendered in amber.
               outer rounded square (sign body) + thin white inset border (the
               distinctive detail that makes it read as a real sign) + white P
               with a bowl hole that shows amber through (evenodd fill). -->
          <svg class="callout-anchor-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <rect x="1" y="1" width="14" height="14" rx="2.5" ry="2.5" fill="currentColor"/>
            <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="1.6" ry="1.6" fill="none" stroke="#fff" stroke-width="0.7"/>
            <path fill="#fff" fill-rule="evenodd" d="M5.5 4.5v7H7V9h1.5a2.25 2.25 0 0 0 0-4.5H5.5Zm1.5 1.5v1.75h1.5a0.875 0.875 0 0 0 0-1.75H7Z"/>
          </svg>
          <span class="fa-badge disabled">Uploads Paused</span>
        </div>
        <div class="callout-body">
          <p>Your organization has reached its monthly AI usage limit. New documents need to be categorized by AI, which is currently blocked. Existing documents remain available for download. AI usage resets monthly.</p>
          <div class="uploads-disabled-action">
            <button type="button" class="callout-cta-btn" @click="$root.$emit('open-ai-usage-request-modal')">
              Request Additional Usage
            </button>
          </div>
        </div>
      </b-alert>

      <!-- upload row -->
      <div class="d-flex align-items-center gap-2 mb-3">
        <input
          ref="managedDocPicker"
          type="file"
          accept=".pdf,.docx,.txt"
          style="display: none"
          multiple
          @change="onManagedDocPicked"
        />
        <b-button
          size="sm"
          variant="primary"
          :disabled="managedBudgetOverLimit"
          :title="managedBudgetOverLimit ? 'Uploads are disabled while over the monthly AI budget' : ''"
          @click="$refs.managedDocPicker.click()"
        >
          Add Document(s)
        </b-button>
        <b-button
          v-if="managedSelectedDocs.length > 0"
          class="ml-2"
          size="sm"
          variant="danger"
          @click="deleteManagedSelectedDocs"
          :disabled="managedDeletingSelected"
        >
          <span v-if="managedDeletingSelected"><b-spinner small></b-spinner> DELETING {{ managedDeleteProgress }}</span>
          <span v-else>Delete Selected ({{ managedSelectedDocs.length }})</span>
        </b-button>
        <span v-if="managedDocsError" class="text-danger small">{{ managedDocsError }}</span>
      </div>

      <!-- summary -->
      <div v-if="managedDocs.length > 0" class="docs-summary mb-2">
        <strong>{{ managedDocs.length }}</strong> document{{ managedDocs.length === 1 ? '' : 's' }} · <strong>{{ formatFileSize(managedDocs.reduce((s, d) => s + (d.file_size || 0), 0)) }}</strong> total
      </div>

      <!-- documents table -->
      <div v-if="managedDocsLoading" class="text-muted small">Loading documents...</div>
      <div v-else-if="managedDocs.length === 0" class="text-muted small">No company documents uploaded yet.</div>
      <table v-else class="company-docs-table">
        <thead>
          <tr>
            <th style="width: 40px;">
              <input
                type="checkbox"
                :checked="allManagedDocsSelected"
                :indeterminate.prop="someManagedDocsSelected && !allManagedDocsSelected"
                @change="toggleManagedSelectAll"
              />
            </th>
            <th>Name</th>
            <th>Uploaded By</th>
            <th>Date</th>
            <th>Size</th>
            <th style="width: 80px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="doc in managedDocs"
            :key="doc.doc_id || doc.temp_id"
            :class="{ 'row-selected': doc.doc_id && managedSelectedDocs.includes(doc.doc_id), 'row-uploading': doc.status === 'uploading' || doc.status === 'processing', 'row-failed': doc.status === 'failed' || doc.status === 'delete-failed', 'row-deleting': doc.status === 'deleting' }"
          >
            <td>
              <input
                type="checkbox"
                :checked="doc.doc_id && managedSelectedDocs.includes(doc.doc_id)"
                :disabled="!doc.doc_id || doc.status === 'uploading' || doc.status === 'processing' || doc.status === 'deleting'"
                @change="toggleManagedDocSelection(doc.doc_id)"
              />
            </td>
            <td class="doc-name" :title="doc.display_name">{{ doc.display_name }}</td>
            <td class="doc-uploader">{{ doc.uploaded_by || '\u2014' }}</td>
            <td class="doc-date">
              <span v-if="doc.status === 'uploading'" class="doc-badge upload">UPLOADING</span>
              <span v-else-if="doc.status === 'processing'" class="doc-badge processing pulsing">LEARNING</span>
              <span v-else-if="doc.status === 'deleting'" class="doc-badge deleting pulsing">DELETING</span>
              <span v-else-if="doc.status === 'delete-failed'" class="doc-badge failed">DELETE FAILED</span>
              <span v-else-if="doc.status === 'failed'" class="doc-badge failed">FAILED</span>
              <span v-else>{{ formatDocDate(doc.uploaded_at) }}</span>
            </td>
            <td class="doc-size">{{ formatFileSize(doc.file_size) }}</td>
            <td class="doc-actions">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="deleteManagedDoc(doc.doc_id)"
                :disabled="!doc.doc_id || doc.status === 'uploading' || doc.status === 'processing' || doc.status === 'deleting' || managedDocDeleting === doc.doc_id || managedDeletingSelected"
              >
                <span v-if="managedDocDeleting === doc.doc_id"><b-spinner small></b-spinner></span>
                <span v-else>Delete</span>
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </b-modal>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { list_help_articles, get_help_article, submit_help_feedback, record_help_article_view, get_help_feedback, list_help_company_documents, get_help_company_document, download_help_company_document, list_fa_documents_company, presign_fa_document_company, finalize_fa_document_company, delete_fa_document_company, list_chat_feedback, update_chat_feedback_status, delete_chat_feedback, restore_chat_feedback, get_chat_feedback_stats, get_chat_feedback_item, get_help_feedback_leaderboard, get_admin_chat_session, get_ai_usage, admin_create_announcement, admin_list_announcements, admin_get_announcement_stats, admin_get_notifications_system_stats, admin_delete_announcement, admin_retract_announcement, admin_get_announcement, admin_update_announcement, admin_publish_announcement, admin_expire_announcement_banner, presign_announcement_image, announcement_image_url } from '../networking/apis'
import { tbn_list_help_articles, tbn_get_help_article, tbn_submit_help_feedback, tbn_record_help_article_view, tbn_get_help_feedback, tbn_list_help_company_documents, tbn_get_help_company_document, tbn_download_help_company_document, tbn_admin_create_announcement, tbn_admin_list_announcements, tbn_admin_get_announcement_stats, tbn_admin_get_notifications_system_stats, tbn_admin_delete_announcement, tbn_admin_retract_announcement } from '../networking/tbn_apis'
import { renderMarkdownToHtml } from '../utils/markdown_renderer'
import { NOTIFICATION_ICON_GROUPS, defaultIconForKind, isDefaultIconForKind, isAllowedIcon, iconForAnnouncement, getAllFaIcons, isSvgIcon, customSvgHtml } from '../utils/notification_icons'
// FI-1432 toast ui markdown editor for admin announcement composer
import { Editor as ToastEditor } from '@toast-ui/vue-editor'
import '@toast-ui/editor/dist/toastui-editor.css'
import moment from 'moment/moment'
import SupportTicketList from './support_ticket_list.vue'
import SupportTicketDetails from './support_ticket_details.vue'
// Assistant Insights (admin dashboard) — FI-1600
import FaFleetView from './fa_admin/fa_fleet_view.vue'
import FaTenantView from './fa_admin/fa_tenant_view.vue'
import FaUserView from './fa_admin/fa_user_view.vue'
import FaConversationView from './fa_admin/fa_conversation_view.vue'
import {
  apiAiUsage,
  apiAiUsageHistory,
  apiAiUsageUsers,
  apiAiUsageFleet,
  apiAdminChatLiveSession,
  apiListArchivedSessions,
  apiGetArchivedSession,
  apiToolsSummary,
  apiListCompanyDocs,
  tenantLabelMap,
  anomalyFlagsForRow,
  projectLimitHitDate,
} from '../helpers/fa_admin'

const SIDEBAR_MIN_WIDTH = 200
const SIDEBAR_MAX_WIDTH = 600

export default {
  name: 'help_center',
  components: {
    SupportTicketList,
    SupportTicketDetails,
    FaFleetView,
    FaTenantView,
    FaUserView,
    FaConversationView,
    ToastEditor,
  },
  data() {
    return {
      activeTab: 'articles', // overridden in created() from ?tab= query
      // FI-1432 notification center admin tab.
      // the announcements tab follows the support ticket list->detail
      // flow: `announcementView` is either 'list' (landing) or 'detail'
      // (compose/edit/read). `announcementDetailMode` picks between
      // 'compose' (drafts + new) and 'read' (published). `announcementDraftId`
      // is the ULID of the draft we're editing when resuming from the list.
      notificationsTabLoaded: false,
      announcementView: 'list',
      announcementDetailMode: null,
      announcementDraftId: null,
      announcementDraftSaving: false,
      announcementDraftLastSavedAt: null,
      announcementTitle: '',
      // body_format picks the renderer on the client. 'markdown' routes the
      // body through the ToastUI editor + shared markdown renderer (the
      // default for most announcements). 'html' swaps in a raw textarea
      // and renders the body verbatim via an iframe srcdoc so slick
      // marketing announcements can bring their own CSS without leaking
      // into the app shell. each mode has its own buffer so switching
      // back and forth doesn't destroy work.
      announcementFormat: 'markdown',
      announcementFormatOptions: [
        { text: 'Rich text', value: 'markdown' },
        { text: 'HTML', value: 'html' },
      ],
      // kind selects the notification "flavor" — Announcement (default,
      // product updates / broadcast messages) or Service Alert (outages
      // and service-impacting issues). service alerts fan out as a
      // distinct notification type so the client can render them with
      // amber-accented visuals and the email opt-in routes through the
      // separate email_service_alerts user pref.
      announcementKind: 'announcement',
      announcementKindOptions: [
        { text: 'Announcement', value: 'announcement' },
        { text: 'Service alert', value: 'service_alert' },
        { text: 'Service resolved', value: 'service_resolved' },
      ],
      // site-wide banner flag — only honored by the server when kind is
      // service_alert. composer re-hides the switch when Announcement is
      // selected; saved drafts still keep this value so toggling back to
      // service_alert restores it.
      announcementBanner: false,
      // opt-in email fan-out flag. default off — admins explicitly
      // choose which announcements also email recipients. honored for
      // all kinds (both announcements and service alerts), still subject
      // to each recipient's per-channel email preference at fan-out.
      announcementEmail: false,
      // per-row icon override. empty string = use the kind default
      // (bullhorn for announcement, exclamation-triangle for service
      // alert). when the user picks something else from the icon picker
      // the value is one of NOTIFICATION_ICON_ALLOWLIST and the puck
      // glyph follows. the puck COLOR still tracks `kind` so changing
      // the icon doesn't affect the urgency read.
      announcementIcon: '',
      // grouped picker registry. shared with all client surfaces
      // (navbar/list/detail/banner) so a single source of truth picks
      // the icon for any notification or announcement row.
      announcementIconGroups: NOTIFICATION_ICON_GROUPS,
      // when true, the picker grid is shown; default collapsed so the
      // composer stays compact and admins can opt in to picking only
      // when they want a non-default glyph.
      showIconPicker: false,
      // ICON EXPLORER (admin design-tool): browse every standard FA icon
      // in a modal, filter by name, and preview against any of the three
      // category palettes (or a custom hex). this is for evaluating
      // candidates to add to the curated set — clicking a tile here
      // doesn't pick the icon for the announcement; it shows the icon
      // class so the admin can request adding it to the registry.
      showIconExplorer: false,
      iconExplorerSearch: '',
      // active preview color tokens. defaults to the announcement palette
      // since that's the most common kind. the three preset buttons swap
      // these in lockstep; the custom color input drives them with a
      // derived bg/border so any hex works.
      iconExplorerColor: '#7c5dd0',
      iconExplorerBg: '#f3f0ff',
      iconExplorerBorder: '#d8cffb',
      iconExplorerColorOptions: [
        { label: 'Announcement', color: '#7c5dd0', bg: '#f3f0ff', border: '#d8cffb' },
        { label: 'Service alert', color: '#d97706', bg: '#fef3c7', border: '#fde68a' },
        { label: 'Service resolved', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
      ],
      iconExplorerSelected: '',
      iconExplorerCopied: '',
      // populated lazily from getAllFaIcons() when the explorer first opens
      // so the cost of scanning stylesheets is paid only when needed.
      iconExplorerAllIcons: [],
      // service-alert banner auto-expiry (datetime-local string, in the
      // user's local timezone — converted to UTC ISO before sending).
      // empty string = no expiry. only meaningful when kind=service_alert
      // AND banner=true; the input is gated on those flags in the
      // template, but the value persists across toggles so flipping
      // banner off+on doesn't lose the chosen time.
      announcementBannerExpiresAtLocal: '',
      // tracks which preset chip is highlighted: '1h' / '4h' / '8h' / '24h'
      // for the quick-set chips, 'custom' for the datetime-local picker,
      // 'none' when nothing is scheduled. drift between this and the iso
      // value is allowed: a custom time picked via the input keeps mode
      // = 'custom' so the picker stays visible.
      _bannerExpiryPresetMode: 'none',
      // preset chips offered next to the datetime-local input. picked
      // for the typical outage window range (under a day). values are
      // hours; 'custom' / 'none' are handled separately in the click
      // handler so they don't need to live in this list.
      bannerExpiryPresetOptions: [
        { value: 1, text: '+1h', title: 'Auto-clear the banner one hour from now' },
        { value: 4, text: '+4h', title: 'Auto-clear the banner four hours from now' },
        { value: 8, text: '+8h', title: 'Auto-clear the banner eight hours from now' },
        { value: 24, text: '+24h', title: 'Auto-clear the banner one day from now' },
      ],
      announcementHtml: '',
      announcementTarget: 'global',
      announcementTargetOptions: [
        { text: 'All customers', value: 'global' },
        { text: 'Specific customer', value: 'tenant' },
      ],
      announcementTenantId: null,
      // default role selection: NONE. empty list is interpreted server-side
      // as "everyone in the target", so a blank composer still reaches all
      // users if the admin publishes without touching roles. forcing an
      // explicit choice avoids over-sending when the intent was narrower.
      announcementRoles: [],
      rolesShortcutRunning: false,
      pulsingRoles: [],
      // kept in sync with the role vocabulary used in navbar.vue's role
      // label map. labels mirror what users see on their profile chip so
      // admins pick from the same taxonomy. this picker is admin-only
      // (announcement composer is gated to role=admin), so the internal
      // "Sys Admin" term is OK here. customer-admin reads as "Fleet Admin"
      // to match every customer-visible surface.
      announcementRoleOptions: [
        { value: 'admin', text: 'Sys Admin', icon: 'fa-shield' },
        { value: 'customer-admin', text: 'Fleet Admin', icon: 'fa-cogs' },
        { value: 'customer', text: 'Staff', icon: 'fa-user' },
        { value: 'manager', text: 'Manager', icon: 'fa-briefcase' },
        { value: 'dispatch', text: 'Dispatch', icon: 'fa-compass' },
        { value: 'driver', text: 'Driver', icon: 'fa-id-badge' },
        { value: 'garage', text: 'Garage', icon: 'fa-wrench' },
        { value: 'wash-bay', text: 'Wash Bay', icon: 'fa-tint' },
        { value: 'customer-care', text: 'Customer Care', icon: 'fa-headphones' },
        { value: 'booking-customer', text: 'Booking Customer', icon: 'fa-calendar-check-o' },
      ],
      announcementPublishing: false,
      announcementFlash: null,
      announcementFlashVariant: 'success',
      announcementImageUploading: false,
      // toastEditorOptions is defined as a computed so addImageBlobHook
      // can close over `this` — the hook presigns an S3 POST, uploads the
      // pasted/dropped image, and calls back with the stable image URL.
      announcementList: [],
      announcementListLoading: false,
      systemStats: null,
      systemStatsLoaded: false,
      statsLoading: false,
      statsWindowDays: 7,
      statsWindowOptions: [
        { text: '7d', value: 7 },
        { text: '30d', value: 30 },
      ],
      announcementDetail: null,
      announcementDetailLoading: false,
      showMarkdownPreviewModal: false,
      markdownPreviewText: '',
      showAnnouncementPreviewModal: false,
      announcementPreviewTitle: '',
      announcementPreviewHtml: '',
      announcementBannerPreviewText: '',
      announcementToDelete: null,
      // tracked separately from announcementToDelete because the expire
      // and retract modals are different actions with different copy +
      // button variants. set when the admin clicks "Expire banner now"
      // on a published service_alert; cleared on hide / confirm.
      bannerToExpire: null,
      bannerExpireRunning: false,
      announcementDeleting: false,
      // default: most recent first by created_at.
      statsSortColumn: 'effective_at',
      statsSortDirection: 'desc',
      // filter pills above the announcement table. defaults to 'all' so
      // first load shows everything; persists in component state only —
      // no query-param sync because admins typically scan the full list
      // and the filter is a "while I'm here" working state.
      announcementListFilter: 'all',
      // filter pill registry — order matters; "All" stays first so it
      // reads as the default. "Active banners" gets a warning accent
      // because it's the during-incident hotspot the admin needs in a
      // hurry. icons mirror the cells they filter on so the pill set
      // visually rhymes with the table.
      announcementListFilters: [
        { value: 'all', text: 'All', icon: '' },
        { value: 'banners_active', text: 'Active banners', icon: 'fa-bullhorn', accent: 'warning' },
        { value: 'service_alerts', text: 'Service alerts', icon: 'fa-exclamation-triangle' },
        { value: 'service_resolved', text: 'Resolved', icon: 'fa-check-circle', accent: 'success' },
        { value: 'drafts', text: 'Drafts', icon: 'fa-pencil' },
        { value: 'published', text: 'Published', icon: 'fa-paper-plane-o' },
      ],
      // undo buffer for Reset: when set, the Reset button morphs into a
      // Restore button until the user starts composing again (typing,
      // changing target, picking a tenant, or editing the body).
      announcementResetSnapshot: null,
      loadingArticles: false,
      loadingArticle: false,
      feedbackSubmitting: false,
      feedbackValue: null,
      feedbackComment: '',
      feedbackCommentSubmitted: false,
      feedbackSummary: null,
      feedbackDetailExpanded: false,
      searchText: '',
      activeSearch: '',
      articles: [],
      selectedArticle: null,
      selectedDocId: null,
      articleContent: '',
      loadingCompanyDocs: false,
      loadingCompanyDoc: false,
      companyDocs: [],
      selectedCompanyDoc: null,
      selectedCompanyDocId: null,
      companyDocContent: '',
      suggestedTopics: ['Getting started', 'Map and tracking', 'Bookings', 'Reports', 'Alerts', 'Drivers and dispatch', 'Fleet Assistant'],
      directoryCollapsed: {},
      downloadingDoc: false,
      topicSuggestionComment: '',
      topicSuggestionSubmitting: false,
      topicSuggestionSubmitted: false,
      // sidebar divider
      articlesSidebarWidth: 320,
      companySidebarWidth: 320,
      isDraggingSidebar: false,
      // document management modal
      showDocManageModal: false,
      managedDocs: [],
      managedDocsLoading: false,
      managedDocsError: '',
      managedUploading: false,
      managedDocDeleting: null,
      managedSelectedDocs: [],
      managedDeletingSelected: false,
      managedDeleteProgress: '',
      _managedDocsPollTimer: null,
      _managedDocsPollDelay: 2000,
      // budget status checked on modal open — disables upload when tenant is over limit
      managedBudgetOverLimit: false,
      managedBudgetMessage: '',
      // captured during a batch upload so we can surface the specific error
      // (e.g. "Monthly AI usage limit reached. Document categorization blocked.")
      managedUploadErrorMessage: '',
      // mobile responsive
      isMobileView: false,
      mobileShowDetail: false,
      // admin: article leaderboard
      showLeaderboardModal: false,
      leaderboardArticles: [],
      leaderboardLoading: false,
      // default sort: worst-rated first so admins spot problem articles
      // at a glance. ascending on helpful_pct puts low numbers on top.
      leaderboardSortBy: 'helpful_pct',
      leaderboardSortDesc: false,
      // true when the user has scrolled the article body past the collapse
      // threshold. drives the `--collapsed` modifier class on the article
      // header so the meta + summary retract and only the title stays. reset
      // to false every time a different article is selected (scrollTop goes
      // back to 0 on render) so each article starts with its full header.
      isHeaderCollapsed: false,
      // admin: comment inbox
      showCommentInboxModal: false,
      commentInboxItems: [],
      commentInboxLoading: false,
      // admin: feedback review tab
      feedbackSubTab: 'fa_response',
      feedbackReviewItems: [],
      feedbackReviewLoading: false,
      feedbackReviewStats: null,
      feedbackReviewLoaded: false,
      fbFilterFromDate: moment().subtract(30, 'days').format('MM-DD-YYYY'),
      fbFilterToDate: moment().format('MM-DD-YYYY'),
      fbFilterRating: '',
      fbFilterStatus: 'open', // default lands on unhandled (NEW + REVIEWED, excludes ACTIONED)
      fbFilterTenant: '',
      fbShowDeleted: false,
      fbPerPage: 25,
      fbCurrentPage: 1,
      fbDatePickerConfig: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        showClear: true,
        showClose: true,
      },
      // admin: feedback detail modal
      showFeedbackDetailModal: false,
      feedbackExported: false,
      feedbackExportIsFile: false,
      feedbackDetailItem: null,
      // admin: conversation viewer
      conversationMessages: [],
      conversationLoading: false,
      // admin: Assistant Insights (FI-1600)
      faFleetRows: [],
      faFleetLoading: false,
      faFleetLoadedAt: 0,
      faFleetLoadedCount: 0,
      faFleetTotalTenants: 0,
      faTenantData: {},
      faTenantLoading: false,
      faTenantLoadedFor: '',
      faUserData: {},
      faUserLoading: false,
      faUserLoadedFor: '',
      faSessionData: {},
      faSessionLoading: false,
      faSessionLoadedFor: '',
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant_type() {
      return this.user.types && this.user.types[this.user.tenant] ? this.user.types[this.user.tenant] : 'CM'
    },
    is_tbn() {
      return this.tenant_type === 'TBN'
    },
    // FI-1432 notification center computeds
    toastEditorOptions() {
      // hideModeSwitch keeps the Markdown/WYSIWYG toggle out of the toolbar —
      // markdown mode is intentionally disabled in this screen.
      // addImageBlobHook handles paste/drop image uploads: it presigns an S3
      // POST against the admin tenant, uploads the blob, then hands the
      // Toast editor a stable /bookings/announcements/images/{id} URL that
      // the user-facing render resolves to a short-lived S3 redirect.
      const self = this
      return {
        usageStatistics: false,
        hideModeSwitch: true,
        hooks: {
          addImageBlobHook: (blob, callback) => {
            self.uploadAnnouncementImageBlob(blob, callback)
          },
        },
      }
    },
    announcementTenantOptions() {
      const base = [{ value: null, text: 'Select a tenant...' }]
      const tenants = (this.user && this.user.tenant_options) || []
      return base.concat(tenants.map(t => {
        if (typeof t === 'object') {
          return { value: t.value || t.tenant_id, text: t.text || t.name || t.label || t.value }
        }
        return { value: t, text: t }
      }))
    },
    renderedAnnouncementBody() {
      const md = this.announcementDetail && this.announcementDetail.body_markdown
      if (!md) return ''
      try { return renderMarkdownToHtml(md) } catch (_) { return md }
    },
    // true when the row currently on the read-only detail pane is an
    // html-format announcement. the read-only template branches on this
    // to swap the markdown v-html block for an iframe srcdoc.
    isAnnouncementDetailHtml() {
      const fmt = this.announcementDetail && this.announcementDetail.body_format
      return fmt === 'html'
    },
    // kind-specific elevated puck for the read-only detail hero. matches
    // the pastel inverted-outline puck family used elsewhere in FI-1432
    // (purple for plain announcements, amber for service alerts).
    readonlyPuckIcon() {
      const k = this.announcementDetail && this.announcementDetail.kind
      if (k === 'service_alert') return 'fa-exclamation-triangle'
      if (k === 'service_resolved') return 'fa-check-circle'
      return 'fa-bullhorn'
    },
    readonlyPuckClass() {
      const k = this.announcementDetail && this.announcementDetail.kind
      if (k === 'service_alert') return 'nc-readonly-puck--alert'
      if (k === 'service_resolved') return 'nc-readonly-puck--resolved'
      return 'nc-readonly-puck--ann'
    },
    readonlyPuckTitle() {
      const k = this.announcementDetail && this.announcementDetail.kind
      if (k === 'service_alert') return 'Service alert'
      if (k === 'service_resolved') return 'Service resolved'
      return 'Announcement'
    },
    // chip + tile family classes derive from the active kind so the
    // composer's icon picker recolors purple/amber/green in lock-step
    // with the kind radio.
    composerIconChipClass() {
      if (this.announcementKind === 'service_alert') return 'nc-icon-current--alert'
      if (this.announcementKind === 'service_resolved') return 'nc-icon-current--resolved'
      return 'nc-icon-current--ann'
    },
    // icon-explorer modal: filtered list of every FA icon. case-insensitive
    // substring match against the icon class name. fast enough for ~675
    // icons without virtualization since the modal is admin-only.
    iconExplorerFiltered() {
      const q = (this.iconExplorerSearch || '').trim().toLowerCase()
      const list = this.iconExplorerAllIcons || []
      if (!q) return list
      return list.filter(ic => ic.toLowerCase().indexOf(q) >= 0)
    },
    // shared inline-style for explorer pucks (browse tiles + inspector
    // detail puck). matches the inverted-outline puck family used in
    // announcement_detail.vue / help_center.vue read-only hero — pastel
    // bg + soft border + deeper-color icon + halo tinted to the deep
    // color. the halo is what makes the puck feel elevated rather than
    // a flat chip; dropping it loses the family resemblance.
    explorerPuckStyle() {
      const halo = this._hexWithAlpha(this.iconExplorerColor, 0.16) || 'rgba(0,0,0,0.10)'
      const halo2 = this._hexWithAlpha(this.iconExplorerColor, 0.08) || 'rgba(0,0,0,0.05)'
      return {
        background: this.iconExplorerBg,
        borderColor: this.iconExplorerBorder,
        color: this.iconExplorerColor,
        boxShadow: '0 2px 5px ' + halo + ', 0 1px 2px ' + halo2,
      }
    },
    // list-size variant — flat (no halo) and lighter borders, matching
    // notifications_list.vue / navbar.vue popup pucks. inspector renders
    // both side-by-side so admins can evaluate how a glyph reads at the
    // small dense size alongside the elevated detail size.
    explorerPuckStyleList() {
      return {
        background: this.iconExplorerBg,
        borderColor: this.iconExplorerBorder,
        color: this.iconExplorerColor,
      }
    },
    readonlyBadgeClass() {
      const k = this.announcementDetail && this.announcementDetail.kind
      if (k === 'service_alert') return 'nc-preview-badge-service-alert'
      if (k === 'service_resolved') return 'nc-preview-badge-service-resolved'
      return 'nc-preview-badge-announcement'
    },
    // resolves the live icon for the read-only detail puck. prefers a
    // per-row override; falls back to the kind default.
    readonlyPuckGlyph() {
      return iconForAnnouncement(this.announcementDetail || {})
    },
    // composer's preview chip glyph. prefers the user's pick; falls back
    // to the kind default so the chip never renders empty even before
    // the user opens the picker.
    composerIcon() {
      if (isAllowedIcon(this.announcementIcon)) return this.announcementIcon
      return defaultIconForKind(this.announcementKind)
    },
    composerIconLabel() {
      const groups = this.announcementIconGroups || []
      for (let i = 0; i < groups.length; i++) {
        const match = (groups[i].icons || []).find(o => o.value === this.composerIcon)
        if (match) return match.label
      }
      return 'Default'
    },
    isComposerIconDefault() {
      return isDefaultIconForKind(this.announcementIcon, this.announcementKind)
    },
    // srcdoc for the read-only iframe — wraps the author's HTML in a
    // minimal doctype so their own CSS works naturally, and injects a
    // target="_blank" base so links open in a new tab rather than
    // trying to navigate the iframe.
    announcementDetailSrcdoc() {
      const html = (this.announcementDetail && this.announcementDetail.body_markdown) || ''
      return this._wrapAnnouncementHtml(html)
    },
    // srcdoc for the preview modal when in HTML mode — same wrapper so
    // the admin sees exactly what recipients will see.
    announcementPreviewSrcdoc() {
      if (this.announcementFormat !== 'html') return ''
      return this._wrapAnnouncementHtml(this.announcementHtml || '')
    },
    previewTenantLabel() {
      if (this.announcementTarget !== 'tenant') return ''
      const opts = (this.user && this.user.tenant_options) || []
      const match = opts.find(t => (t && (t.value || t.tenant_id)) === this.announcementTenantId)
      if (match) return match.text || match.name || match.value || 'Specific customer'
      return this.announcementTenantId ? ('Customer: ' + this.announcementTenantId) : 'Specific customer'
    },
    // the "Muggles" preset — every role option except 'admin'.
    mugglesRoleSet() {
      return (this.announcementRoleOptions || []).map(r => r.value).filter(v => v !== 'admin')
    },
    // full role set — used for the form's default state and for
    // detecting divergence from default in the Reset/Restore check.
    allRolesSet() {
      return (this.announcementRoleOptions || []).map(r => r.value)
    },
    isMugglesActive() {
      // Muggles is active when every non-admin role is selected.
      // intentionally does NOT care about the Sys Admin chip's state
      // so admins can be included or excluded independently.
      if (!this.announcementRoles) return false
      const cur = new Set(this.announcementRoles)
      return this.mugglesRoleSet.every(v => cur.has(v))
    },
    isAllRolesSelected() {
      const all = this.allRolesSet
      if (!this.announcementRoles || this.announcementRoles.length !== all.length) return false
      const cur = new Set(this.announcementRoles)
      return all.every(v => cur.has(v))
    },
    // human label for the draft's last-saved timestamp, shown in the
    // detail header next to the status badge. keep it gentle — "saved 2
    // minutes ago" beats a precise ISO string for a background action.
    // accepts ISO strings (the canonical format written by
    // notification_service._now_iso()) and epoch seconds/milliseconds (a
    // few legacy rows were written by scripts/update_announcement_draft.py
    // as int(time.time()) before the script was fixed — keep parsing
    // robust so those rows don't show "Saved Invalid date").
    announcementDraftLastSavedLabel() {
      const raw = this.announcementDraftLastSavedAt
      if (!raw) return ''
      try {
        let m
        const asNum = Number(raw)
        // numeric-looking value → epoch. values below ~1e12 are seconds
        // (any year before ~5138 fits), above are milliseconds.
        if (!isNaN(asNum) && /^\d+(\.\d+)?$/.test(String(raw))) {
          m = asNum < 1e12 ? moment.unix(asNum) : moment(asNum)
        } else {
          m = moment(raw)
        }
        if (!m || !m.isValid()) return 'Saved'
        return 'Saved ' + m.fromNow()
      } catch (_) {
        return 'Saved'
      }
    },
    // Stats table sort: stable client-side sort by the selected column.
    // numeric fields sort numerically; strings case-insensitive. the
    // synthetic 'effective_at' column resolves to published_at for
    // published rows and created_at for drafts so the column matches
    // what the notification fan-out (and therefore the notifications
    // list) reflects.
    sortedAnnouncementList() {
      const filtered = (this.announcementList || []).filter((a) => {
        if (!a) return false
        switch (this.announcementListFilter) {
          case 'drafts':
            return this.isDraft(a)
          case 'published':
            return !this.isDraft(a)
          case 'service_alerts':
            return a.kind === 'service_alert'
          case 'service_resolved':
            return a.kind === 'service_resolved'
          case 'banners_active':
            return a.kind === 'service_alert' && this.bannerIsActive(a) && !this.isDraft(a)
          case 'all':
          default:
            return true
        }
      })
      const col = this.statsSortColumn
      const dir = this.statsSortDirection === 'asc' ? 1 : -1
      const numericCols = { recipient_count: true, read_count: true, action_count: true }
      const pick = (a) => {
        if (col === 'effective_at') return a.published_at || a.created_at || ''
        if (numericCols[col]) return Number(a[col] || 0)
        const v = a[col]
        return typeof v === 'string' ? v.toLowerCase() : (v || '')
      }
      filtered.sort((a, b) => {
        const av = pick(a), bv = pick(b)
        if (av < bv) return -dir
        if (av > bv) return dir
        return 0
      })
      return filtered
    },
    // counters for the filter pills so admins see "Drafts (3)" / "Active
    // banners (1)" without scanning the table. cheap to recompute since
    // the list is bounded to a few hundred at most. driven off the
    // unfiltered list so each pill always reports its own scope.
    announcementListCounts() {
      const list = this.announcementList || []
      let drafts = 0, published = 0, sa = 0, sr = 0, banners = 0
      for (const a of list) {
        if (!a) continue
        if (this.isDraft(a)) drafts += 1
        else published += 1
        if (a.kind === 'service_alert') sa += 1
        if (a.kind === 'service_resolved') sr += 1
        if (a.kind === 'service_alert' && this.bannerIsActive(a) && !this.isDraft(a)) banners += 1
      }
      return { all: list.length, drafts, published, service_alerts: sa, service_resolved: sr, banners_active: banners }
    },
    // Assistant Insights (FI-1600): drive sub-views from query params so
    // deep links work. ?fa_view=fleet|tenant|user|session, plus
    // ?fa_tenant, ?fa_user, ?fa_session.
    faView() {
      const v = (this.$route && this.$route.query && this.$route.query.fa_view) || 'fleet'
      if (['fleet', 'tenant', 'user', 'session'].indexOf(v) === -1) return 'fleet'
      return v
    },
    faSelectedTenant() {
      return (this.$route && this.$route.query && this.$route.query.fa_tenant) || ''
    },
    faSelectedUser() {
      return (this.$route && this.$route.query && this.$route.query.fa_user) || ''
    },
    faSelectedSession() {
      return (this.$route && this.$route.query && this.$route.query.fa_session) || ''
    },
    // raw URL value: '' or 'last' map to "current month" / "last month"
    // tokens; concrete months use 'YYYY-MM'. drives the picker's selection.
    faSelectedPeriod() {
      return (this.$route && this.$route.query && this.$route.query.fa_period) || ''
    },
    // resolves the picker token to a concrete YYYY-MM the loaders pass
    // through to the api. an empty token means "this month" — we pass an
    // empty string to the api, which then defaults to the current month
    // server-side.
    faPeriod() {
      const token = this.faSelectedPeriod
      if (!token || token === 'current') return ''
      if (token === 'last') {
        const d = new Date()
        d.setUTCDate(1)
        d.setUTCMonth(d.getUTCMonth() - 1)
        return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
      }
      return token
    },
    faPeriodOptions() {
      const fmt = (d) => d.toLocaleString(undefined, { month: 'short', year: 'numeric' })
      const periodKey = (d) => d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
      const now = new Date()
      const opts = [
        { value: '', text: 'Current (' + fmt(now) + ')' },
      ]
      const last = new Date()
      last.setUTCDate(1)
      last.setUTCMonth(last.getUTCMonth() - 1)
      opts.push({ value: 'last', text: 'Last month (' + fmt(last) + ')' })
      // previous 10 months as concrete values, skipping the two already
      // surfaced as friendly tokens.
      for (let i = 2; i <= 11; i += 1) {
        const d = new Date()
        d.setUTCDate(1)
        d.setUTCMonth(d.getUTCMonth() - i)
        opts.push({ value: periodKey(d), text: fmt(d) })
      }
      return opts
    },
    enabledFeatures() {
      const featureMap = this.user && this.user.features && this.user.features[this.user.tenant]
        ? this.user.features[this.user.tenant]
        : {}
      return Object.keys(featureMap).filter(k => featureMap[k])
    },
    // admins can opt into a "view as non-admin" preview via the navbar toggle;
    // gate admin-only UI on this so the preview toggle actually affects render.
    effectiveRole() {
      const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
      if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
      return this.user && this.user.role
    },
    isAdmin() {
      return this.effectiveRole === 'admin'
    },
    canUseFleetAssistant() {
      // mirrors the FA launcher gating in App.vue — role allowed + feature enabled
      const u = this.user
      if (!u || !u.features || !u.tenant) return false
      const fa = u.features[u.tenant]
      if (!fa || fa.fleet_assistant_enabled === false) return false
      return ['admin', 'customer-admin', 'tbn-admin', 'manager', 'dispatch'].includes(u.role)
    },
    showCompanyDocsTab() {
      // admins and customer-admins always see the documents tab; others need the setting enabled
      if (this.user.role === 'admin' || this.user.role === 'customer-admin') return true
      const features = this.user && this.user.features && this.user.features[this.user.tenant]
      return features ? features.company_documents_in_help_center !== false : true
    },
    isDirectoryMode() {
      return !this.activeSearch
    },
    // true when an admin has toggled the "view as non-admin" preview in the
    // navbar. derived from the profile store + actual user.role so that the
    // toggle only takes effect for sys admins (the only role the toggle is
    // exposed to); other roles ignore the localStorage flag entirely.
    viewAsNonAdmin() {
      const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
      return !!(this.user && this.user.role === 'admin' && viewAs === 'non-admin')
    },
    // in muggle preview, hide articles that target only sys admins so the
    // sidebar matches what a real non-admin would see. an article counts as
    // "admin-only" when its audience_roles list is non-empty, has no wildcard
    // (* / all), and contains nothing outside the sys-admin role set. this
    // mirrors how the server's _help_role_matches would gate the same article
    // for a non-admin requester — but client-side, since the request is
    // already authenticated as admin and we just want a preview filter.
    visibleArticles() {
      if (!this.viewAsNonAdmin) return this.articles
      return (this.articles || []).filter(a => !this._isAdminOnlyArticle(a))
    },
    articleDirectory() {
      const TYPE_ORDER = ['faq', 'task_guide', 'reference', 'troubleshooting']
      const TYPE_LABELS = { faq: 'FAQ', task_guide: 'Task Guide', reference: 'Reference', troubleshooting: 'Troubleshooting' }
      const groups = {}
      for (const article of this.visibleArticles) {
        const type = article.article_type || 'faq'
        if (!groups[type]) groups[type] = []
        groups[type].push(article)
      }
      return TYPE_ORDER
        .filter(t => groups[t] && groups[t].length > 0)
        .map(t => ({ type: t, label: TYPE_LABELS[t] || t, articles: groups[t] }))
    },
    canManageDocs() {
      return this.user.role === 'admin' || this.user.role === 'customer-admin'
    },
    supportTicketId() {
      return this.$route.query && this.$route.query.ticket_id ? this.$route.query.ticket_id : null
    },
    allManagedDocsSelected() {
      const selectable = this.selectableManagedDocs
      return selectable.length > 0 && this.managedSelectedDocs.length === selectable.length
    },
    someManagedDocsSelected() {
      return this.managedSelectedDocs.length > 0
    },
    feedbackTenantOptions() {
      const opts = [{ value: '', text: 'All tenants' }]
      if (this.user && this.user.tenant_options) {
        for (const opt of this.user.tenant_options) {
          if (typeof opt === 'object' && opt.value != null) {
            opts.push(opt)
          } else if (typeof opt === 'string') {
            opts.push({ value: opt, text: opt })
          }
        }
      }
      return opts
    },
    feedbackReviewFields() {
      // context_preview column shows different source content per feedback
      // type (user_message / article_title / search_query). label tracks the
      // active sub-tab so admins see what the column actually contains rather
      // than a generic catchall.
      let contextLabel = 'Context Preview'
      if (this.feedbackSubTab === 'topic_suggestion') contextLabel = 'Search Term'
      else if (this.feedbackSubTab === 'help_article') contextLabel = 'Article'
      return [
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'tenant_id', label: 'Account', sortable: true },
        { key: 'user_id', label: 'User', sortable: true },
        { key: 'rating', label: 'Rating', sortable: false },
        { key: 'context_preview', label: contextLabel, sortable: false },
        { key: 'comment', label: 'Comment', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'handled_by', label: 'Handled By', sortable: false },
        { key: 'actions', label: 'Actions', sortable: false },
      ]
    },
    leaderboardFields() {
      // narrow numeric columns so the title column gets the rest of the
      // modal width. tdClass centers numeric cells — keeps parity with
      // the prior static-table layout that used inline text-align:center.
      return [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'article_type', label: 'Type', sortable: true, thStyle: { width: '80px' } },
        { key: 'view_count', label: '\u{1F441}', sortable: true, thStyle: { width: '55px', textAlign: 'center' }, tdClass: 'text-center', thClass: 'text-center', formatter: (v) => v || 0 },
        { key: 'yes_count', label: '\u{1F44D}', sortable: true, thStyle: { width: '50px', textAlign: 'center' }, tdClass: 'text-center', thClass: 'text-center', formatter: (v) => v || 0 },
        { key: 'no_count', label: '\u{1F44E}', sortable: true, thStyle: { width: '50px', textAlign: 'center' }, tdClass: 'text-center', thClass: 'text-center', formatter: (v) => v || 0 },
        { key: 'helpful_pct', label: 'Helpful %', sortable: true, thStyle: { width: '85px', textAlign: 'center' }, tdClass: 'text-center', thClass: 'text-center' },
        { key: 'total', label: 'Total', sortable: true, thStyle: { width: '60px', textAlign: 'center' }, tdClass: 'text-center', thClass: 'text-center', formatter: (v) => v || 0 },
      ]
    },
    selectableManagedDocs() {
      return (this.managedDocs || []).filter(d =>
        d.doc_id &&
        d.status !== 'uploading' &&
        d.status !== 'processing' &&
        d.status !== 'deleting'
      )
    },
  },
  watch: {
    // FI-1432: when the user starts re-composing after Reset, drop the
    // restore buffer so the button flips back to "Reset".
    announcementTitle(newVal) {
      if (this.announcementResetSnapshot && newVal && newVal.length > 0) {
        this._invalidateResetSnapshot()
      }
    },
    announcementTarget() { this._invalidateResetSnapshot() },
    announcementTenantId(newVal) {
      if (newVal) this._invalidateResetSnapshot()
    },
    // when kind flips, snap the icon to the new kind's default IF the
    // current value is the OLD kind's default (or empty). a user who
    // explicitly picked a non-default glyph keeps it across the flip —
    // the puck color changes (purple↔amber) but their chosen symbol
    // stays put.
    announcementKind(newKind, oldKind) {
      if (newKind === oldKind) return
      if (isDefaultIconForKind(this.announcementIcon, oldKind)) {
        this.announcementIcon = defaultIconForKind(newKind)
      }
    },
    // populate the all-icons list lazily on first open of the explorer.
    // once populated, stays populated for the rest of the session
    // (getAllFaIcons memoizes internally, but we cache on the instance
    // too so the v-for has a stable reference).
    showIconExplorer(open) {
      if (open && !this.iconExplorerAllIcons.length) {
        this.iconExplorerAllIcons = getAllFaIcons()
      }
    },
    // when a preset is picked, the input[type=color] gets a hex written
    // to it via iconExplorerColor — that ALSO fires this watcher. derive
    // bg/border from the hex when the user picked a custom color (i.e.
    // the new color isn't on the preset list).
    iconExplorerColor(newColor) {
      const preset = (this.iconExplorerColorOptions || []).find(o => o.color.toLowerCase() === (newColor || '').toLowerCase())
      if (preset) return  // preset button handler already set bg/border
      // custom hex — derive a soft tinted bg + a slightly darker border.
      this.iconExplorerBg = this._hexWithAlpha(newColor, 0.10) || '#f8fafc'
      this.iconExplorerBorder = this._hexWithAlpha(newColor, 0.35) || '#cbd5e1'
    },
    '$route'(to) {
      // legacy deep link support — ?request_usage=1 can still land here from old
      // bookmarks or external URLs. the modal itself now lives globally in App.vue,
      // so we just fire the root event and scrub the query param.
      if (to.query && to.query.request_usage) {
        this.$root.$emit('open-ai-usage-request-modal')
        const cleanQuery = { ...to.query }
        delete cleanQuery.request_usage
        this.$router.replace({ name: 'help_center', query: cleanQuery }).catch(() => {})
      }

      // sync activeTab from the URL on every route change so browser
      // back/forward steps over a tab boundary (e.g. Insights/Tenant ↔
      // Feedback Review via the cross-link button) actually flip the
      // visible tab. without this, the URL changed but activeTab kept
      // its stale value because switchTab() only fires on user clicks.
      // when the URL has no ?tab= (e.g. the navbar help-icon links to
      // { name: 'help_center' } with no query), fall back to the same
      // role-based default created() uses — admins land on Support,
      // non-admins on Articles — so the help-icon respects the
      // admin landing rule.
      const validTabs = ['articles', 'company', 'support', 'feedback', 'insights', 'announcements']
      // an articleId in the path means we're on a specific help article, so
      // the articles tab must win over the admin support-landing default.
      // an explicit ?tab= still beats both.
      const hasArticleParam = !!(to.params && to.params.articleId)
      const wantTab = (to.query && to.query.tab) || ((this.isAdmin && !hasArticleParam) ? 'support' : 'articles')
      if (validTabs.includes(wantTab) && wantTab !== this.activeTab) {
        this.activeTab = wantTab
        this.mobileShowDetail = false
      }

      // Assistant Insights: react to fa_* query param changes so deep
      // links and back navigation between sub-views reload the right data.
      if (this.activeTab === 'insights') {
        this.maybeLoadFaCurrentView()
      }

      // Announcements: react to ?announcement= changes so browser back/
      // forward toggles list ↔ detail. _syncAnnouncementQuery now pushes
      // a history entry per transition, so popstate has to flow back
      // into announcementView state for the page to actually flip.
      if (this.activeTab === 'announcements') {
        const want = (to.query && to.query.announcement) || null
        if (!want && this.announcementView !== 'list') {
          // back-from-detail: reset detail-mode state and reload the list
          // for fresh data (matches backToAnnouncementList()).
          this.announcementView = 'list'
          this.announcementDetailMode = null
          this.announcementDraftId = null
          this.announcementDraftLastSavedAt = null
          this.announcementFlash = null
          this.loadAnnouncementList()
        } else if (want === 'new' && (this.announcementView !== 'detail' || this.announcementDetailMode !== 'compose' || this.announcementDraftId !== null)) {
          this.openAnnouncementForNew()
        } else if (want && want !== 'new' && this.announcementDraftId !== want) {
          const a = (this.announcementList || []).find(x => x.announcement_id === want)
          if (a) this.openAnnouncementDetail(a)
        }
      }

      const id = (to.params && to.params.articleId) || null
      if (id === this.selectedDocId) return
      this.selectedDocId = id
      if (!id) {
        this.selectedArticle = null
        this.articleContent = ''
        this.feedbackSummary = null
        return
      }
      const inList = this.articles.find(a => a.doc_id === id)
      if (inList) {
        this.selectArticle(inList, false)
      } else if (this.articles.length > 0) {
        this.loadArticleById(id)
      }
    },
  },
  created() {
    // per-session dedup for view-count increments. plain instance property
    // (not in data()) so Vue doesn't proxy/track it — we only read via .has/
    // .add, never render off it. set of doc_ids that have already been
    // counted this session; re-opens are ignored so the count reflects
    // unique reader-sessions rather than every click. resets on page reload.
    this._viewedArticleIds = new Set()

    // sys admins clicking the navbar help-icon land on Support Tickets
    // — they hit the help center to triage tickets, not browse articles.
    // non-admins keep the data() default ('articles'). a query-param
    // override (?tab=...) below wins over both, so deep links still
    // open whichever tab they specify. an :articleId in the path also
    // beats the admin default, because the user is asking for a specific
    // article (e.g. refreshing on /help_center/<doc_id>) — without this,
    // refresh would silently swap them onto the Support tab.
    const hasArticleParam = !!(this.$route.params && this.$route.params.articleId)
    if (this.isAdmin && !hasArticleParam) {
      this.activeTab = 'support'
    }
    // restore tab from query param
    const validTabs = ['articles', 'company', 'support', 'feedback', 'insights', 'announcements']
    const tabParam = this.$route.query && this.$route.query.tab
    if (tabParam && validTabs.includes(tabParam)) {
      this.activeTab = tabParam
    }
    this.selectedDocId = (this.$route.params && this.$route.params.articleId) || null
    this.loadArticles()
    if (this.activeTab === 'company') {
      this.loadCompanyDocs()
    }
    // deep link to a specific feedback detail
    if (this.$route.query.fb_tenant && this.$route.query.fb_id) {
      this.activeTab = 'feedback'
      this._loadFeedbackDetailFromUrl(this.$route.query.fb_tenant, this.$route.query.fb_id)
    }
    // legacy deep link support — ?request_usage=1 still opens the (now global) modal
    if (this.$route.query.request_usage) {
      this.$root.$emit('open-ai-usage-request-modal')
      // clear the param so refreshing the page doesn't re-trigger the modal
      const cleanQuery = { ...this.$route.query }
      delete cleanQuery.request_usage
      this.$router.replace({ name: 'help_center', query: cleanQuery }).catch(() => {})
    }
    // Assistant Insights: if we landed on the insights tab via deep link,
    // kick off the load for whichever sub-view the query params describe.
    if (this.activeTab === 'insights') {
      this.$nextTick(() => this.maybeLoadFaCurrentView())
    }
    // Announcements: reloading with ?tab=announcements sets activeTab
    // directly, skipping switchTab() — which is what normally triggers
    // loadNotificationsTab. do it here so a refresh on the announcements
    // page rehydrates the list (and honors ?announcement=<id> deep links)
    // instead of sitting on an empty-state forever.
    if (this.activeTab === 'announcements') {
      this.$nextTick(() => this.loadNotificationsTab())
    }
    // mobile breakpoint — use window.innerWidth (proven reliable on iOS)
    this.isMobileView = window.innerWidth <= 768
    this._onResize = () => {
      const wasMobile = this.isMobileView
      this.isMobileView = window.innerWidth <= 768
      if (wasMobile && !this.isMobileView) this.mobileShowDetail = false
    }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    this.stopManagedDocsRefresh()
    document.removeEventListener('mousemove', this.onDividerMouseMove)
    document.removeEventListener('mouseup', this.onDividerMouseUp)
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize)
    }
  },
  methods: {
    // FI-1432 Notification Center admin tab methods.
    // list-as-landing flow: entering the tab always loads the list and
    // system stats together (stats now render inline on the list header).
    // deep-link via ?announcement=<id>|new is handled after the list is
    // ready so we can look up the row before switching to detail mode.
    async loadNotificationsTab() {
      this.notificationsTabLoaded = true
      await this.loadAnnouncementList()
      if (!this.systemStatsLoaded) {
        this.loadSystemStats()
      }
      // honor a deep-link if we landed straight on ?announcement=
      const q = this.$route && this.$route.query && this.$route.query.announcement
      if (q === 'new') {
        this.openAnnouncementForNew()
      } else if (q) {
        const a = (this.announcementList || []).find(x => x.announcement_id === q)
        if (a) this.openAnnouncementDetail(a)
      }
    },
    // draft check — a row is a draft if its status is explicitly 'draft'.
    // rows created before FI-1432's status column are 'published' by
    // default, so the absence of status also means published.
    isDraft(a) {
      return !!(a && a.status === 'draft')
    },
    // resolves the icon glyph for an admin-list row. prefers the row's
    // per-row icon override; falls back to the kind default. shared with
    // the read-only detail puck via iconForAnnouncement().
    iconForRow(a) {
      return iconForAnnouncement(a || {})
    },
    // list-row puck color tracks `kind`. mirrors the readonlyPuckClass
    // computed used on the detail page so the list and detail surfaces
    // stay visually in lockstep.
    rowPuckClass(a) {
      const k = a && a.kind
      if (k === 'service_alert') return 'nc-title-puck-service-alert'
      if (k === 'service_resolved') return 'nc-title-puck-service-resolved'
      return 'nc-title-puck-announcement'
    },
    rowPuckTitle(a) {
      const k = a && a.kind
      if (k === 'service_alert') return 'Service alert'
      if (k === 'service_resolved') return 'Service resolved'
      return 'Announcement'
    },
    // list-view helpers: switch to the detail subpage in either 'compose'
    // (for new + drafts) or 'read' (published) mode. the page uses
    // ?announcement= query params so a reload lands the user back where
    // they were. we drive form state off data rather than the URL — the
    // URL is purely for navigation/bookmarking.
    openAnnouncementForNew() {
      this.announcementView = 'detail'
      this.announcementDetailMode = 'compose'
      this.announcementDraftId = null
      this.announcementDraftLastSavedAt = null
      this.announcementFlash = null
      // reset form to defaults without touching the Reset snapshot.
      this.announcementTitle = ''
      this.announcementFormat = 'markdown'
      this.announcementKind = 'announcement'
      this.announcementIcon = ''
      this.showIconPicker = false
      this.announcementBanner = false
      this.announcementBannerExpiresAtLocal = ''
      this._bannerExpiryPresetMode = 'none'
      this.announcementEmail = false
      this.announcementHtml = ''
      this.announcementTenantId = null
      this.announcementTarget = 'global'
      this.announcementRoles = []
      this.announcementResetSnapshot = null
      try {
        if (this.$refs.announcementEditor) this.$refs.announcementEditor.invoke('setMarkdown', '')
      } catch (_) {}
      this._syncAnnouncementQuery('new')
      this.refreshAnnouncementEditor()
    },
    backToAnnouncementList() {
      this.announcementView = 'list'
      this.announcementDetailMode = null
      this.announcementDraftId = null
      this.announcementDraftLastSavedAt = null
      this.announcementFlash = null
      this._syncAnnouncementQuery(null)
      // refetch so optimistic updates (publish / draft save / delete)
      // reconcile with the authoritative list.
      this.loadAnnouncementList()
    },
    // save-as-draft: create a new draft row or patch the existing one.
    // drafts skip fan-out on the backend, so there's no user-visible
    // side effect until Publish. title is allowed empty for drafts
    // (the list renders "(untitled draft)").
    async saveAnnouncementDraft() {
      if (this.announcementDraftSaving || this.announcementPublishing) return
      const body_markdown = this._getAnnouncementBody()
      const body_format = this.announcementFormat || 'markdown'
      const title = (this.announcementTitle || '').trim()
      if (!title && !body_markdown.trim()) {
        this.announcementFlash = 'Add a title or body before saving.'
        this.announcementFlashVariant = 'warning'
        return
      }
      if (this.announcementTarget === 'tenant' && !this.announcementTenantId) {
        this.announcementFlash = 'Pick a customer or switch to All customers.'
        this.announcementFlashVariant = 'warning'
        return
      }
      this.announcementDraftSaving = true
      this.announcementFlash = null
      try {
        let row = null
        if (this.announcementDraftId) {
          // patch existing draft — server rejects edits on published rows
          const resp = await admin_update_announcement(this.user.tenant, this.announcementDraftId, {
            title,
            body_markdown,
            body_format,
            kind: this.announcementKind,
            banner: this.announcementBanner,
            banner_expires_at: this.announcementKind === 'service_alert' && this.announcementBanner
              ? this._localDatetimeToIso(this.announcementBannerExpiresAtLocal)
              : null,
            email: this.announcementEmail,
            icon: this.isComposerIconDefault ? '' : this.announcementIcon,
            target: this.announcementTarget,
            tenant_id: this.announcementTarget === 'tenant' ? this.announcementTenantId : null,
            roles: this.announcementRoles,
          })
          const data = resp && resp.value && resp.value.data || {}
          row = data.data || data
        } else {
          // create a new draft — status=draft tells the backend to skip
          // fan-out; the row comes back with a fresh announcement_id we
          // capture so subsequent saves patch instead of creating.
          const fn = this.is_tbn ? tbn_admin_create_announcement : admin_create_announcement
          const resp = await fn(this.user.tenant, {
            title,
            body_markdown,
            body_format,
            kind: this.announcementKind,
            banner: this.announcementBanner,
            banner_expires_at: this.announcementKind === 'service_alert' && this.announcementBanner
              ? this._localDatetimeToIso(this.announcementBannerExpiresAtLocal)
              : null,
            email: this.announcementEmail,
            icon: this.isComposerIconDefault ? '' : this.announcementIcon,
            target: this.announcementTarget,
            tenant_id: this.announcementTarget === 'tenant' ? this.announcementTenantId : null,
            roles: this.announcementRoles,
            status: 'draft',
          })
          const data = resp && resp.value && resp.value.data || {}
          row = data.data || data
        }
        if (row && row.announcement_id) {
          this.announcementDraftId = row.announcement_id
          this.announcementDraftLastSavedAt = new Date().toISOString()
          this._syncAnnouncementQuery(row.announcement_id)
        }
      } catch (e) {
        const status = e && (e.status || (e.response && e.response.status))
        const payload = e && (e.data || (e.response && e.response.data)) || {}
        if (status === 503 && payload && payload.error === 'announcements_table_missing') {
          this.announcementFlash = 'Announcements table is not provisioned in this environment. Nothing was saved.'
        } else if (status === 409) {
          this.announcementFlash = "Can't edit a published announcement. Retract it first if you need to change it."
        } else {
          this.announcementFlash = "Couldn't save the draft — try again."
        }
        this.announcementFlashVariant = 'danger'
      } finally {
        this.announcementDraftSaving = false
      }
    },
    // keep the URL in sync with detail-view state so reloads land back on
    // the same screen AND so the browser back button steps list ↔ detail.
    // `null` means we're on the list; a value of 'new' or an
    // announcement_id populates ?announcement=<value>. uses push so each
    // list↔detail transition gets its own history entry — the $route
    // watcher above syncs announcementView back when the user pops the
    // history. handle the vue-router NavigationDuplicated rejection
    // silently (clicking the breadcrumb for the same announcement etc).
    _syncAnnouncementQuery(value) {
      try {
        const route = this.$route
        const query = Object.assign({}, (route && route.query) || {})
        if (value) {
          if (query.announcement === value) return
          query.announcement = value
        } else {
          if (!('announcement' in query)) return
          delete query.announcement
        }
        this.$router.push({ path: (route && route.path) || this.$route.path, query }).catch(() => {})
      } catch (_) { /* no router in some harnesses */ }
    },
    // toast-ui's markdown mode is a CodeMirror instance. if CodeMirror mounts
    // inside a hidden/zero-size container it caches bad dimensions and loses
    // click/focus behavior — the editor looks alive but text boxes are inert.
    // call this after the editor host becomes visible so CodeMirror
    // re-measures. falls back to a resize event which CodeMirror also honors.
    refreshAnnouncementEditor() {
      this.$nextTick(() => {
        try {
          const inst = this.$refs.announcementEditor
          if (inst && typeof inst.invoke === 'function') {
            const cm = inst.invoke('getCodeMirror')
            if (cm && typeof cm.refresh === 'function') {
              cm.refresh()
              return
            }
          }
        } catch (_) { /* fall through to resize-event fallback */ }
        try { window.dispatchEvent(new Event('resize')) } catch (_) {}
      })
    },
    // called from the Toast editor's addImageBlobHook when an admin
    // pastes or drags an image into the composer. presigns an S3 POST,
    // uploads the blob, then hands the editor back a stable URL. on
    // failure we show a flash and skip the callback so the editor drops
    // the insert rather than embedding a broken placeholder.
    async uploadAnnouncementImageBlob(blob, callback) {
      if (!blob) return
      this.announcementImageUploading = true
      this.announcementFlash = null
      try {
        const presignBody = {
          filename: blob.name || 'pasted-image.png',
          content_type: blob.type || 'image/png',
          file_size: blob.size || 0,
        }
        const presignResp = await presign_announcement_image(this.user.tenant, presignBody)
        const presignData = presignResp && presignResp.value && presignResp.value.data || {}
        const payload = presignData.data || presignData
        const upload = payload.upload
        const image_id = payload.image_id
        if (!upload || !upload.url || !upload.fields || !image_id) {
          throw new Error('Upload URL missing from presign response')
        }
        // build multipart form with S3-mandated fields first, file last.
        const form = new FormData()
        Object.keys(upload.fields).forEach((k) => form.append(k, upload.fields[k]))
        form.append('file', blob, presignBody.filename)
        const s3Resp = await fetch(upload.url, { method: 'POST', body: form })
        if (!s3Resp.ok) {
          throw new Error('S3 upload failed with status ' + s3Resp.status)
        }
        callback(announcement_image_url(image_id), presignBody.filename)
      } catch (e) {
        console.warn('announcement image upload failed:', e)
        this.announcementFlash = "Couldn't upload that image — please try again."
        this.announcementFlashVariant = 'danger'
      } finally {
        this.announcementImageUploading = false
      }
    },
    async publishAnnouncement() {
      const title = (this.announcementTitle || '').trim()
      if (!title) { return }
      const body_markdown = this._getAnnouncementBody()
      const body_format = this.announcementFormat || 'markdown'
      if (!body_markdown || !body_markdown.trim()) {
        this.announcementFlash = 'Enter a body before publishing.'
        this.announcementFlashVariant = 'warning'
        return
      }
      if (this.announcementTarget === 'tenant' && !this.announcementTenantId) {
        this.announcementFlash = 'Pick a customer or switch to All customers.'
        this.announcementFlashVariant = 'warning'
        return
      }
      this.announcementPublishing = true
      this.announcementFlash = null
      try {
        // if we have a draft id, patch its latest field state first and
        // then flip it to published so the backend fans it out. otherwise
        // create + publish in one shot (the original path).
        let row = null
        if (this.announcementDraftId) {
          const updateResp = await admin_update_announcement(this.user.tenant, this.announcementDraftId, {
            title,
            body_markdown,
            body_format,
            kind: this.announcementKind,
            banner: this.announcementBanner,
            banner_expires_at: this.announcementKind === 'service_alert' && this.announcementBanner
              ? this._localDatetimeToIso(this.announcementBannerExpiresAtLocal)
              : null,
            email: this.announcementEmail,
            icon: this.isComposerIconDefault ? '' : this.announcementIcon,
            target: this.announcementTarget,
            tenant_id: this.announcementTarget === 'tenant' ? this.announcementTenantId : null,
            roles: this.announcementRoles,
          })
          // ignore updateResp payload — the publish call returns the
          // fanned-out row which is what the UI cares about.
          void updateResp
          const publishResp = await admin_publish_announcement(this.user.tenant, this.announcementDraftId)
          const publishData = publishResp && publishResp.value && publishResp.value.data || {}
          row = publishData.data || publishData
        } else {
          const fn = this.is_tbn ? tbn_admin_create_announcement : admin_create_announcement
          const resp = await fn(this.user.tenant, {
            title,
            body_markdown,
            body_format,
            kind: this.announcementKind,
            banner: this.announcementBanner,
            banner_expires_at: this.announcementKind === 'service_alert' && this.announcementBanner
              ? this._localDatetimeToIso(this.announcementBannerExpiresAtLocal)
              : null,
            email: this.announcementEmail,
            icon: this.isComposerIconDefault ? '' : this.announcementIcon,
            target: this.announcementTarget,
            tenant_id: this.announcementTarget === 'tenant' ? this.announcementTenantId : null,
            roles: this.announcementRoles,
          })
          const data = resp && resp.value && resp.value.data || {}
          row = data.data || data
        }
        // row falsy → backend returned null (e.g. service swallowed an
        // error we didn't map). count 0 → canonical row saved but
        // fan-out matched no users — likely a tenant-membership bug or
        // the notifications table is missing. both cases deserve a
        // clear signal instead of a cheerful success flash.
        if (!row || typeof row !== 'object') {
          this.announcementFlash = "Couldn't publish — the server returned no record. Check backend logs."
          this.announcementFlashVariant = 'danger'
          await this.loadAnnouncementList()
        } else {
          const count = Number(row.recipient_count || 0)
          if (count === 0) {
            // unusual state — keep the user on the detail page so they
            // can investigate and/or retract instead of silently popping
            // back to the list.
            this.announcementFlash = 'Announcement saved but matched 0 recipients. Verify the target tenant has users, or that the notifications table exists.'
            this.announcementFlashVariant = 'warning'
            this.announcementDraftId = row.announcement_id
            await this.loadAnnouncementList()
          } else {
            // success: announce, return to the list, let the list
            // reload surface the now-published row with fresh counts.
            this.$emit('showAlert', 'Announcement published to ' + count + ' user' + (count === 1 ? '' : 's') + '.', 'success', 4500)
            this.announcementFlash = null
            // nudge App.vue to repoll so the admin sees their own
            // per-user notification (and banner for service alerts)
            // without waiting for the next polling tick. the background
            // fan-out thread writes per-user rows in parallel; a short
            // delay gives the admin's own row time to land before we
            // refetch.
            setTimeout(() => this.$root.$emit('nc-refresh'), 1500)
            this.backToAnnouncementList()
          }
        }
      } catch (e) {
        // surface the specific "tables aren't provisioned" error from the
        // backend's 503 response if present, otherwise fall back to a
        // generic message.
        const status = e && (e.status || (e.response && e.response.status))
        const payload = e && (e.data || (e.response && e.response.data)) || {}
        if (status === 503 && payload && payload.error === 'announcements_table_missing') {
          this.announcementFlash = 'Announcements table is not provisioned in this environment. Nothing was saved.'
          this.announcementFlashVariant = 'danger'
        } else {
          this.announcementFlash = "Couldn't publish — try again."
          this.announcementFlashVariant = 'danger'
        }
      } finally {
        this.announcementPublishing = false
      }
    },
    toggleAnnouncementRole(role) {
      const i = this.announcementRoles.indexOf(role)
      if (i >= 0) this.announcementRoles.splice(i, 1)
      else this.announcementRoles.push(role)
    },
    // Muggles shortcut: a toggle over just the 10 non-admin roles.
    // active → deactivate by removing ALL non-admin roles, leaving the
    //   Sys Admin chip in whatever state the user had it.
    // inactive → ripple on the missing non-admin roles (again, without
    //   touching Sys Admin).
    toggleMugglesRoles() {
      if (this.rolesShortcutRunning) return
      if (this.isMugglesActive) {
        const mugglesSet = new Set(this.mugglesRoleSet)
        this.announcementRoles = this.announcementRoles.filter(r => !mugglesSet.has(r))
        return
      }
      this.selectMugglesRoles()
    },
    // shortcut-mode ripple — similar spirit to the FA tutorial-mode
    // animation but much quicker (45ms between chips vs the slow
    // typing/flipping effect). only animates non-admin roles that
    // aren't already in the selection; preserves whatever state the
    // other chips (including Sys Admin) are in.
    async selectMugglesRoles() {
      if (this.rolesShortcutRunning) return
      this.rolesShortcutRunning = true
      try {
        const already = new Set(this.announcementRoles)
        const targets = this.mugglesRoleSet.filter(v => !already.has(v))
        const stepMs = 45
        const pulseMs = 260
        for (const role of targets) {
          this.announcementRoles = this.announcementRoles.concat([role])
          this.pulsingRoles = this.pulsingRoles.concat([role])
          setTimeout(() => {
            this.pulsingRoles = this.pulsingRoles.filter(r => r !== role)
          }, pulseMs)
          await new Promise(r => setTimeout(r, stepMs))
        }
        await new Promise(r => setTimeout(r, pulseMs))
      } finally {
        this.pulsingRoles = []
        this.rolesShortcutRunning = false
      }
    },
    async resetAnnouncementForm() {
      // capture the current form state so the Reset button can morph into
      // a Restore button for accidental-click recovery. we only stash
      // when there's something substantive worth restoring. snapshot
      // tracks the active format + both buffers so a restore after a
      // reset never silently drops the other mode's work.
      const currentMd = this._getAnnouncementMarkdown()
      const currentHtml = this.announcementHtml || ''
      const snap = {
        title: this.announcementTitle || '',
        format: this.announcementFormat || 'markdown',
        kind: this.announcementKind || 'announcement',
        icon: this.announcementIcon || '',
        banner: !!this.announcementBanner,
        bannerExpiresAtLocal: this.announcementBannerExpiresAtLocal || '',
        email: !!this.announcementEmail,
        target: this.announcementTarget || 'global',
        tenantId: this.announcementTenantId || null,
        roles: (this.announcementRoles || []).slice(),
        bodyMarkdown: currentMd || '',
        bodyHtml: currentHtml,
      }
      const hasContent = !!(snap.title.trim() || snap.bodyMarkdown.trim() || snap.bodyHtml.trim()
                            || snap.target !== 'global' || snap.tenantId
                            || snap.kind !== 'announcement'
                            || snap.banner
                            || snap.bannerExpiresAtLocal
                            || snap.email
                            || !!snap.icon
                            || (snap.roles && snap.roles.length))

      this.announcementTitle = ''
      this.announcementFormat = 'markdown'
      this.announcementKind = 'announcement'
      this.announcementIcon = ''
      this.showIconPicker = false
      this.announcementBanner = false
      this.announcementBannerExpiresAtLocal = ''
      this._bannerExpiryPresetMode = 'none'
      this.announcementEmail = false
      this.announcementHtml = ''
      this.announcementTenantId = null
      this.announcementTarget = 'global'
      // reset to empty (default form state) — empty is interpreted
      // server-side as "everyone in the target". Muggles is a narrowing
      // preset from that starting point, not the default.
      this.announcementRoles = []
      try {
        if (this.$refs.announcementEditor) this.$refs.announcementEditor.invoke('setMarkdown', '')
      } catch (_) {}

      // wait for the reset value changes to propagate through the watchers
      // (which would otherwise invalidate the snapshot we're about to set),
      // then stash it so the button can flip to Restore.
      await this.$nextTick()
      this.announcementResetSnapshot = hasContent ? snap : null
    },
    // restore the pre-Reset form state. clears the snapshot first so the
    // watcher-driven invalidations triggered by setting fields here are
    // no-ops (snapshot is already null when they fire).
    restoreAnnouncementForm() {
      if (!this.announcementResetSnapshot) return
      const snap = this.announcementResetSnapshot
      this.announcementResetSnapshot = null
      this.announcementTitle = snap.title || ''
      this.announcementFormat = snap.format || 'markdown'
      this.announcementKind = snap.kind || 'announcement'
      this.announcementIcon = snap.icon || ''
      this.announcementBanner = !!snap.banner
      this.announcementBannerExpiresAtLocal = snap.bannerExpiresAtLocal || ''
      this._bannerExpiryPresetMode = this.announcementBannerExpiresAtLocal ? 'custom' : 'none'
      this.announcementEmail = !!snap.email
      this.announcementHtml = snap.bodyHtml || ''
      this.announcementTarget = snap.target || 'global'
      this.announcementTenantId = snap.tenantId || null
      this.announcementRoles = (snap.roles || []).slice()
      try {
        if (this.$refs.announcementEditor) {
          this.$refs.announcementEditor.invoke('setMarkdown', snap.bodyMarkdown || '')
        }
      } catch (_) {}
    },
    // re-evaluate whether the Reset snapshot is still useful. fires from
    // every input that could signal "the user has started composing
    // again": title watcher, target/tenant watchers, editor @change.
    // the check compares the CURRENT form against the post-reset
    // defaults — if anything has actually diverged from an empty-and-
    // global form, the snapshot is stale and we drop it. this avoids
    // the classic flicker bug where toast-ui's @change fires during
    // our own setMarkdown('') call and would otherwise nuke the
    // snapshot we just stashed.
    _invalidateResetSnapshot() {
      if (!this.announcementResetSnapshot) return
      let md = ''
      try { md = this._getAnnouncementMarkdown() } catch (_) {}
      const html = (this.announcementHtml || '').trim()
      // roles are "composed" if they diverge from the default (all
      // roles selected). we can't use a simple length check because
      // the default is non-empty.
      const rolesDiverged = !this.isAllRolesSelected
      const hasComposed =
        (this.announcementTitle && this.announcementTitle.trim().length > 0) ||
        (md && md.trim().length > 0) ||
        (html.length > 0) ||
        (this.announcementFormat && this.announcementFormat !== 'markdown') ||
        (this.announcementKind && this.announcementKind !== 'announcement') ||
        !this.isComposerIconDefault ||
        !!this.announcementBanner ||
        !!this.announcementBannerExpiresAtLocal ||
        !!this.announcementEmail ||
        (this.announcementTarget && this.announcementTarget !== 'global') ||
        !!this.announcementTenantId ||
        rolesDiverged
      if (hasComposed) {
        this.announcementResetSnapshot = null
      }
    },
    // user clicked an icon tile in the picker. setting the value here
    // (vs binding directly via v-model) lets us collapse the grid back
    // when the user picks the kind default — the chip is enough state
    // for "default" so the open grid is just visual noise.
    _pickIcon(value) {
      if (!isAllowedIcon(value)) return
      this.announcementIcon = value
      this._invalidateResetSnapshot()
      // collapse the picker after a pick — admins almost always pick
      // once and move on. they can re-open with the chip if they want
      // to change again.
      this.showIconPicker = false
    },
    _resetComposerIcon() {
      this.announcementIcon = defaultIconForKind(this.announcementKind)
      this._invalidateResetSnapshot()
    },
    // ICON EXPLORER actions ----------------------------------------
    // the explorer is a design tool: click an icon to INSPECT it (load
    // it into the dual-size preview panel so the admin can see how it
    // reads at both detail and list sizes before committing). actually
    // ADOPTING the icon for the announcement is a separate explicit
    // action via the "Use this icon" button — only allowlisted icons
    // expose that button; non-allowlisted icons stay inspect-only.
    _exploreIcon(name) {
      if (!name) return
      this.iconExplorerSelected = name
    },
    _adoptExplorerIcon() {
      const name = this.iconExplorerSelected
      if (!name || !isAllowedIcon(name)) return
      this.announcementIcon = name
      this._invalidateResetSnapshot()
      this.showIconExplorer = false
    },
    // BV b-modal @shown fires AFTER the modal is fully visible — at that
    // point the search input is mounted and we can autofocus it so admins
    // can start typing immediately. ref is on the b-form-input wrapper;
    // the underlying <input> lives at .$el.
    _onIconExplorerShown() {
      // BV b-modal focuses modal-content immediately on @shown to set up
      // its focus trap. defer with a short setTimeout so our focus on the
      // search input runs AFTER BV's, otherwise BV reclaims it and the
      // user sees focus on body.
      setTimeout(() => {
        try {
          const ref = this.$refs.iconExplorerSearchInput
          const el = ref && (ref.$el || ref)
          if (el && typeof el.focus === 'function') {
            el.focus()
            return
          }
          if (el) {
            const input = el.querySelector('input')
            if (input) input.focus()
          }
        } catch (_) { /* focus is a nice-to-have */ }
      }, 80)
    },
    // reset transient explorer state on close so reopening starts fresh
    // (no stale selected tile, no leftover filter). the all-icons cache
    // and chosen palette persist across opens since those are setup
    // costs, not user state.
    _onIconExplorerHidden() {
      this.iconExplorerSelected = ''
      this.iconExplorerSearch = ''
      this.iconExplorerCopied = ''
    },
    _setIconExplorerPreset(opt) {
      if (!opt) return
      this.iconExplorerColor = opt.color
      this.iconExplorerBg = opt.bg
      this.iconExplorerBorder = opt.border
    },
    _copyIconClass(name) {
      if (!name) return
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(name)
          this.iconExplorerCopied = name
          setTimeout(() => { if (this.iconExplorerCopied === name) this.iconExplorerCopied = '' }, 1200)
        }
      } catch (_) { /* clipboard blocked — silent */ }
    },
    // helper for the iconExplorerColor watcher — converts a #rrggbb hex
    // to an rgba string at the given alpha. used to derive a soft bg
    // and a stronger border from a custom color the admin types in.
    // template wrapper for the imported isAllowedIcon helper. vue 2 can
    // call methods directly from templates but not bare imports, so this
    // exposes the registry check to the explorer's tile + selected panel.
    _isAllowedIconWrap(name) {
      return isAllowedIcon(name)
    },
    // template wrappers for the custom-SVG branch. used by every icon
    // render surface to decide whether to emit `<i class="fa">` (legacy
    // FA path) or an inline `<svg>` for the brand monograms.
    _isSvgIconWrap(name) {
      return isSvgIcon(name)
    },
    _svgIconHtmlWrap(name) {
      return customSvgHtml(name)
    },
    _hexWithAlpha(hex, alpha) {
      if (!hex || typeof hex !== 'string') return ''
      const m = hex.replace('#', '').match(/^([0-9a-fA-F]{6})$/)
      if (!m) return ''
      const r = parseInt(m[1].slice(0, 2), 16)
      const g = parseInt(m[1].slice(2, 4), 16)
      const b = parseInt(m[1].slice(4, 6), 16)
      return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')'
    },
    // convert a <input type="datetime-local"> string ('YYYY-MM-DDTHH:MM',
    // interpreted as the user's local timezone) into a UTC ISO 8601 string
    // for the API. empty input → null (server interprets as 'no expiry'
    // on create, 'no change' on update). unparseable → null so a malformed
    // value never silently sends a bogus iso to the backend.
    _localDatetimeToIso(local) {
      if (!local) return null
      const d = new Date(local)
      if (Number.isNaN(d.getTime())) return null
      return d.toISOString()
    },
    // inverse of _localDatetimeToIso. takes an ISO string off a row and
    // produces a 'YYYY-MM-DDTHH:MM' value the datetime-local input can
    // round-trip without surprising timezone shifts. empty / unparseable
    // input → '' so the input renders blank instead of an "Invalid Date".
    _isoToLocalDatetime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
        'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    // ---------- banner expiry preset helpers (FI-1432) ----------
    // map a preset chip click into both the datetime-local input value
    // and the active-mode tracker. presets: number = hours from now,
    // 'custom' = open the datetime picker without setting a value,
    // 'none' = clear and collapse the picker.
    _selectBannerExpiryPreset(value) {
      if (value === 'none') {
        this.announcementBannerExpiresAtLocal = ''
        this._bannerExpiryPresetMode = 'none'
      } else if (value === 'custom') {
        // keep any existing value, just flag mode so the input stays
        // visible even when the value is empty (admin's about to type).
        this._bannerExpiryPresetMode = 'custom'
      } else if (typeof value === 'number') {
        const t = Date.now() + value * 3600 * 1000
        const d = new Date(t)
        const pad = (n) => String(n).padStart(2, '0')
        this.announcementBannerExpiresAtLocal =
          d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
          'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
        this._bannerExpiryPresetMode = String(value) + 'h'
      }
      this._invalidateResetSnapshot()
    },
    // a preset chip is "active" when the current local-datetime is within
    // ~30s of (now + preset hours). this lets a chip stay highlighted
    // immediately after click without flickering off as 'now' drifts. the
    // 'custom' chip is active when the mode flag says so. 'none' is never
    // a chip — there's no "no expiry" affordance, just the Clear button.
    _isBannerExpiryPresetActive(value) {
      if (value === 'custom') return this._bannerExpiryPresetMode === 'custom'
      if (typeof value !== 'number') return false
      if (!this.announcementBannerExpiresAtLocal) return false
      const target = Date.now() + value * 3600 * 1000
      const t = Date.parse(this.announcementBannerExpiresAtLocal)
      if (Number.isNaN(t)) return false
      return Math.abs(t - target) < 60 * 1000  // 1-minute slack
    },
    // when the admin types directly into the datetime-local input, drop
    // out of preset mode so the chips stop highlighting. fires on
    // change (input would be too noisy).
    _onBannerExpiryInputChange() {
      this._bannerExpiryPresetMode = this.announcementBannerExpiresAtLocal ? 'custom' : 'none'
      this._invalidateResetSnapshot()
    },
    // a relative label for the picked time so admins can sanity-check
    // their pick at a glance ("in 3h 45m"). past times surface as "in
    // the past" in red — the backend would refuse to honour it on
    // create, so flag it before the admin hits Save.
    _bannerExpiresRelativeLabel(local) {
      if (!local) return ''
      const t = Date.parse(local)
      if (Number.isNaN(t)) return ''
      const diffMs = t - Date.now()
      if (diffMs < 0) return 'In the past — pick a later time'
      const totalMin = Math.round(diffMs / 60000)
      if (totalMin < 60) return 'in ' + totalMin + 'm'
      const hours = Math.floor(totalMin / 60)
      const mins = totalMin % 60
      if (hours < 24) return mins ? ('in ' + hours + 'h ' + mins + 'm') : ('in ' + hours + 'h')
      const days = Math.floor(hours / 24)
      const remHours = hours % 24
      return remHours ? ('in ' + days + 'd ' + remHours + 'h') : ('in ' + days + 'd')
    },
    // toast-ui's WYSIWYG emits raw html in its markdown output for some
    // formatting (soft line breaks, italic/bold from the toolbar, paste
    // of styled text). the shared renderer now passes a whitelist of
    // formatting tags through, but we still normalize to pure markdown
    // before save so body_preview stripping (server side) and exports
    // don't have to chase a moving HTML target.
    _cleanAnnouncementMarkdown(md) {
      if (!md) return ''
      return String(md)
        .replace(/<br\s*\/?\s*>/gi, '')
        .replace(/<(?:em|i)>([\s\S]*?)<\/(?:em|i)>/gi, '*$1*')
        .replace(/<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>/gi, '**$1**')
        .replace(/<(?:s|strike|del)>([\s\S]*?)<\/(?:s|strike|del)>/gi, '~~$1~~')
        .replace(/<\/?[a-zA-Z][^>]*>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .split('\n').map(l => l.replace(/[ \t]+$/g, '')).join('\n')
        .trim()
    },
    // js port of notification_service._strip_body so the banner preview
    // in the composer matches what gets stored on publish. keep the
    // regex set in lockstep with the server.
    _stripBodyForPreview(text, format) {
      if (!text) return ''
      let cleaned = String(text)
      if (format === 'html') {
        cleaned = cleaned
          .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
          .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
          .replace(/<!--[\s\S]*?-->/g, '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
      } else {
        cleaned = cleaned
          .replace(/```[\s\S]*?```/g, '')
          .replace(/`+/g, '')
          .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
          .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
          .replace(/<[^>]+>/g, '')
          .replace(/[*_~#>]+/g, '')
      }
      return cleaned.replace(/\s+/g, ' ').trim()
    },
    _getAnnouncementMarkdown() {
      let md = ''
      try {
        if (this.$refs.announcementEditor) {
          md = this.$refs.announcementEditor.invoke('getMarkdown') || ''
        }
      } catch (_) { md = '' }
      return this._cleanAnnouncementMarkdown(md)
    },
    // unified "get the body the user is about to save" — branches on
    // announcementFormat so html mode grabs the raw textarea buffer
    // without running it through the markdown cleaner (which would
    // eat <br> tags and other legitimate HTML).
    _getAnnouncementBody() {
      if (this.announcementFormat === 'html') {
        return (this.announcementHtml || '').trim()
      }
      return this._getAnnouncementMarkdown()
    },
    // wrap an html announcement body in a minimal doctype + reset CSS so
    // the author's own styles don't need boilerplate to look right and
    // links open in a new tab instead of navigating the iframe.
    // used by both the preview modal and the read-only detail iframe.
    _wrapAnnouncementHtml(html) {
      const safe = html || ''
      return '<!doctype html>' +
        '<html lang="en"><head><meta charset="utf-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1">' +
        '<base target="_blank">' +
        '<style>' +
          'html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1d1d1f;background:#fff;line-height:1.5;}' +
          'body{padding:24px;}' +
          'img{max-width:100%;height:auto;}' +
          'a{color:#0071e3;}' +
        '</style></head><body>' + safe + '</body></html>'
    },
    // resize an iframe rendering an announcement to fit its document.
    // iframes with srcdoc don't auto-size; measure contentDocument once
    // it loads and post-load any images settle. called from @load on
    // each iframe host and re-called on window resize so responsive
    // layouts don't clip on zoom.
    _resizeAnnouncementIframe(ev) {
      const iframe = ev && ev.target
      if (!iframe || !iframe.contentDocument) return
      const doc = iframe.contentDocument
      const setHeight = () => {
        try {
          const h = Math.max(
            doc.body ? doc.body.scrollHeight : 0,
            doc.documentElement ? doc.documentElement.scrollHeight : 0,
          )
          if (h > 0) iframe.style.height = (h + 12) + 'px'
        } catch (_) {}
      }
      setHeight()
      // images + webfonts can land after the initial measurement. re-run
      // on load events and after a short settle delay so the frame doesn't
      // end up clipped to its pre-image height.
      try {
        const imgs = doc.images || []
        for (let i = 0; i < imgs.length; i++) {
          if (!imgs[i].complete) imgs[i].addEventListener('load', setHeight, { once: true })
        }
      } catch (_) {}
      setTimeout(setHeight, 200)
      this._installAnnouncementActionHandler(doc)
    },
    // announcement HTML authors can't run inline scripts (sandbox lacks
    // allow-scripts), so any CTA that wants to do more than "open a new
    // tab" uses a special href scheme the parent intercepts here:
    //   #fi-action:fa-tutorial           → open Fleet Assistant with tutorial
    //   #fi-action:fa-tutorial:<message> → same, but pre-fill the input
    //   #fi-action:nav:/some/path        → router.push (stays in same window)
    // everything else (including ordinary #/route hrefs) falls through to
    // the base target=_blank so it opens in a new tab.
    _installAnnouncementActionHandler(doc) {
      if (!doc || doc.__fiActionHandlerInstalled) return
      doc.__fiActionHandlerInstalled = true
      // Safari gotcha: <base target="_blank"> in the srcdoc wrapper makes
      // every anchor inherit target=_blank, and Safari decides to open a
      // new tab synchronously at mousedown — before our capture-phase
      // click handler can preventDefault(). result: a stray new tab
      // opens at #/fi-action:fa-tutorial:… which the hash router can't
      // match. override target on fi-action anchors so Safari sees them
      // as same-tab and our handler gets to intercept cleanly.
      try {
        const anchors = doc.querySelectorAll('a[href^="#fi-action:"]')
        for (let i = 0; i < anchors.length; i++) anchors[i].setAttribute('target', '_self')
      } catch (_) {}
      doc.addEventListener('click', (e) => {
        let el = e.target
        while (el && el !== doc.body && (!el.tagName || el.tagName.toLowerCase() !== 'a')) {
          el = el.parentNode
        }
        if (!el || !el.tagName || el.tagName.toLowerCase() !== 'a') return

        // any anchor click (fi-action OR plain <base target=_blank> link)
        // is a signal the reader wants to go somewhere. close the preview
        // modal so it isn't sitting open behind the new tab / redirected
        // page. idempotent when the modal isn't open (detail-view iframe).
        this.showAnnouncementPreviewModal = false

        const href = el.getAttribute('href') || ''
        const m = href.match(/^#fi-action:([a-z-]+)(?::(.*))?$/i)
        if (!m) return  // ordinary link: let <base target=_blank> open a tab
        e.preventDefault()
        const action = m[1]
        const arg = m[2] || ''
        if (action === 'fa-tutorial') {
          // emit AFTER a microtask so the modal's leave transition has
          // started — keeps the FA panel from rendering behind it.
          this.$nextTick(() => {
            this.$root.$emit('open-fleet-assistant-tutorial', {
              message: arg || 'Show me what you can do.'
            })
          })
        } else if (action === 'nav') {
          // defense-in-depth: announcement authoring is admin-only today,
          // but refuse anything that isn't a same-origin router path.
          // rejects protocol-relative (//evil.com), absolute urls
          // (http://…), javascript:, and bare words.
          if (typeof arg !== 'string' || !arg.startsWith('/')) return
          try { this.$router.push(arg) } catch (_) {}
        }
      }, true)
    },
    // pull the serialized markdown out of the WYSIWYG editor and show it in
    // a modal so the admin can verify/copy the source before publishing.
    // in html mode, shows the raw html textarea contents instead (same
    // modal — it's the "view source" affordance regardless of format).
    openMarkdownPreview() {
      this.markdownPreviewText = this._getAnnouncementBody()
      this.showMarkdownPreviewModal = true
    },
    // render the composed body the same way recipients will see it. for
    // markdown mode that means running it through renderMarkdownToHtml
    // (the shared help-article renderer). html mode skips markdown and
    // routes through an iframe srcdoc — see announcementPreviewSrcdoc.
    openAnnouncementPreview() {
      this.announcementPreviewTitle = (this.announcementTitle || '').trim() || 'Untitled announcement'
      const rawBody = this._getAnnouncementBody()
      if (this.announcementFormat === 'html') {
        // iframe path: the srcdoc computed handles the wrapping. we
        // clear announcementPreviewHtml so the modal's v-if branches
        // to the iframe block instead of an empty markdown pane.
        this.announcementPreviewHtml = ''
      } else {
        let html = ''
        try { html = renderMarkdownToHtml(rawBody) } catch (_) { html = rawBody }
        this.announcementPreviewHtml = html
      }
      // mirror the server's _strip_body output so the banner preview
      // matches what gets stored on publish. 200 = BODY_PREVIEW_MAX.
      this.announcementBannerPreviewText = this._stripBodyForPreview(rawBody, this.announcementFormat).slice(0, 200)
      this.showAnnouncementPreviewModal = true
    },
    // resolve a role value ('dispatch') → human label ('Dispatch') from the
    // announcementRoleOptions vocabulary. unknown roles fall back to the
    // raw value so legacy announcements still render something.
    roleLabel(roleValue) {
      const opt = (this.announcementRoleOptions || []).find(r => r.value === roleValue)
      return (opt && opt.text) || roleValue
    },
    // look up a tenant's display name from user.tenant_options. falls
    // back to the raw id if the user can't see the tenant (shouldn't
    // happen for admin announcements, but guard anyway).
    tenantName(tenantId) {
      if (!tenantId) return '—'
      const opts = (this.user && this.user.tenant_options) || []
      const match = opts.find(t => (t && (t.value || t.tenant_id)) === tenantId)
      return (match && (match.text || match.name || match.value)) || tenantId
    },
    rolesSummary(roles) {
      if (!roles || !roles.length) return 'Everyone'
      if (roles.length <= 3) return roles.map(r => this.roleLabel(r)).join(', ')
      return roles.slice(0, 2).map(r => this.roleLabel(r)).join(', ') + ' +' + (roles.length - 2)
    },
    async copyMarkdownToClipboard() {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.markdownPreviewText || '')
        } else {
          const ta = document.createElement('textarea')
          ta.value = this.markdownPreviewText || ''
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$emit('showAlert', 'Markdown copied to clipboard.', 'success', 2000)
      } catch (_) {
        this.$emit('showAlert', "Couldn't copy — select the text and copy manually.", 'warning', 3000)
      }
    },
    async loadAnnouncementList() {
      this.announcementListLoading = true
      try {
        const fn = this.is_tbn ? tbn_admin_list_announcements : admin_list_announcements
        const resp = await fn(this.user.tenant, 50)
        const data = resp && resp.value && resp.value.data || {}
        const payload = data.data || data
        this.announcementList = (payload && payload.items) || []
      } catch (e) {
        this.announcementList = []
      } finally {
        this.announcementListLoading = false
      }
    },
    async loadSystemStats() {
      this.statsLoading = true
      try {
        const fn = this.is_tbn ? tbn_admin_get_notifications_system_stats : admin_get_notifications_system_stats
        const resp = await fn(this.user.tenant, this.statsWindowDays)
        const data = resp && resp.value && resp.value.data || {}
        this.systemStats = data.data || data
        this.systemStatsLoaded = true
      } catch (e) {
        this.systemStats = null
      } finally {
        this.statsLoading = false
      }
    },
    promptDeleteAnnouncement(a) {
      if (!a || !a.announcement_id) return
      this.announcementToDelete = a
    },
    // ---------- banner expiry helpers (FI-1432) ----------
    // a banner is "active" on a row when banner=true AND it isn't yet
    // expired (no expiry, or expiry is in the future). drafts are never
    // shown as active here — they have no fan-out yet — so callers
    // should pre-filter on !isDraft(a) when controlling published-only
    // actions (the "Expire banner now" button does).
    bannerIsActive(a) {
      if (!a || a.banner !== true) return false
      const exp = a.banner_expires_at
      if (!exp) return true
      const t = Date.parse(exp)
      if (Number.isNaN(t)) return true
      return t > Date.now()
    },
    bannerStatusLabel(a) {
      if (!a || a.banner !== true) return ''
      const exp = a.banner_expires_at
      if (!exp) return 'Banner showing'
      const t = Date.parse(exp)
      if (Number.isNaN(t)) return 'Banner showing'
      if (t <= Date.now()) return 'Banner expired'
      // future expiry — surface a relative hint so admins can see at a
      // glance when the banner will auto-clear.
      const mins = Math.round((t - Date.now()) / 60000)
      if (mins < 60) return 'Banner clears in ' + mins + 'm'
      const hrs = Math.round(mins / 60)
      if (hrs < 48) return 'Banner clears in ' + hrs + 'h'
      const days = Math.round(hrs / 24)
      return 'Banner clears in ' + days + 'd'
    },
    bannerStatusClass(a) {
      return this.bannerIsActive(a) ? 'nc-banner-sub-badge-on' : 'nc-banner-sub-badge-off'
    },
    bannerStatusTitle(a) {
      if (!a) return ''
      const exp = a.banner_expires_at
      if (!exp) return 'Site-wide banner is active'
      return 'Banner expires ' + (new Date(exp)).toLocaleString()
    },
    promptExpireBanner(a) {
      if (!a || !a.announcement_id) return
      // only published service-alert rows should reach this prompt; the
      // template-level v-if on the action button already enforces this,
      // but guard again so a misclick on a stale row can't fire.
      if (this.isDraft(a) || a.banner !== true) return
      this.bannerToExpire = a
    },
    async confirmExpireBanner() {
      const a = this.bannerToExpire
      if (!a || !a.announcement_id || this.bannerExpireRunning) return
      this.bannerExpireRunning = true
      try {
        // when=null in the payload tells the backend to default to "now",
        // which is the typical "outage just ended" intent.
        await admin_expire_announcement_banner(this.user.tenant, a.announcement_id, null)
        // refetch so the row reflects the new banner_expires_at and the
        // sub-badge flips from "Banner showing" to "Banner expired".
        await this.loadAnnouncementList()
        // nudge App.vue to repoll so the banner clears for the admin's
        // own session immediately rather than waiting for the next tick.
        this.$root.$emit('nc-refresh')
        this.$emit('showAlert', 'Banner cleared. The notification stays in users\' dropdowns.', 'success', 4500)
      } catch (e) {
        this.$emit('showAlert', "Couldn't clear the banner — try again.", 'danger', 4000)
      } finally {
        this.bannerExpireRunning = false
        this.bannerToExpire = null
      }
    },
    // shared confirm handler for the list's destructive action. branches
    // on the row's current status:
    //   · draft     → hard delete via admin_delete_announcement (nothing
    //                 was fanned out, canonical row just goes away).
    //   · published → retract via admin_retract_announcement: drops per-
    //                 user rows and flips status back to draft so the
    //                 admin can edit and republish.
    async confirmDeleteAnnouncement() {
      const a = this.announcementToDelete
      if (!a || !a.announcement_id || this.announcementDeleting) return
      const wasDraft = this.isDraft(a)
      const onDetailForThisRow = this.announcementView === 'detail' && (
        this.announcementDraftId === a.announcement_id ||
        (this.announcementDetail && this.announcementDetail.announcement_id === a.announcement_id)
      )
      this.announcementDeleting = true
      try {
        if (wasDraft) {
          const fn = this.is_tbn ? tbn_admin_delete_announcement : admin_delete_announcement
          await fn(this.user.tenant, a.announcement_id)
          // hard-deleted — drop from the list optimistically.
          this.announcementList = (this.announcementList || [])
            .filter(x => x.announcement_id !== a.announcement_id)
        } else {
          const fn = this.is_tbn ? tbn_admin_retract_announcement : admin_retract_announcement
          await fn(this.user.tenant, a.announcement_id)
          // retracted — refetch so the row reappears as a draft with
          // cleared stats. cheaper than trying to mutate the row in
          // place and keep sort/filters in sync.
          await this.loadAnnouncementList()
        }
        // ping App.vue to repoll the notifications store so any
        // service-alert banner for this announcement vanishes from the
        // admin's own UI immediately — otherwise it sticks around until
        // the next 120s list-refresh tick.
        this.$root.$emit('nc-refresh')
        const msg = wasDraft
          ? 'Draft deleted.'
          : 'Announcement retracted. Moved back to Drafts — edit and republish when you\'re ready.'
        this.$emit('showAlert', msg, 'success', 4500)
        // if the acted-on row is the one currently showing in the
        // detail view, bounce back to the list. drafts are gone;
        // published rows have become drafts (editing the retracted
        // draft requires it to load fresh from the list anyway).
        if (onDetailForThisRow) this.backToAnnouncementList()
      } catch (e) {
        const verb = wasDraft ? 'delete' : 'retract'
        this.$emit('showAlert', "Couldn't " + verb + ' the announcement — try again.', 'danger', 4000)
      } finally {
        this.announcementDeleting = false
        this.announcementToDelete = null
      }
    },
    // click a row in the list → open the detail subpage. drafts get the
    // compose form (rehydrated from the canonical row); published rows
    // get the read-only view + per-announcement stats. the modal is gone
    // in favor of this full-page flow (support_ticket_details pattern).
    async openAnnouncementDetail(a) {
      if (!a || !a.announcement_id) return
      this.announcementFlash = null
      this.announcementView = 'detail'
      this._syncAnnouncementQuery(a.announcement_id)
      if (this.isDraft(a)) {
        await this._loadAnnouncementForEdit(a)
      } else {
        await this._loadAnnouncementForRead(a)
      }
    },
    // rehydrate the compose form from a draft row. title/target/tenant/
    // roles come straight off the row; the body is pulled from the
    // backend via admin_get_announcement since the list endpoint strips
    // body_markdown to keep the payload small.
    async _loadAnnouncementForEdit(a) {
      this.announcementDetailMode = 'compose'
      this.announcementDraftId = a.announcement_id
      this.announcementDraftLastSavedAt = a.updated_at || a.created_at || null
      this.announcementTitle = a.title || ''
      this.announcementTarget = a.target || 'global'
      this.announcementTenantId = a.tenant_id || null
      this.announcementKind = a.kind || 'announcement'
      this.announcementIcon = isAllowedIcon(a.icon) ? a.icon : ''
      this.announcementBanner = !!a.banner
      this.announcementBannerExpiresAtLocal = this._isoToLocalDatetime(a.banner_expires_at)
      this._bannerExpiryPresetMode = this.announcementBannerExpiresAtLocal ? 'custom' : 'none'
      this.announcementEmail = !!a.email
      this.announcementRoles = (a.roles && a.roles.length) ? a.roles.slice() : this.allRolesSet.slice()
      this.announcementResetSnapshot = null
      // fetch the canonical row to get the body (list endpoint omits it)
      // and the body_format (legacy rows render as markdown).
      let body = ''
      let fmt = 'markdown'
      try {
        const resp = await admin_get_announcement(this.user.tenant, a.announcement_id)
        const data = resp && resp.value && resp.value.data || {}
        const row = data.data || data
        body = (row && row.body_markdown) || ''
        fmt = (row && row.body_format) || a.body_format || 'markdown'
        if (row && row.kind) this.announcementKind = row.kind
        if (row && Object.prototype.hasOwnProperty.call(row, 'icon')) {
          this.announcementIcon = isAllowedIcon(row.icon) ? row.icon : ''
        }
        if (row) this.announcementBanner = !!row.banner
        if (row) this.announcementEmail = !!row.email
        if (row && Object.prototype.hasOwnProperty.call(row, 'banner_expires_at')) {
          this.announcementBannerExpiresAtLocal = this._isoToLocalDatetime(row.banner_expires_at)
          this._bannerExpiryPresetMode = this.announcementBannerExpiresAtLocal ? 'custom' : 'none'
        }
      } catch (_) { /* editor stays empty; user can re-type */ }
      this.announcementFormat = (fmt === 'html') ? 'html' : 'markdown'
      if (this.announcementFormat === 'html') {
        // html mode: drop the raw body into the textarea buffer. clear
        // the toast editor so switching back to markdown mode doesn't
        // surface stale content from a prior session.
        this.announcementHtml = body
        try {
          if (this.$refs.announcementEditor) this.$refs.announcementEditor.invoke('setMarkdown', '')
        } catch (_) {}
      } else {
        this.announcementHtml = ''
        this.refreshAnnouncementEditor()
        this.$nextTick(() => {
          try {
            if (this.$refs.announcementEditor) this.$refs.announcementEditor.invoke('setMarkdown', body)
          } catch (_) {}
        })
      }
    },
    // load a published announcement for the read-only view: fetch the
    // body + per-announcement stats (recipient/read/action counts).
    async _loadAnnouncementForRead(a) {
      this.announcementDetailMode = 'read'
      this.announcementDraftId = null
      this.announcementDraftLastSavedAt = null
      this.announcementDetailLoading = true
      this.announcementDetail = Object.assign({}, a)
      try {
        const statsFn = this.is_tbn ? tbn_admin_get_announcement_stats : admin_get_announcement_stats
        const [statsResp, bodyResp] = await Promise.all([
          statsFn(this.user.tenant, a.announcement_id),
          admin_get_announcement(this.user.tenant, a.announcement_id).catch(() => null),
        ])
        const statsData = statsResp && statsResp.value && statsResp.value.data || {}
        const bodyData = bodyResp && bodyResp.value && bodyResp.value.data || {}
        const statsRow = statsData.data || statsData
        const bodyRow = bodyData.data || bodyData
        this.announcementDetail = Object.assign({}, a, statsRow || {}, bodyRow && bodyRow.body_markdown ? { body_markdown: bodyRow.body_markdown } : {})
      } catch (e) { /* keep what we have */ }
      finally {
        this.announcementDetailLoading = false
      }
    },
    formatAnnouncementDate(iso) {
      if (!iso) return ''
      try { return moment(iso).format('MM-DD-YYYY HH:mm') } catch (_) { return iso }
    },
    // longer-form date for the read-only detail hero — matches the
    // format used on the user-facing /announcements/:id page so admin
    // and reader views read identically.
    formatAnnouncementDateLong(iso) {
      if (!iso) return ''
      try { return moment(iso).format('MMMM D, YYYY [at] h:mm A') } catch (_) { return iso }
    },
    // compact number formatting for headline stats (1.6k vs 1635). under
    // 1000 stay literal so small audiences still feel concrete; over
    // 1000 round to one decimal to keep the headline scannable.
    formatStatNum(n) {
      const v = Number(n) || 0
      if (v < 1000) return String(v)
      if (v < 10000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return Math.round(v / 1000) + 'k'
    },
    // ---------- read-only detail banner-panel helpers ----------
    // banner panel state machine for service_alerts. four states:
    //   1. banner=false → "no banner" (advisory, panel still useful so
    //      admin understands they could enable one on a future row).
    //   2. banner=true, no expiry → active indefinitely.
    //   3. banner=true, future expiry → active, scheduled to clear.
    //   4. banner=true, past expiry → expired (soft-cleared).
    bannerPanelClass(a) {
      if (!a || a.banner !== true) return 'nc-readonly-banner-panel--off'
      return this.bannerIsActive(a)
        ? 'nc-readonly-banner-panel--on'
        : 'nc-readonly-banner-panel--off'
    },
    bannerPanelIcon(a) {
      if (!a || a.banner !== true) return 'fa-eye-slash'
      return this.bannerIsActive(a) ? 'fa-bullhorn' : 'fa-check-circle'
    },
    bannerPanelStatus(a) {
      if (!a || a.banner !== true) return 'No banner on this alert'
      const exp = a.banner_expires_at
      if (!exp) return 'Banner is showing site-wide'
      const t = Date.parse(exp)
      if (Number.isNaN(t)) return 'Banner is showing site-wide'
      if (t <= Date.now()) return 'Banner has expired'
      return 'Banner is showing — auto-clears soon'
    },
    // dedicated time line for the panel — promotes the absolute clear
    // time to its own row so it reads as data rather than prose. returns
    // empty string when no expiry exists (no banner / no time set), in
    // which case the caller hides the row.
    bannerPanelTimeLabel(a) {
      if (!a || a.banner !== true) return ''
      const exp = a.banner_expires_at
      if (!exp) return ''
      const t = Date.parse(exp)
      if (Number.isNaN(t)) return ''
      const abs = (new Date(exp)).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
      if (t <= Date.now()) {
        // past — show how long ago it cleared.
        const minsAgo = Math.round((Date.now() - t) / 60000)
        if (minsAgo < 60) return 'Cleared at ' + abs + ' · ' + minsAgo + 'm ago'
        const hrsAgo = Math.round(minsAgo / 60)
        if (hrsAgo < 48) return 'Cleared at ' + abs + ' · ' + hrsAgo + 'h ago'
        const daysAgo = Math.round(hrsAgo / 24)
        return 'Cleared at ' + abs + ' · ' + daysAgo + 'd ago'
      }
      // future — show countdown alongside the absolute time.
      const totalMin = Math.round((t - Date.now()) / 60000)
      let rel = ''
      if (totalMin < 60) rel = 'in ' + totalMin + 'm'
      else {
        const hrs = Math.floor(totalMin / 60)
        const mins = totalMin % 60
        if (hrs < 24) rel = mins ? ('in ' + hrs + 'h ' + mins + 'm') : ('in ' + hrs + 'h')
        else {
          const days = Math.floor(hrs / 24)
          const remH = hrs % 24
          rel = remH ? ('in ' + days + 'd ' + remH + 'h') : ('in ' + days + 'd')
        }
      }
      return 'Clears ' + abs + ' · ' + rel
    },
    bannerPanelHint(a) {
      if (!a || a.banner !== true) {
        return 'This service alert went out as a regular notification — no persistent banner.'
      }
      if (!this.bannerIsActive(a)) {
        return 'The in-app notification is still in users\' dropdowns; only the persistent banner has been cleared.'
      }
      if (a.banner_expires_at) {
        return 'You can also clear it now if the underlying issue is resolved.'
      }
      return 'Visible on every page until each user dismisses it. Clear it for everyone once the underlying issue is resolved.'
    },
    // click a sortable header: first click sets the column (desc for
    // dates+numbers so most-recent / highest show up on top, asc for
    // text so A comes first); subsequent clicks flip direction.
    setStatsSort(col) {
      if (this.statsSortColumn === col) {
        this.statsSortDirection = this.statsSortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.statsSortColumn = col
        this.statsSortDirection = (col === 'title' || col === 'created_by') ? 'asc' : 'desc'
      }
    },
    sortIconFor(col) {
      if (this.statsSortColumn !== col) return 'fa-sort nc-th-sort-idle'
      return this.statsSortDirection === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'
    },
    // format the author string. if it's an email, strip the domain for
    // a tighter column; otherwise show as-is.
    authorLabel(createdBy) {
      if (!createdBy) return '—'
      const at = createdBy.indexOf('@')
      if (at > 0) return createdBy.slice(0, at)
      return createdBy
    },
    // first character of the author's local-part for the avatar dot in
    // the list view. uppercase to match the navbar profile dot style.
    authorInitial(createdBy) {
      if (!createdBy) return '?'
      const at = createdBy.indexOf('@')
      const head = at > 0 ? createdBy.slice(0, at) : createdBy
      const ch = (head || '').trim().charAt(0)
      return ch ? ch.toUpperCase() : '?'
    },
    // "n / total (pct%)" — shown in the Stats per-announcement table.
    // graceful when total is 0 or counts missing from older rows.
    formatCount(n, total) {
      const count = Number(n || 0)
      const recipients = Number(total || 0)
      if (!recipients) return count ? String(count) : '—'
      const pct = Math.round(100 * count / recipients)
      return count + ' / ' + recipients + ' (' + pct + '%)'
    },
    async loadArticles() {
      this.loadingArticles = true
      this.activeSearch = (this.searchText || '').trim()
      this.topicSuggestionComment = ''
      this.topicSuggestionSubmitting = false
      this.topicSuggestionSubmitted = false
      try {
        const isSearching = !!(this.searchText || '').trim()
        const params = {
          route_name: isSearching ? (this.$route && this.$route.name ? this.$route.name : '') : '',
          query: this.searchText || '',
          enabled_features: this.enabledFeatures,
          limit: 100,
        }
        const fn = this.is_tbn ? tbn_list_help_articles : list_help_articles
        const { value: { data = {} } } = await fn(this.user.tenant, params)
        const payload = data.data || data
        this.articles = payload.articles || []
        if (this.selectedDocId) {
          // explicit article in URL — load it
          const inList = this.articles.find(a => a.doc_id === this.selectedDocId)
          if (inList) {
            await this.selectArticle(inList, false)
          } else {
            await this.loadArticleById(this.selectedDocId)
          }
        } else if (isSearching && this.articles.length === 1) {
          // single search result — auto-display it
          await this.selectArticle(this.articles[0], false)
        } else {
          // no article selected — show landing page
          this.selectedArticle = null
          this.selectedDocId = null
          this.articleContent = ''
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load help articles', 'danger', 5000)
      } finally {
        this.loadingArticles = false
      }
    },
    mobileBack() {
      this.mobileShowDetail = false
    },
    async selectArticle(article, fromUserTap) {
      if (!article || !article.doc_id) return
      this.selectedArticle = article
      this.selectedDocId = article.doc_id
      if (fromUserTap !== false) this.mobileShowDetail = true
      this.$router.replace({ name: 'help_center', params: { articleId: article.doc_id }, query: this.$route.query }).catch(() => {})
      this.feedbackValue = null
      this.feedbackComment = ''
      this.feedbackCommentSubmitted = false
      this.feedbackSummary = null
      this.feedbackDetailExpanded = false
      // the new article starts unscrolled, so show the full header.
      this.isHeaderCollapsed = false
      this.loadingArticle = true
      // captured BEFORE bumpArticleView so we can show an optimistic +1 if
      // this is the first open in the current session. otherwise the UI
      // would display a count one-behind until the user refreshes.
      const wasFirstViewThisSession = !(this._viewedArticleIds && this._viewedArticleIds.has(article.doc_id))
      try {
        const fn = this.is_tbn ? tbn_get_help_article : get_help_article
        const { value: { data = {} } } = await fn(this.user.tenant, article.doc_id)
        const payload = data.data || data
        this.articleContent = payload.content || ''
        // optimistic view count for the detail header: pre-bump count + 1
        // if this session hasn't already viewed it.
        const displayViewCount = (payload.view_count || 0) + (wasFirstViewThisSession ? 1 : 0)
        if (payload.title || payload.summary != null || payload.view_count != null) {
          this.selectedArticle = {
            ...article,
            title: payload.title || article.title,
            summary: payload.summary != null ? payload.summary : article.summary,
            view_count: displayViewCount,
          }
        }
        if (payload.user_feedback) {
          this.feedbackValue = payload.user_feedback.helpful
          this.feedbackCommentSubmitted = !!(payload.user_feedback.comment && payload.user_feedback.comment.trim())
        }
      } catch (e) {
        this.articleContent = ''
        this.$emit('showAlert', 'Unable to load help article content', 'danger', 5000)
      } finally {
        this.loadingArticle = false
      }
      // bump the view counter — once per article per session. fire-and-forget
      // so a slow/failed counter call never blocks or delays article rendering.
      this.bumpArticleView(article.doc_id)
      if (this.isAdmin && article.doc_id) {
        this.loadFeedback(article.doc_id)
      }
    },
    // scroll handler for the article body — toggles isHeaderCollapsed once
    // the user has scrolled past a small threshold (40px, roughly one line
    // of body text). the comparison gate prevents unnecessary reactive
    // updates on every scroll pixel: we only write state when the boolean
    // actually flips. transitions handle the visual smoothness.
    onArticleScroll(event) {
      const scrollTop = event.target.scrollTop
      const shouldCollapse = scrollTop > 40
      if (shouldCollapse !== this.isHeaderCollapsed) {
        this.isHeaderCollapsed = shouldCollapse
      }
    },
    // per-session dedup + async fire-and-forget increment. the backend uses
    // DynamoDB ADD for atomic increment so concurrent calls are safe; this
    // just avoids the same reader spamming the counter by re-opening the
    // same article. we don't await or check the result — the UI doesn't
    // render off view_count in real time, so a dropped increment is harmless.
    //
    // initek staff (user.role === 'admin') are excluded so their browsing
    // / triage activity doesn't drown out real customer reads in the admin
    // leaderboard. key off user.role and NOT effectiveRole on purpose — an
    // admin previewing-as-muggle is still an admin, and inflating muggle-
    // side metrics from a staff preview would defeat the point of the
    // counter. the backend re-enforces this gate as a safety net.
    bumpArticleView(docId) {
      if (!docId) return
      if (this.user && this.user.role === 'admin') return
      const set = this._viewedArticleIds
      if (!set || set.has(docId)) return
      set.add(docId)
      const fn = this.is_tbn ? tbn_record_help_article_view : record_help_article_view
      try {
        fn(this.user.tenant, docId).catch(() => {})
      } catch (_) { /* ignore — view count is best-effort */ }
    },
    async loadArticleById(docId) {
      if (!docId) return
      this.loadingArticle = true
      this.selectedArticle = { doc_id: docId, title: 'Article', summary: '' }
      this.selectedDocId = docId
      this.feedbackValue = null
      this.feedbackComment = ''
      this.feedbackCommentSubmitted = false
      this.feedbackSummary = null
      this.isHeaderCollapsed = false
      const wasFirstViewThisSession = !(this._viewedArticleIds && this._viewedArticleIds.has(docId))
      try {
        const fn = this.is_tbn ? tbn_get_help_article : get_help_article
        const { value: { data = {} } } = await fn(this.user.tenant, docId)
        const payload = data.data || data
        this.articleContent = payload.content || ''
        if (payload.title) this.selectedArticle = { ...this.selectedArticle, title: payload.title }
        if (payload.summary != null) this.selectedArticle = { ...this.selectedArticle, summary: payload.summary || '' }
        this.selectedArticle = {
          ...this.selectedArticle,
          view_count: (payload.view_count || 0) + (wasFirstViewThisSession ? 1 : 0),
        }
        if (payload.user_feedback) {
          this.feedbackValue = payload.user_feedback.helpful
          this.feedbackCommentSubmitted = !!(payload.user_feedback.comment && payload.user_feedback.comment.trim())
        }
        this.$router.replace({ name: 'help_center', params: { articleId: docId }, query: this.$route.query }).catch(() => {})
      } catch (e) {
        this.articleContent = ''
        this.selectedArticle = null
        this.selectedDocId = null
        this.$emit('showAlert', 'Help article not found', 'warning', 4000)
        this.$router.replace({ name: 'help_center', query: this.$route.query }).catch(() => {})
      } finally {
        this.loadingArticle = false
      }
      // also bump on direct-link loads so a shared article URL counts as a
      // view. dedup Set handles the case where loadArticleById then
      // selectArticle both fire for the same doc_id.
      this.bumpArticleView(docId)
      if (this.isAdmin && docId) {
        this.loadFeedback(docId)
      }
    },
    async loadFeedback(docId) {
      try {
        const fn = this.is_tbn ? tbn_get_help_feedback : get_help_feedback
        const { value: { data = {} } } = await fn(this.user.tenant, docId)
        this.feedbackSummary = data.data || data
      } catch (e) {
        this.feedbackSummary = null
      }
    },
    async submitFeedback(helpful, comment) {
      if (!this.selectedDocId) return
      const commentText = typeof comment === 'string' ? comment.trim() : ''
      this.feedbackSubmitting = true
      try {
        const fn = this.is_tbn ? tbn_submit_help_feedback : submit_help_feedback
        const body = {
          doc_id: this.selectedDocId,
          helpful: !!helpful,
          route_name: this.$route && this.$route.name ? this.$route.name : '',
        }
        if (commentText) body.comment = commentText
        const { value: { data = {} } } = await fn(this.user.tenant, body)
        this.feedbackValue = helpful
        if (typeof comment === 'string') {
          this.feedbackComment = ''
          this.feedbackCommentSubmitted = true
        }
        const payload = data.data || data
        if (payload && payload.recorded) {
          this.feedbackSummary = payload
        } else if (this.isAdmin && this.selectedDocId) {
          this.loadFeedback(this.selectedDocId)
        }
      } catch (e) {
        this.$emit('showAlert', 'Could not submit feedback', 'warning', 4000)
        // focus comment box after a "no" vote to encourage detail
        if (!helpful && typeof comment !== 'string') {
          this.$nextTick(() => {
            const el = this.$refs.feedbackCommentInput
            if (el) (el.$el || el).focus()
          })
        }
      } finally {
        this.feedbackSubmitting = false
      }
    },
    submitComment() {
      this.submitFeedback(this.feedbackValue, this.feedbackComment)
    },
    askFleetAssistant() {
      // tutorial-mode open: launcher flashes, pauses, opens the panel, flashes
      // the input, and animates the message typing out. lets the user see the
      // path they can take themselves next time.
      const q = (this.activeSearch || '').trim()
      const msg = q
        ? `Can you help me find information on "${q}"?`
        : 'Can you help me find something?'
      this.$root.$emit('open-fleet-assistant-tutorial', { message: msg })
    },
    async submitTopicSuggestion() {
      if (this.topicSuggestionSubmitting) return
      this.topicSuggestionSubmitting = true
      try {
        const fn = this.is_tbn ? tbn_submit_help_feedback : submit_help_feedback
        await fn(this.user.tenant, {
          doc_id: '__topic_suggestion__',
          helpful: false,
          route_name: this.$route && this.$route.name ? this.$route.name : '',
          // send search query as its own field so the admin modal doesn't
          // duplicate it inside the comment section. comment is now just
          // whatever the user typed.
          search_query: this.activeSearch || '',
          comment: this.topicSuggestionComment || '',
        })
        this.topicSuggestionSubmitted = true
      } catch (e) {
        this.$emit('showAlert', 'Unable to submit suggestion', 'danger', 4000)
      } finally {
        this.topicSuggestionSubmitting = false
      }
    },
    switchTab(tab) {
      // special case: re-clicking the Support Tickets tab while a ticket
      // detail is open should drop the caller back to the list. without
      // this, the early-return below kept the ticket_id query param in
      // place and the user saw no navigation happen.
      if (this.activeTab === tab && tab === 'support' && this.supportTicketId) {
        const q = { ...this.$route.query, tab }
        delete q.ticket_id
        delete q.tenant_id
        delete q.id
        this.$router.replace({ query: q }).catch(() => {})
        return
      }
      // same affordance for the Announcements tab: re-clicking while the
      // detail subpage is open should bounce back to the list.
      if (this.activeTab === tab && tab === 'announcements' && this.announcementView === 'detail') {
        this.backToAnnouncementList()
        return
      }
      if (this.activeTab === tab) return
      this.activeTab = tab
      this.mobileShowDetail = false
      // persist tab in url. when switching INTO support, drop any lingering
      // ticket_id/tenant_id so you always land on the list — detail view
      // re-opens via deep links or from clicking a ticket row.
      const query = { ...this.$route.query, tab }
      if (tab === 'support') {
        delete query.ticket_id
        delete query.tenant_id
        delete query.id
      }
      // leaving the announcements tab drops ?announcement= so we don't
      // come back to a stale deep-link when the tab is next opened.
      if (tab !== 'announcements') {
        delete query.announcement
      }
      // leaving the insights tab drops the fa_* drill-down params so the
      // destination tab's URL doesn't carry over insights state. without
      // this, the cross-link "Feedback for this tenant" was producing
      // `?tab=feedback&fa_view=tenant&fa_tenant=X` URLs and back/forward
      // got confused (especially on a re-entry to Insights, which would
      // immediately re-derive the Tenant view from the lingering params).
      if (tab !== 'insights') {
        delete query.fa_view
        delete query.fa_tenant
        delete query.fa_user
        delete query.fa_session
        delete query.fa_archived
        delete query.fa_period
      }
      // leaving the feedback tab drops the deep-link fb_* params for the
      // same reason — keeps tab boundaries clean.
      if (tab !== 'feedback') {
        delete query.fb_tenant
        delete query.fb_id
      }
      if (tab === 'articles') delete query.tab // articles is default, keep url clean
      this.$router.replace({ query }).catch(() => {})
      if (tab === 'company' && this.companyDocs.length === 0 && !this.loadingCompanyDocs) {
        this.loadCompanyDocs()
      }
      if (tab === 'feedback' && !this.feedbackReviewLoaded) {
        this.loadFeedbackReview()
      }
      if (tab === 'insights') {
        // on first entry, kick off fleet load. drill-down views re-load
        // whenever their ?fa_* query params change (see $route watcher).
        this.maybeLoadFaCurrentView()
      }
      if (tab === 'announcements' && !this.notificationsTabLoaded) {
        this.loadNotificationsTab()
      }
    },
    async loadCompanyDocs() {
      this.loadingCompanyDocs = true
      try {
        const fn = this.is_tbn ? tbn_list_help_company_documents : list_help_company_documents
        const { value: { data = {} } } = await fn(this.user.tenant)
        const payload = data.data || data
        this.companyDocs = payload.documents || []
        if (this.companyDocs.length > 0 && !this.selectedCompanyDocId) {
          await this.selectCompanyDoc(this.companyDocs[0], false)
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load company documents', 'danger', 5000)
      } finally {
        this.loadingCompanyDocs = false
      }
    },
    async selectCompanyDoc(doc, fromUserTap) {
      if (!doc || !doc.doc_id) return
      this.selectedCompanyDoc = doc
      this.selectedCompanyDocId = doc.doc_id
      if (fromUserTap !== false) this.mobileShowDetail = true
      this.loadingCompanyDoc = true
      try {
        const fn = this.is_tbn ? tbn_get_help_company_document : get_help_company_document
        const { value: { data = {} } } = await fn(this.user.tenant, doc.doc_id)
        const payload = data.data || data
        this.companyDocContent = payload.content || ''
      } catch (e) {
        this.companyDocContent = ''
        this.$emit('showAlert', 'Unable to load document content', 'danger', 5000)
      } finally {
        this.loadingCompanyDoc = false
      }
    },
    formatUploadDate(value) {
      if (!value) return ''
      try {
        // handle epoch seconds (number) or ISO/date strings
        const d = typeof value === 'number' ? new Date(value * 1000) : new Date(value)
        if (isNaN(d.getTime())) return ''
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      } catch (e) {
        return ''
      }
    },
    clearSearch() {
      this.searchText = ''
      this.activeSearch = ''
      this.loadArticles()
    },
    formatFeedbackDate(epochSeconds) {
      if (!epochSeconds) return '-'
      try {
        const epoch = Number(epochSeconds)
        if (isNaN(epoch)) return '-'
        return new Date(epoch * 1000).toLocaleString(undefined, {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        })
      } catch (e) {
        return '-'
      }
    },
    // compact variant matching the support ticket list ("4/16 6:39p"). used in
    // the review table where column space is tight; tooltip keeps the long form.
    formatFeedbackDateCompact(epochSeconds) {
      if (!epochSeconds) return '-'
      const epoch = Number(epochSeconds)
      if (isNaN(epoch)) return '-'
      const d = new Date(epoch * 1000)
      const m = d.getMonth() + 1
      const day = d.getDate()
      let h = d.getHours()
      const min = d.getMinutes()
      const ampm = h >= 12 ? 'p' : 'a'
      h = h % 12 || 12
      return `${m}/${day} ${h}:${String(min).padStart(2, '0')}${ampm}`
    },
    // look up the tenant_options entry and return it if is_vip is true. mirrors
    // the support ticket list so the feedback review can reuse the same VIP
    // signal (gold left-edge bar via stl-row-vip). gated on effectiveRole so
    // non-admin users (and Muggle preview) never see VIP signaling — matches
    // the FI-1647 "always effectiveRole, never user.role" rule.
    getTenantVip(tenantId) {
      if (this.effectiveRole !== 'admin') return null
      if (!tenantId || !this.user || !this.user.tenant_options) return null
      const opt = this.user.tenant_options.find(o => o.value === tenantId)
      return (opt && opt.is_vip) ? opt : null
    },
    // row-level class for b-table: tag VIP rows so the left-edge bar renders.
    feedbackRowClass(item) {
      if (!item || !this.getTenantVip(item.tenant_id)) return ''
      return 'stl-row-vip'
    },
    handleSearch() {
      if (this.activeTab === 'company') {
        this.loadCompanyDocs()
      } else {
        this.loadArticles()
      }
    },
    renderContent(content) {
      // strip leading H1 that duplicates the sidebar article title
      let cleaned = content
      if (this.selectedArticle && this.selectedArticle.title) {
        const titlePattern = new RegExp('^#\\s+' + this.selectedArticle.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*\\n', 'i')
        cleaned = cleaned.replace(titlePattern, '')
      }
      return renderMarkdownToHtml(cleaned)
    },
    // markdown_renderer emits <a data-help-article> and <a data-spa-nav>
    // with raw paths (e.g. /help_center/<id>, /fleet_map). without
    // interception the browser does a full navigation, which on
    // dev/prod hits the s3 origin directly because cloudfront only
    // falls back to index.html at the root — hence access-denied on
    // /help_center/<doc_id>. route them through vue-router instead so
    // the SPA stays mounted and hash-mode is handled correctly.
    handleRenderedLinkClick(event) {
      var anchor = event.target && event.target.closest && event.target.closest('a[data-spa-nav], a[data-help-article]')
      if (!anchor) return
      event.preventDefault()
      var href = anchor.getAttribute('href')
      if (href && this.$router) {
        this.$router.push(href).catch(function () {})
      }
    },
    // shared classifier for the muggle-preview filter and any future call
    // site that needs to know whether an article is staff-only. an article
    // is admin-only when its audience_roles list is restrictive (not empty
    // and no * / all wildcard) and contains nothing outside the sys-admin
    // role set. articles with no audience_roles or a wildcard are visible
    // to everyone and are NOT admin-only.
    _isAdminOnlyArticle(article) {
      const roles = (article && article.audience_roles ? article.audience_roles : [])
        .map(r => String(r || '').trim().toLowerCase())
        .filter(r => r.length > 0)
      if (roles.length === 0) return false
      if (roles.includes('*') || roles.includes('all')) return false
      return roles.every(r => r === 'admin')
    },
    focusSearch() {
      const input = this.$refs.searchInput
      if (!input) return
      const el = input.$el || input
      el.focus()
      this.highlightElement(el)
    },
    highlightElement(el) {
      el.classList.add('help-highlight-flash')
      setTimeout(() => el.classList.remove('help-highlight-flash'), 1200)
    },
    async downloadCompanyDoc() {
      if (!this.selectedCompanyDocId || this.downloadingDoc) return
      this.downloadingDoc = true
      try {
        const fn = this.is_tbn ? tbn_download_help_company_document : download_help_company_document
        const { value: { data = {} } } = await fn(this.user.tenant, this.selectedCompanyDocId)
        const payload = data.data || data
        if (payload.url) {
          const a = document.createElement('a')
          a.href = payload.url
          a.download = payload.filename || 'document'
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        } else {
          this.$emit('showAlert', 'Download not available for this document', 'warning', 4000)
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to download document', 'danger', 5000)
      } finally {
        this.downloadingDoc = false
      }
    },
    // -- sidebar divider drag methods --
    onDividerMouseDown(widthKey, event) {
      event.preventDefault()
      this._dragStartX = event.clientX
      this._dragStartWidth = this[widthKey]
      this._activeDragTarget = widthKey
      this.isDraggingSidebar = true
      document.addEventListener('mousemove', this.onDividerMouseMove)
      document.addEventListener('mouseup', this.onDividerMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    onDividerMouseMove(event) {
      if (!this.isDraggingSidebar) return
      const dx = event.clientX - this._dragStartX
      let newWidth = this._dragStartWidth + dx
      newWidth = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, newWidth))
      this[this._activeDragTarget] = newWidth
    },
    onDividerMouseUp() {
      this.isDraggingSidebar = false
      document.removeEventListener('mousemove', this.onDividerMouseMove)
      document.removeEventListener('mouseup', this.onDividerMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },
    searchTopic(topic) {
      // type the topic into the search field with rainbow highlight, then search
      this.searchText = ''
      const input = this.$refs.searchInput
      const el = input ? (input.$el || input) : null
      if (el) {
        this.highlightElement(el)
      }
      let i = 0
      const typeNext = () => {
        if (i < topic.length) {
          this.searchText += topic[i]
          i++
          setTimeout(typeNext, 40)
        } else {
          this.loadArticles()
        }
      }
      typeNext()
    },
    guideThenManage() {
      // pulse the header button so the user learns where it lives, then auto-click it
      const btn = this.$refs.manageDocsBtn
      if (btn) {
        const el = btn.$el || btn
        this.highlightElement(el)
        setTimeout(() => this.openDocManageModal(), 1200)
      } else {
        this.openDocManageModal()
      }
    },
    // -- document management modal methods --
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '\u2014'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    formatDocDate(timestamp) {
      if (!timestamp) return '\u2014'
      const d = new Date(timestamp * 1000)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    async openDocManageModal() {
      this.showDocManageModal = true
      // kick off budget check and doc load in parallel — neither blocks the other
      this.refreshManagedBudgetStatus()
      await this.loadManagedDocs()
    },
    async refreshManagedBudgetStatus() {
      // fa-documents finalize rejects with 429 when tenant is over limit.
      // surface that state up-front so users don't hit a silent upload failure.
      try {
        const { value: { data = {} } } = await get_ai_usage(this.user.tenant)
        const usage = data.data || data || {}
        this.managedBudgetOverLimit = !!usage.over_limit
        this.managedBudgetMessage = this.managedBudgetOverLimit
          ? 'This organization has reached its monthly AI usage limit.'
          : ''
      } catch (e) {
        // fail-open: if the usage check errors out, allow the attempt rather than
        // blocking uploads. the server-side gate is still in place.
        this.managedBudgetOverLimit = false
        this.managedBudgetMessage = ''
      }
    },
    onDocManageModalClosed() {
      this.managedSelectedDocs = []
      this.managedBudgetOverLimit = false
      this.managedBudgetMessage = ''
      this.managedUploadErrorMessage = ''
      this.stopManagedDocsRefresh()
      // refresh the help center's read-only doc list
      this.loadCompanyDocs()
    },
    async loadManagedDocs(silent = false) {
      if (!silent) {
        this.managedDocsError = ''
        this.managedDocsLoading = true
      }
      try {
        const { value: { data = {} } } = await list_fa_documents_company(this.user.tenant)
        const results = data.data || data
        const serverDocs = results.documents || []
        const existing = this.managedDocs || []
        const existingByDocId = new Map()
        for (const doc of existing) {
          if (doc && doc.doc_id) existingByDocId.set(doc.doc_id, doc)
        }
        const merged = []
        for (const doc of serverDocs) {
          const current = existingByDocId.get(doc.doc_id)
          if (current) {
            Object.assign(current, doc)
            merged.push(current)
          } else {
            merged.push({ ...doc })
          }
        }
        const serverDocIds = new Set(serverDocs.map(d => d.doc_id).filter(Boolean))
        const localInProgress = existing.filter(d =>
          (d.status === 'uploading' || d.status === 'processing' || d.status === 'failed') &&
          (!d.doc_id || !serverDocIds.has(d.doc_id))
        )
        const combined = [...merged, ...localInProgress]
        this.managedDocs = combined.sort((a, b) => (b.uploaded_at || 0) - (a.uploaded_at || 0))
        const validIds = new Set(this.managedDocs.map(d => d.doc_id).filter(Boolean))
        this.managedSelectedDocs = this.managedSelectedDocs.filter(id => validIds.has(id))
        this.scheduleManagedDocsRefresh()
      } catch (e) {
        if (!silent) {
          this.managedDocsError = e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Could not load company documents'
          this.managedDocs = (this.managedDocs || []).filter(d =>
            d.status === 'uploading' || d.status === 'processing' || d.status === 'failed'
          )
        }
      } finally {
        if (!silent) {
          this.managedDocsLoading = false
        }
      }
    },
    onManagedDocPicked(event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.uploadManagedDocs(Array.from(files))
      }
      if (this.$refs.managedDocPicker) {
        this.$refs.managedDocPicker.value = ''
      }
    },
    async uploadManagedDocs(files) {
      if (!files || files.length === 0) return
      this.managedUploading = true
      this.managedDocsError = ''
      let successCount = 0
      let failCount = 0
      const MAX_FILE_SIZE = 25 * 1024 * 1024
      const MAX_DOCS = 25
      const currentCount = (this.managedDocs || []).filter(d =>
        d.doc_id || d.status === 'uploading' || d.status === 'processing'
      ).length
      const CONCURRENCY = 3
      const items = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.size > MAX_FILE_SIZE) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
          failCount++
          this.managedDocsError = `${file.name} is ${sizeMB} MB \u2014 maximum is 25 MB`
          continue
        }
        if (currentCount + items.length >= MAX_DOCS) {
          failCount++
          this.managedDocsError = `Document limit reached (${MAX_DOCS}). Delete a document to add another.`
          break
        }
        const tempId = `temp-${Date.now()}-${i}-${Math.random().toString(16).slice(2)}`
        const placeholder = {
          temp_id: tempId,
          doc_id: null,
          display_name: file.name,
          uploaded_by: (this.user && (this.user.email || this.user.username)) || '\u2014',
          uploaded_at: Math.floor(Date.now() / 1000),
          file_size: file.size,
          status: 'uploading',
        }
        this.managedDocs = [placeholder, ...(this.managedDocs || [])]
        items.push({ file, tempId })
      }
      const finalizePromises = []
      let index = 0
      const worker = async () => {
        while (index < items.length) {
          const { file, tempId } = items[index++]
          const ok = await this._uploadManagedItem(file, tempId, finalizePromises)
          if (ok) successCount++
          else failCount++
        }
      }
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker())
      )
      const finalizeResults = await Promise.allSettled(finalizePromises)
      for (const r of finalizeResults) {
        if (r.status === 'rejected' || (r.status === 'fulfilled' && !r.value)) {
          failCount++
          successCount = Math.max(0, successCount - 1)
        }
      }
      await this.loadManagedDocs()
      this.managedUploading = false
      // re-check budget in case the race caused the finalize failures
      if (failCount > 0) {
        this.refreshManagedBudgetStatus()
      }
      if (failCount === 0) {
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Uploaded ${successCount} document(s)`, 'success', 3000)
      } else if (successCount === 0) {
        // prefer the most specific error we captured (e.g. over-budget 429 message)
        const msg = this.managedUploadErrorMessage || 'Upload failed'
        this.managedDocsError = msg
        this.$emit('showAlert', msg, 'danger', 5000)
      } else {
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Uploaded ${successCount}, failed ${failCount}`, 'warning', 5000)
      }
      this.managedUploadErrorMessage = ''
    },
    async _uploadManagedItem(file, tempId, finalizePromises) {
      try {
        const filename = file && file.name ? file.name : 'document'
        const contentType = file && file.type ? file.type : 'application/octet-stream'
        const presignResp = await presign_fa_document_company(this.user.tenant, {
          filename,
          content_type: contentType,
          file_size: file && file.size ? file.size : 0,
          display_name: filename,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data ? (presignResp.value.data.data || presignResp.value.data) : {}
        const upload = presignData.upload || {}
        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)
        const s3Resp = await fetch(upload.url, { method: 'POST', body: uploadForm })
        if (!s3Resp.ok) throw new Error('S3 upload failed')
        this.managedDocs = (this.managedDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'processing', doc_id: presignData.doc_id } : d)
        finalizePromises.push(this._finalizeManagedItem(tempId, presignData, filename, contentType))
        return true
      } catch (e) {
        this._captureManagedUploadError(e)
        this.managedDocs = (this.managedDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'failed' } : d)
        return false
      }
    },
    async _finalizeManagedItem(tempId, presignData, filename, contentType) {
      try {
        const finalizeResp = await finalize_fa_document_company(this.user.tenant, {
          doc_id: presignData.doc_id,
          filename,
          content_type: contentType,
          display_name: presignData.display_name || filename,
        })
        const finalized = finalizeResp && finalizeResp.value && finalizeResp.value.data ? (finalizeResp.value.data.data || finalizeResp.value.data) : {}
        this.managedDocs = (this.managedDocs || []).map(d => {
          if (d.temp_id !== tempId) return d
          return {
            ...d,
            doc_id: finalized.doc_id || presignData.doc_id || d.doc_id,
            display_name: finalized.display_name || presignData.display_name || d.display_name,
            uploaded_at: finalized.uploaded_at || d.uploaded_at,
            uploaded_by: finalized.uploaded_by || d.uploaded_by,
            file_size: finalized.file_size || d.file_size,
            status: undefined,
          }
        })
        return true
      } catch (e) {
        this._captureManagedUploadError(e)
        this.managedDocs = (this.managedDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'failed' } : d)
        return false
      }
    },
    // pulls the most specific error the server returned (usually from a 429 over-limit)
    // and caches it so uploadManagedDocs can surface it instead of the generic fallback.
    _captureManagedUploadError(err) {
      const data = err && err.response && err.response.data
      const serverMsg = data && (data.error || data.message)
      if (serverMsg && !this.managedUploadErrorMessage) {
        this.managedUploadErrorMessage = serverMsg
      }
    },
    async deleteManagedDoc(docId) {
      if (!confirm('Delete this document? The assistant will no longer use it.')) return
      this.managedDocDeleting = docId
      try {
        await delete_fa_document_company(this.user.tenant, docId)
        await this.loadManagedDocs()
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', 'Document deleted', 'success', 3000)
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed', 'danger', 5000)
      } finally {
        this.managedDocDeleting = null
      }
    },
    toggleManagedDocSelection(docId) {
      if (!docId) return
      const idx = this.managedSelectedDocs.indexOf(docId)
      if (idx === -1) {
        this.managedSelectedDocs.push(docId)
      } else {
        this.managedSelectedDocs.splice(idx, 1)
      }
    },
    toggleManagedSelectAll() {
      if (this.allManagedDocsSelected) {
        this.managedSelectedDocs = []
      } else {
        this.managedSelectedDocs = this.selectableManagedDocs.map(d => d.doc_id)
      }
    },
    async deleteManagedSelectedDocs() {
      const toDelete = [...this.managedSelectedDocs]
      const count = toDelete.length
      if (count === 0) return
      if (!confirm(`Delete ${count} selected document(s)? This cannot be undone.`)) return
      this.managedDeletingSelected = true
      let completed = 0
      let successCount = 0
      let failCount = 0
      this.managedDeleteProgress = `0/${count}`
      const deleteSet = new Set(toDelete)
      this.managedDocs = this.managedDocs.map(d => deleteSet.has(d.doc_id) ? { ...d, status: 'deleting' } : d)
      const CONCURRENCY = 3
      let index = 0
      const worker = async () => {
        while (index < toDelete.length) {
          const docId = toDelete[index++]
          try {
            await delete_fa_document_company(this.user.tenant, docId)
            successCount++
            this.managedDocs = (this.managedDocs || []).filter(d => d.doc_id !== docId)
          } catch (e) {
            failCount++
            this.managedDocs = (this.managedDocs || []).map(d => d.doc_id === docId ? { ...d, status: 'delete-failed' } : d)
          }
          completed++
          this.managedDeleteProgress = `${completed}/${count}`
        }
      }
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, toDelete.length) }, () => worker())
      )
      this.managedSelectedDocs = []
      await this.loadManagedDocs()
      this.managedDeletingSelected = false
      this.managedDeleteProgress = ''
      if (failCount === 0) {
        this.$emit('showAlert', `Deleted ${successCount} document(s)`, 'success', 3000)
      } else {
        this.$emit('showAlert', `Deleted ${successCount}, failed ${failCount}`, 'warning', 5000)
      }
    },
    scheduleManagedDocsRefresh() {
      if (!this.showDocManageModal) {
        this.stopManagedDocsRefresh()
        return
      }
      const hasProcessing = (this.managedDocs || []).some(d => (d.status || 'uploaded') === 'processing')
      if (!hasProcessing) {
        this.stopManagedDocsRefresh()
        return
      }
      if (this._managedDocsPollTimer) return
      const delay = this._managedDocsPollDelay || 2000
      this._managedDocsPollTimer = setTimeout(async () => {
        this._managedDocsPollTimer = null
        if (this.managedDocsLoading) {
          this.scheduleManagedDocsRefresh()
          return
        }
        await this.loadManagedDocs(true)
        this._managedDocsPollDelay = Math.min(Math.round((this._managedDocsPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopManagedDocsRefresh() {
      if (this._managedDocsPollTimer) {
        clearTimeout(this._managedDocsPollTimer)
        this._managedDocsPollTimer = null
      }
      this._managedDocsPollDelay = 2000
    },
    // -- admin: article leaderboard --
    async openLeaderboardModal() {
      this.showLeaderboardModal = true
      this.leaderboardLoading = true
      this.leaderboardArticles = []
      try {
        const { value: { data = {} } } = await get_help_feedback_leaderboard(this.user.tenant)
        const payload = data.data || data
        const articles = payload.articles || payload || []
        // sort by lowest helpful % first (worst articles on top)
        this.leaderboardArticles = articles.sort(function (a, b) { return (a.helpful_pct != null ? a.helpful_pct : 100) - (b.helpful_pct != null ? b.helpful_pct : 100) })
      } catch (e) {
        this.$emit('showAlert', 'Unable to load leaderboard', 'danger', 5000)
      } finally {
        this.leaderboardLoading = false
      }
    },
    leaderboardNavigate(article) {
      this.showLeaderboardModal = false
      if (article && article.doc_id) {
        this.switchTab('articles')
        this.$nextTick(() => {
          const inList = this.articles.find(a => a.doc_id === article.doc_id)
          if (inList) {
            this.selectArticle(inList)
          } else {
            this.selectArticle({ doc_id: article.doc_id, title: article.title || 'Article', summary: '' })
          }
        })
      }
    },
    leaderboardPctClass(pct) {
      if (pct == null) return ''
      if (pct < 50) return 'fb-pct-bad'
      if (pct < 75) return 'fb-pct-warn'
      return 'fb-pct-good'
    },
    // -- admin: comment inbox --
    async openCommentInboxModal() {
      this.showCommentInboxModal = true
      this.commentInboxLoading = true
      this.commentInboxItems = []
      try {
        const { value: { data = {} } } = await list_chat_feedback({ type: 'help_article', limit: 100 })
        const payload = data.data || data
        // only items with a comment
        this.commentInboxItems = (payload.items || payload || []).filter(i => i.comment && i.comment.trim())
      } catch (e) {
        this.$emit('showAlert', 'Unable to load comment inbox', 'danger', 5000)
      } finally {
        this.commentInboxLoading = false
      }
    },
    async markCommentReviewed(item) {
      if (!item || !item.feedback_id) return
      this.$set(item, '_updating', true)
      try {
        await update_chat_feedback_status(item.feedback_id, { status: 'reviewed' })
        this.$set(item, 'status', 'reviewed')
      } catch (e) {
        this.$emit('showAlert', 'Could not update status', 'danger', 4000)
      } finally {
        this.$set(item, '_updating', false)
      }
    },
    // -- admin: feedback review tab --
    switchFeedbackSubTab(tab) {
      if (this.feedbackSubTab === tab) return
      this.feedbackSubTab = tab
      this.feedbackReviewItems = []
      this.feedbackReviewStats = null
      this.fbCurrentPage = 1
      this.loadFeedbackReview()
    },
    async loadFeedbackReview() {
      this.feedbackReviewLoading = true
      this.fbCurrentPage = 1
      try {
        const params = {
          type: this.feedbackSubTab,
          limit: 200,
        }
        if (this.fbFilterFromDate) params.start = Math.floor(moment(this.fbFilterFromDate, 'MM-DD-YYYY').startOf('day').valueOf() / 1000)
        if (this.fbFilterToDate) params.end = Math.floor(moment(this.fbFilterToDate, 'MM-DD-YYYY').endOf('day').valueOf() / 1000)
        if (this.fbFilterRating) params.rating = this.fbFilterRating
        // 'open' is a frontend-synthesized filter (NEW + REVIEWED); backend
        // only understands exact status values, so don't pass it through
        if (this.fbFilterStatus && this.fbFilterStatus !== 'open') params.status = this.fbFilterStatus
        if (this.fbFilterTenant) params.tenant = this.fbFilterTenant
        if (this.fbShowDeleted) params.include_deleted = 'true'

        const [fbResp, statsResp] = await Promise.all([
          list_chat_feedback(params),
          get_chat_feedback_stats({ type: this.feedbackSubTab, days: 30 }),
        ])

        const fbPayload = fbResp.value && fbResp.value.data ? (fbResp.value.data.data || fbResp.value.data) : {}
        let rawItems = (fbPayload.items || fbPayload || []).map(function (item) {
          // derive context_preview from nested context
          var ctx = item.context || {}
          var preview = ''
          if (item.feedback_type === 'fa_response') {
            preview = ctx.user_message || ''
          } else if (item.feedback_type === 'help_article') {
            preview = ctx.article_title || ctx.doc_id || ''
          } else if (item.feedback_type === 'topic_suggestion') {
            preview = ctx.search_query || ''
          }
          return Object.assign({}, item, { context_preview: preview, _updating: false })
        })
        // client-side "Open" filter: NEW + REVIEWED (excludes ACTIONED)
        if (this.fbFilterStatus === 'open') {
          rawItems = rawItems.filter(i => (i.status || 'new') !== 'actioned')
        }
        this.feedbackReviewItems = rawItems

        const statsPayload = statsResp.value && statsResp.value.data ? (statsResp.value.data.data || statsResp.value.data) : {}
        this.feedbackReviewStats = statsPayload
        this.feedbackReviewLoaded = true
      } catch (e) {
        this.$emit('showAlert', 'Unable to load feedback', 'danger', 5000)
      } finally {
        this.feedbackReviewLoading = false
      }
    },
    async updateFeedbackStatus(item, status) {
      if (!item || !item.feedback_id) return
      this.$set(item, '_updating', true)
      try {
        const resp = await update_chat_feedback_status(item.feedback_id, { tenant_id: item.tenant_id, status: status })
        // pick up reviewed_by / reviewed_at from the response so the Handled By
        // column populates immediately without a full list reload
        const updated = resp && resp.value && resp.value.data ? (resp.value.data.data || resp.value.data) : null
        this.$set(item, 'status', status)
        if (updated) {
          if (updated.reviewed_by) this.$set(item, 'reviewed_by', updated.reviewed_by)
          if (updated.reviewed_at) this.$set(item, 'reviewed_at', updated.reviewed_at)
        }
      } catch (e) {
        this.$emit('showAlert', 'Could not update status', 'danger', 4000)
      } finally {
        this.$set(item, '_updating', false)
      }
    },
    async confirmDeleteFeedback(item) {
      if (!item || !item.feedback_id) return
      if (!confirm('Delete this feedback? It can be recovered by toggling "Show deleted".')) return
      this.$set(item, '_updating', true)
      try {
        await delete_chat_feedback(item.tenant_id, item.feedback_id)
        // remove from the visible list unless Show deleted is on; in either
        // case the next reload will reflect the new state
        if (this.fbShowDeleted) {
          this.$set(item, 'deleted_at', Math.floor(Date.now() / 1000))
        } else {
          this.feedbackReviewItems = this.feedbackReviewItems.filter(i => i.feedback_id !== item.feedback_id)
        }
        this.$emit('showAlert', 'Feedback deleted', 'success', 2500)
      } catch (e) {
        this.$emit('showAlert', 'Could not delete feedback', 'danger', 4000)
      } finally {
        this.$set(item, '_updating', false)
      }
    },
    async restoreFeedback(item) {
      if (!item || !item.feedback_id) return
      this.$set(item, '_updating', true)
      try {
        await restore_chat_feedback(item.tenant_id, item.feedback_id)
        // clear the deleted marker locally so the row updates without a full reload
        this.$set(item, 'deleted_at', null)
        this.$set(item, 'deleted_by', null)
        this.$emit('showAlert', 'Feedback restored', 'success', 2500)
      } catch (e) {
        this.$emit('showAlert', 'Could not restore feedback', 'danger', 4000)
      } finally {
        this.$set(item, '_updating', false)
      }
    },
    reviewerInitials(email) {
      if (!email) return ''
      // grab the local part before @, then initials from first + last dotted pieces
      const local = String(email).split('@')[0]
      const parts = local.split(/[._-]+/).filter(Boolean)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return (parts[0] || '?').slice(0, 2).toUpperCase()
    },
    // prefer the logged-in user's first name when the reviewer is self; otherwise
    // fall back to the first dotted piece of the email local part, capitalized.
    reviewerDisplayName(email) {
      if (!email) return ''
      if (this.user && this.user.username === email && this.user.first_name) {
        return this.user.first_name
      }
      const local = String(email).split('@')[0]
      const first = (local.split(/[._-]+/).filter(Boolean)[0]) || local
      return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase()
    },
    // deterministic pastel-ish hue from the email so each reviewer lands on the
    // same pill color every time. saturation/lightness tuned so white text stays
    // readable on any hue.
    reviewerPillColor(email) {
      if (!email) return '#6c757d'
      let hash = 0
      const s = String(email).toLowerCase()
      for (let i = 0; i < s.length; i++) {
        hash = (hash * 31 + s.charCodeAt(i)) >>> 0
      }
      const hue = hash % 360
      return 'hsl(' + hue + ', 45%, 45%)'
    },
    feedbackStatusClass(status) {
      if (status === 'reviewed') return 'fb-status-reviewed'
      if (status === 'actioned') return 'fb-status-actioned'
      return 'fb-status-new'
    },
    truncateText(text, max) {
      if (!text) return ''
      return text.length > max ? text.substring(0, max) + '...' : text
    },
    // rating tag descriptor for the feedback-review table. parallels the
    // `getPriorityTag()` pattern on support_ticket_list so the rating chip
    // reads as part of the same tag-family visually. returns null for
    // unknown ratings so the template falls back to a neutral muted tag.
    getRatingTag(rating) {
      // color choices:
      //   positive → green (success)
      //   negative → yellow (mildly unhappy — a complaint, not a crisis)
      //   concern  → red (danger — user is flagging something that needs
      //              immediate attention, stronger signal than "negative")
      const tags = {
        positive: { label: 'Positive', icon: 'fa-thumbs-up',   variant: 'fb-tag-success' },
        negative: { label: 'Negative', icon: 'fa-thumbs-down', variant: 'fb-tag-warning' },
        concern:  { label: 'Concern',  icon: 'fa-flag',        variant: 'fb-tag-danger'  },
      }
      return tags[rating] || null
    },
    getTenantLabel(tenantId) {
      if (!tenantId) return '-'
      if (this.user && this.user.tenant_options) {
        const opt = this.user.tenant_options.find(o => (typeof o === 'object' ? o.value : o) === tenantId)
        if (opt && typeof opt === 'object') return opt.text || tenantId
      }
      return tenantId
    },
    // -- admin: feedback detail + conversation viewer --
    openFeedbackDetail(item) {
      this.feedbackDetailItem = item
      this.conversationMessages = []
      this.conversationLoading = false
      this.feedbackExported = false
      this.feedbackExportIsFile = false
      this.showFeedbackDetailModal = true
      // encode in URL so the detail is shareable
      if (item && item.tenant_id && item.feedback_id) {
        var query = Object.assign({}, this.$route.query, { fb_tenant: item.tenant_id, fb_id: item.feedback_id })
        this.$router.replace({ query: query }).catch(function () {})
      }
    },
    async _loadFeedbackDetailFromUrl(tenantId, feedbackId) {
      // load a single feedback item by ID and open the detail modal
      try {
        var resp = await get_chat_feedback_item(tenantId, feedbackId)
        var item = resp.value && resp.value.data ? (resp.value.data.data || resp.value.data) : null
        if (item && item.tenant_id) {
          var ctx = item.context || {}
          item.context_preview = ctx.user_message || ctx.article_title || ctx.search_query || ''
          item._updating = false
          this.openFeedbackDetail(item)
        } else {
          this.$emit('showAlert', 'Feedback item not found', 'warning', 4000)
        }
      } catch (e) {
        console.warn('Failed to load feedback detail from URL:', e)
        this.$emit('showAlert', 'Unable to load feedback detail', 'danger', 4000)
      }
    },
    exportFeedbackDetail() {
      var item = this.feedbackDetailItem
      if (!item) return
      var ctx = item.context || {}
      var lines = [
        '# Fleet Assistant Response Feedback',
        '',
        '> This feedback was exported from the Fleet Integrations Feedback Review system.',
        '> Use this context to diagnose and improve the Fleet Assistant response quality.',
        '',
        '## Issue Summary',
        '',
        '- **Rating:** ' + (item.rating || '') + (item.rating === 'concern' ? ' (user flagged a concern)' : item.rating === 'negative' ? ' (user marked as not helpful)' : ''),
        '- **User Comment:** ' + (item.comment || 'No comment provided'),
        '',
        '## The Interaction',
        '',
        '### User Question',
        '```',
        ctx.user_message || '-',
        '```',
        '',
        '### Assistant Response',
        '```',
        ctx.assistant_response || '-',
        '```',
        '',
        '## Request Metadata',
        '',
        '| Field | Value |',
        '|-------|-------|',
        '| Model | ' + (ctx.model || '-') + ' |',
        '| Total Tokens | ' + (ctx.tokens || '-') + ' |',
        '| Estimated Cost | $' + (ctx.cost || '-') + ' |',
        '| Page/Route | ' + (ctx.route_name || '-') + ' |',
        '| Tenant | ' + (item.tenant_id || '-') + ' |',
        '| User | ' + (item.user_id || '-') + ' |',
        '| Session ID | `' + (ctx.session_id || '-') + '` |',
        '| Turn Index | ' + (ctx.turn_index != null ? ctx.turn_index : '-') + ' |',
        '| Date | ' + this.formatFeedbackDate(item.created_at) + ' |',
        '| Feedback ID | ' + (item.feedback_id || '-') + ' |',
      ]

      // include full conversation with tool calls if loaded
      if (this.conversationMessages && this.conversationMessages.length > 0) {
        lines.push('', '## Full Conversation', '')
        var totalTokens = 0
        var totalCost = 0
        var toolCallCount = 0
        for (var i = 0; i < this.conversationMessages.length; i++) {
          var msg = this.conversationMessages[i]
          lines.push('### Turn ' + (i + 1) + ' — ' + (msg.role === 'user' ? 'User' : 'Assistant'))
          lines.push('')
          if (msg.role === 'user') {
            lines.push('```')
            lines.push(msg.content || '')
            lines.push('```')
          } else {
            lines.push(msg.content || '')
            lines.push('')
            // per-turn metadata
            var meta = []
            if (msg.model) meta.push('Model: ' + msg.model)
            if (msg.tokens) { meta.push('Tokens: ' + msg.tokens); totalTokens += msg.tokens }
            if (msg.cost) { meta.push('Cost: $' + (typeof msg.cost === 'number' ? msg.cost.toFixed(4) : msg.cost)); totalCost += (typeof msg.cost === 'number' ? msg.cost : 0) }
            if (meta.length) lines.push('*' + meta.join(' · ') + '*')
            // tool calls with full detail
            if (msg.tool_calls && msg.tool_calls.length > 0) {
              lines.push('')
              lines.push('**Tool Calls:**')
              for (var t = 0; t < msg.tool_calls.length; t++) {
                var tc = msg.tool_calls[t]
                toolCallCount++
                lines.push('')
                lines.push('- **' + (tc.name || 'unknown') + '**' + (tc.duration_ms ? ' (' + tc.duration_ms + 'ms)' : ''))
                if (tc.arguments && Object.keys(tc.arguments).length > 0) {
                  lines.push('  - Parameters: `' + JSON.stringify(tc.arguments) + '`')
                }
                if (tc.result) {
                  var resultStr = typeof tc.result === 'string' ? tc.result : JSON.stringify(tc.result)
                  if (resultStr.length > 500) resultStr = resultStr.substring(0, 500) + '... (truncated)'
                  lines.push('  - Result: `' + resultStr + '`')
                }
              }
            }
          }
          lines.push('')
        }
        // session totals
        lines.push('### Session Totals')
        lines.push('')
        lines.push('- **Total tokens:** ' + totalTokens)
        lines.push('- **Total cost:** $' + totalCost.toFixed(4))
        lines.push('- **Tool calls:** ' + toolCallCount)
        lines.push('- **Turns:** ' + this.conversationMessages.length)
      } else {
        lines.push('', '> Note: Full conversation not loaded. Click "View Full Session" in the Feedback Detail modal before exporting to include the complete conversation with tool call details.')
      }

      // investigation hints
      lines.push('')
      lines.push('## Investigation Guide')
      lines.push('')
      lines.push('Common issues to check:')
      lines.push('- **Incorrect facts:** Did the assistant state something factually wrong about the fleet data?')
      lines.push('- **Wrong directions:** Did the assistant tell the user to do something the app doesn\'t support?')
      lines.push('- **Incorrect conclusions:** Did the assistant draw a wrong conclusion from the tool call results?')
      lines.push('- **Performance:** Was the response slow? Check tool call durations and total token count.')
      lines.push('- **Cost:** Was this response expensive? Check if unnecessary tool calls were made.')
      lines.push('- **Missing context:** Did the assistant lack information it should have had?')
      lines.push('- **Tool call errors:** Did any tool calls return errors or unexpected results?')
      lines.push('')
      lines.push('Relevant source files:')
      lines.push('- `fleet-int-api/external/openai_client.py` — chat logic, tool definitions, response handling')
      lines.push('- `fleet-int-api/ai/fleet_assistant.py` — session management, system prompt, context building')
      lines.push('- `fleet-int-api/ai/fa_documents.py` — document retrieval for RAG context')

      var text = lines.join('\n')
      var self = this
      if (this.feedbackExportIsFile) {
        // export as .md file download
        var blob = new Blob([text], { type: 'text/markdown;charset=utf-8' })
        var url = URL.createObjectURL(blob)
        var a = document.createElement('a')
        a.href = url
        a.download = 'feedback-' + (item.feedback_id || 'export') + '.md'
        a.style.display = 'none'
        document.body.appendChild(a)
        setTimeout(function () {
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          self.feedbackExported = true
          setTimeout(function () { self.feedbackExported = false }, 2000)
        }, 0)
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function () {
          self.feedbackExported = true
          setTimeout(function () { self.feedbackExported = false }, 2000)
        })
      }
    },
    onFeedbackDetailClosed() {
      // remove feedback detail params from URL
      if (this.$route.query.fb_tenant || this.$route.query.fb_id) {
        var query = Object.assign({}, this.$route.query)
        delete query.fb_tenant
        delete query.fb_id
        this.$router.replace({ query: query }).catch(function () {})
      }
    },
    toggleConversationSession() {
      if (this.conversationMessages.length > 0) {
        this.conversationMessages = []
        this.feedbackExportIsFile = false
      } else {
        this.loadConversationSession()
      }
    },
    renderConversationMarkdown(content) {
      if (!content) return ''
      return renderMarkdownToHtml(content)
    },
    async loadConversationSession() {
      const ctx = this.feedbackDetailItem && this.feedbackDetailItem.context
      if (!ctx || !ctx.session_id) return
      this.conversationLoading = true
      this.conversationMessages = []
      try {
        const tenant = this.feedbackDetailItem.tenant_id || this.user.tenant
        const { value: { data = {} } } = await get_admin_chat_session(tenant, ctx.session_id)
        const payload = data.data || data
        // transform raw turns into display messages
        var turns = payload.turns || []
        var msgs = []
        for (var i = 0; i < turns.length; i++) {
          var turn = turns[i]
          if (turn.user) {
            msgs.push({ role: 'user', content: turn.user })
          }
          if (turn.assistant) {
            var usage = turn.usage || {}
            msgs.push({
              role: 'assistant',
              content: turn.assistant,
              model: usage.model || '',
              tokens: (usage.input_tokens || 0) + (usage.output_tokens || 0),
              cost: usage.estimated_cost_usd || null,
              tool_calls: turn.tool_calls || [],
            })
          }
        }
        this.conversationMessages = msgs
        // with full session loaded, always use file export — better for Claude Code as a file attachment
        this.feedbackExportIsFile = msgs.length > 0
      } catch (e) {
        this.$emit('showAlert', 'Unable to load conversation', 'danger', 5000)
      } finally {
        this.conversationLoading = false
      }
    },
    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$emit('showAlert', 'Copied to clipboard', 'success', 2000)
        })
      }
    },
    // --- Assistant Insights (FI-1600) admin methods ---

    faTenantLabel(tenantId) {
      if (!tenantId) return ''
      const map = tenantLabelMap(this.user)
      return map[tenantId] || tenantId
    },

    faSetPeriod(value) {
      // '' = current month (picker token, not in URL); 'last' or 'YYYY-MM'
      // get persisted as the URL query param so deep links carry the
      // period and reloads survive.
      const query = { ...this.$route.query, tab: 'insights' }
      if (!value) delete query.fa_period
      else query.fa_period = value
      this.$router.replace({ query }).catch(() => {})
      // invalidate loaded-for caches so the watcher re-fetches.
      this.faFleetLoadedAt = 0
      this.faFleetRows = []
      this.faTenantLoadedFor = ''
      this.faUserLoadedFor = ''
      this.$nextTick(() => this.maybeLoadFaCurrentView())
    },

    faNavigate(opts) {
      // each level transition (Fleet ↔ Tenant ↔ User ↔ Conversation)
      // gets its own history entry via $router.push, so browser back /
      // forward step level-by-level. previously we used replace() which
      // collapsed the whole drill-down into one history step. the
      // existing $route watcher (above) catches popstate-driven query
      // changes and calls maybeLoadFaCurrentView() so the data reloads
      // for whichever level the back/forward landed on.
      const query = { ...this.$route.query, tab: 'insights' }
      const view = opts.view || 'fleet'
      if (view === 'fleet') {
        delete query.fa_view
        delete query.fa_tenant
        delete query.fa_user
        delete query.fa_session
        delete query.fa_archived
      } else {
        query.fa_view = view
        if (opts.tenant) query.fa_tenant = opts.tenant
        if (opts.user) query.fa_user = opts.user
        else if (view === 'tenant') delete query.fa_user
        if (opts.session) query.fa_session = opts.session
        else if (view !== 'session') delete query.fa_session
        if (opts.archived) query.fa_archived = '1'
        else delete query.fa_archived
      }
      // .catch swallows NavigationDuplicated when the user clicks a
      // breadcrumb for the level they're already on — push() rejects
      // rather than no-ops, so the catch keeps it quiet.
      this.$router.push({ query }).catch(() => {})
      // load handled by the $route watcher, but kick it now too so the
      // spinner shows before the route watcher fires.
      this.$nextTick(() => this.maybeLoadFaCurrentView())
    },

    maybeLoadFaCurrentView() {
      if (this.activeTab !== 'insights' || !this.isAdmin) return
      const view = this.faView
      // resolve to a concrete YYYY-MM so the loaded-for key matches the
      // value the loader writes (which always resolves to the current
      // month when the picker token is empty).
      const periodKey = this.faPeriod || (function () {
        const d = new Date()
        return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
      })()
      if (view === 'fleet') {
        if (this.faFleetRows.length === 0 && !this.faFleetLoading) this.loadFaFleet(false)
        return
      }
      if (view === 'tenant' && this.faSelectedTenant) {
        const wantKey = this.faSelectedTenant + '|' + periodKey
        if (this.faTenantLoadedFor !== wantKey) this.loadFaTenant(this.faSelectedTenant)
        return
      }
      if (view === 'user' && this.faSelectedTenant && this.faSelectedUser) {
        const wantKey = this.faSelectedTenant + ':' + this.faSelectedUser + '|' + periodKey
        if (this.faUserLoadedFor !== wantKey) this.loadFaUser(this.faSelectedTenant, this.faSelectedUser)
        return
      }
      if (view === 'session' && this.faSelectedTenant && this.faSelectedSession) {
        const archived = !!(this.$route && this.$route.query && this.$route.query.fa_archived)
        const key = this.faSelectedTenant + ':' + this.faSelectedSession + (archived ? ':a' : ':l')
        if (this.faSessionLoadedFor !== key) this.loadFaSession(this.faSelectedTenant, this.faSelectedSession, archived)
      }
    },

    async loadFaFleet(force) {
      if (!this.isAdmin) return
      // in-flight guard — only blocks duplicate non-forced loads. force
      // (Refresh button) always proceeds so clicking it mid-fanout
      // doesn't silently short-circuit and leave the user watching stale
      // results from the previous, pre-refresh data.
      if (this.faFleetLoading && !force) return
      const tenants = (this.user && this.user.tenant_options) || []
      if (!force && this.faFleetRows.length > 0 && (Date.now() - this.faFleetLoadedAt < 5 * 60 * 1000)) {
        return
      }
      // bump a request token so a later Refresh that finishes first
      // doesn't get overwritten by an earlier slow fanout finishing later.
      const token = (this._faFleetRequestToken || 0) + 1
      this._faFleetRequestToken = token
      this.faFleetLoading = true
      this.faFleetLoadedCount = 0
      this.faFleetTotalTenants = tenants.length
      const labelMap = tenantLabelMap(this.user)
      try {
        // single batched call per service (CM + TBN); replaces the
        // previous per-tenant fan-out that issued 2 requests × N tenants
        // and was throttled to 6 concurrent by the browser. period flows
        // from the picker — '' means "current" (server defaults to it).
        const fleet = await apiAiUsageFleet(this.user, this.faPeriod)
        if (this._faFleetRequestToken !== token) return  // stale, drop
        const rows = (fleet.rows || []).map(item => {
          const current = item.current || {}
          const prior = item.prior || {}
          const blended = Number(current.blended_cost_cents || 0)
          const priorCost = Number(prior.blended_cost_cents || 0)
          const quotaLimitCents = Number(item.quota_limit_cents || 0)
          const quotaPct = Number(item.percentage || 0)
          const row = {
            tenant_id: item.tenant_id,
            tenant_name: labelMap[item.tenant_id] || item.tenant_id,
            fa_enabled: !!item.fa_enabled,
            active_users: 0, // filled by tenant-detail view; fleet row keeps it cheap
            chat_requests: Number(current.chat_requests || 0),
            doc_categorizations: Number(current.doc_categorizations || 0),
            blended_cost_cents: blended,
            quota_limit_cents: quotaLimitCents,
            quota_pct: Math.max(0, Math.min(100, quotaPct)),
            usage_percentage: quotaPct,
            reconciled_at: Number(current.reconciled_at || 0),
            exact_cost_cents: Number(current.exact_cost_cents || 0),
            estimated_cost_cents_at_reconcile: Number(current.estimated_cost_cents_at_reconcile || 0),
            last_activity: Number(current.updated_at || 0),
            mom_multiplier: priorCost > 0 ? (blended / priorCost) : 0,
          }
          const projDate = projectLimitHitDate(blended, quotaLimitCents)
          row.projected_limit_hit = projDate ? projDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''
          row.anomaly_flags = anomalyFlagsForRow(row)
          return row
        })
        this.faFleetLoadedCount = rows.length
        this.faFleetRows = rows.sort((a, b) => (b.blended_cost_cents || 0) - (a.blended_cost_cents || 0))
        this.faFleetLoadedAt = Date.now()
      } catch (e) {
        // surface the failure rather than leaving a stale-looking spinner.
        this.$emit('showAlert', 'Unable to load fleet summary', 'danger', 5000)
      } finally {
        if (this._faFleetRequestToken === token) this.faFleetLoading = false
      }
    },

    async loadFaTenant(tenantId) {
      if (!this.isAdmin || !tenantId) return
      this.faTenantLoading = true
      // include period in the loaded-for key so changing the picker
      // re-triggers a load instead of being short-circuited by the
      // duplicate-load guard in maybeLoadFaCurrentView.
      const periodArg = this.faPeriod
      const effectivePeriod = periodArg || (function () {
        const d = new Date()
        return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
      })()
      this.faTenantLoadedFor = tenantId + '|' + effectivePeriod
      try {
        const [usageRes, histRes, usersRes, toolsRes, docsRes] = await Promise.allSettled([
          apiAiUsage(this.user, tenantId, periodArg),
          apiAiUsageHistory(this.user, tenantId, 12),
          apiAiUsageUsers(this.user, tenantId, periodArg),
          apiToolsSummary(this.user, tenantId, effectivePeriod),
          apiListCompanyDocs(this.user, tenantId),
        ])
        const unwrap = (r) => r.status === 'fulfilled' ? (((r.value || {}).value || {}).data || {}) : {}
        const usage = unwrap(usageRes); const hist = unwrap(histRes)
        const users = unwrap(usersRes); const tools = unwrap(toolsRes); const docs = unwrap(docsRes)
        const usageP = usage.data || usage
        const histP = hist.data || hist
        const usersP = users.data || users
        const toolsP = tools.data || tools
        const docsP = docs.data || docs
        const months = (histP && histP.months) || []
        // pin the summary card to the selected period — the user/tool
        // tables below run their own period-scoped queries, so this keeps
        // all of the "this month" framing aligned.
        const current = months.find(m => m.period === effectivePeriod) || {}
        const activeUsers = ((usersP && usersP.users) || []).length
        this.faTenantData = {
          tenant_id: tenantId,
          tenant_name: this.faTenantLabel(tenantId),
          // prefer the server-side truth from get_tenant_usage_percentage
          // over the client's features map — the latter is only populated
          // for tenants the admin is explicitly configured for.
          fa_enabled: !!usageP.fa_enabled,
          quota_pct: Number(usageP.percentage || 0),
          quota_limit_cents: Number(usageP.quota_limit_cents || 0),
          blended_cost_cents: Number(current.blended_cost_cents || 0),
          chat_requests: Number(current.chat_requests || 0),
          doc_categorizations: Number(current.doc_categorizations || 0),
          reconciled_at: Number(current.reconciled_at || 0),
          active_users: activeUsers,
          period: effectivePeriod,
          history: months,
          users: (usersP && usersP.users) || [],
          tools_summary: toolsP || { tools: [], truncated: false },
          documents: (docsP && docsP.documents) || [],
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load tenant insights', 'danger', 5000)
      } finally {
        this.faTenantLoading = false
      }
    },

    async loadFaUser(tenantId, userId) {
      if (!this.isAdmin || !tenantId || !userId) return
      this.faUserLoading = true
      const periodArg = this.faPeriod
      const effectivePeriod = periodArg || (function () {
        const d = new Date()
        return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
      })()
      this.faUserLoadedFor = tenantId + ':' + userId + '|' + effectivePeriod
      try {
        const [usersRes, liveRes, archivedRes] = await Promise.allSettled([
          apiAiUsageUsers(this.user, tenantId, periodArg),
          apiAdminChatLiveSession(this.user, tenantId, userId),
          apiListArchivedSessions(this.user, tenantId, { user_id: userId, limit: 50 }),
        ])
        const unwrap = (r) => r.status === 'fulfilled' ? (((r.value || {}).value || {}).data || {}) : {}
        const usersP = (unwrap(usersRes).data || unwrap(usersRes))
        const liveP = (unwrap(liveRes).data || unwrap(liveRes))
        const archivedP = (unwrap(archivedRes).data || unwrap(archivedRes))
        const match = ((usersP && usersP.users) || []).find(u => u.user_id === userId) || {}
        // archived sessions list isn't period-scoped server-side; filter
        // client-side by created_at falling within the selected month so
        // the user view's "Conversations" list matches the picker.
        const allArchived = (archivedP && archivedP.sessions) || []
        const archivedForPeriod = allArchived.filter(s => {
          const ts = Number(s.created_at || s.updated_at || 0)
          if (!ts) return false
          const d = new Date(ts * 1000)
          const sk = d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
          return sk === effectivePeriod
        })
        // hide the "live session" affordance when the picker isn't on
        // the current month — a live session is by definition right now.
        const isCurrentMonth = (function () {
          const d = new Date()
          const cur = d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
          return effectivePeriod === cur
        })()
        this.faUserData = {
          tenant_id: tenantId,
          user_id: userId,
          period: effectivePeriod,
          blended_cost_cents: Number(match.blended_cost_cents || 0),
          chat_requests: Number(match.chat_requests || 0),
          doc_categorizations: Number(match.doc_categorizations || 0),
          input_tokens: Number(match.input_tokens || 0),
          output_tokens: Number(match.output_tokens || 0),
          live_session: isCurrentMonth ? (liveP || { exists: false }) : { exists: false },
          archived_sessions: archivedForPeriod,
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load user insights', 'danger', 5000)
      } finally {
        this.faUserLoading = false
      }
    },

    async loadFaSession(tenantId, sessionId, archived) {
      if (!this.isAdmin || !tenantId || !sessionId) return
      this.faSessionLoading = true
      this.faSessionLoadedFor = tenantId + ':' + sessionId + (archived ? ':a' : ':l')
      try {
        let payload
        if (archived) {
          const res = await apiGetArchivedSession(this.user, tenantId, sessionId)
          payload = ((res && res.value && res.value.data) || {})
          payload = payload.data || payload
        } else {
          // for the live session, prefer the live-session lookup by user_id
          // when available — the archived/live endpoints take different keys.
          const res = await apiAdminChatLiveSession(this.user, tenantId, this.faSelectedUser)
          payload = ((res && res.value && res.value.data) || {})
          payload = payload.data || payload
        }
        this.faSessionData = payload || {}
      } catch (e) {
        this.$emit('showAlert', 'Unable to load session', 'danger', 5000)
      } finally {
        this.faSessionLoading = false
      }
    },

    faOpenFeedbackForTenant(tenantId) {
      // cross-link to the Feedback Review tab with tenant filter prefilled.
      // push (not replace) so browser back returns to the Insights view
      // the user clicked from. switchTab() uses replace, which would
      // collapse the Tenant-view history entry into the Feedback entry —
      // wrong for a deliberate cross-link. handle the URL update
      // directly here and let the $route watcher pick up the tab change.
      this.fbFilterTenant = tenantId
      const query = { ...this.$route.query, tab: 'feedback' }
      delete query.fa_view
      delete query.fa_tenant
      delete query.fa_user
      delete query.fa_session
      delete query.fa_archived
      delete query.fa_period
      this.$router.push({ query }).catch(() => {})
      this.activeTab = 'feedback'
      this.mobileShowDetail = false
      this.$nextTick(() => this.loadFeedbackReview && this.loadFeedbackReview())
    },
  },
}
</script>

<style scoped>
.help-center-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  text-align: left;
}
/* clip horizontal overflow always; vertical overflow visible for support/feedback date pickers */
.help-center-root {
  overflow-x: hidden;
}
.help-center-root:not(.help-tab-support):not(.help-tab-feedback):not(.help-tab-announcements):not(.help-tab-insights) {
  overflow: hidden;
}
/* support + announcements + insights tabs scroll with the page, not in
   a nested container. overflow: visible is required so sticky headers
   and taller composer forms can anchor to the viewport instead of
   getting trapped by help-center-root's overflow-x: hidden. insights
   was originally clipped at viewport height which truncated the FA
   fleet table around row 5/6 on small screens — see comment near
   .help-insights-body ("flows with the page; no nested scroller"). */
.help-center-root.help-tab-support,
.help-center-root.help-tab-announcements,
.help-center-root.help-tab-insights {
  height: auto;
  min-height: calc(100vh - 80px);
  overflow: visible;
}

.help-header-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: #eee;
  border-bottom: 1px solid #dce1e6;
  flex-shrink: 0;
  flex-wrap: wrap;
}

/* toolbar search fields */
.help-search-fields {
  flex-shrink: 1;
  min-width: 0;
}

.help-mobile-back {
  display: none;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f5f5f5;
  border: none;
  border-bottom: 1px solid #dce1e6;
  font-size: 13px;
  font-weight: 600;
  color: #1a5ea8;
  cursor: pointer;
  width: 100%;
  text-align: left;
}
.help-mobile-back:hover {
  background: #eaeef3;
}

.help-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  flex-shrink: 1;
  min-width: 0;
  max-width: 100%;
}

.help-tab {
  padding: 6px 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background: #ddd;
  font-size: 16px;
  font-weight: 700;
  color: #555;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.help-tab:hover {
  background: #e0e0e0;
}

.help-tab.active {
  background: #fff;
  color: #1a5ea8;
  border-color: #aab;
}

.help-admin-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  background: #fef3e2;
  color: #c45e00;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  vertical-align: middle;
  margin-right: 3px;
}

/* admin-only tab uses the admin color palette instead of the neutral gray
   other tabs use. signals admin-scope visually without needing a badge. */
.help-tab-admin {
  background: #fef3e2;
  border-color: #f0c88a;
  color: #8a4500;
}
.help-tab-admin:hover {
  background: #fce7c9;
}
.help-tab-admin.active {
  background: #fff;
  color: #c45e00;
  border-color: #e0a866;
}

.help-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: #f5f6f8;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: nowrap;
}

.help-toolbar-spacer {
  flex: 1;
}

/* sys-admin-only actions in the help-center toolbar (e.g. Article Leaderboard)
   get the same burnt-orange treatment used on admin-only rows in the Fleet
   Assistant settings, so privileged actions read as a coherent family across
   the app. outline style keeps it unobtrusive; the accent says "you can see
   this because you're a sys-admin". button is already gated by v-if="isAdmin"
   — the color is a visual cue, not the access control. */
.help-leaderboard-btn {
  border-color: #f0a957 !important;
  color: #c45e00 !important;
  background: #fff8ef !important;
}
.help-leaderboard-btn:hover,
.help-leaderboard-btn:focus {
  background: #fdecd5 !important;
  border-color: #c45e00 !important;
  color: #c45e00 !important;
}
.help-leaderboard-btn .fa-trophy {
  color: #c45e00;
}

.help-search-fields {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

/* input flex-grows to fill whatever width the parent is bound to; the container
   width is driven inline by the resizable sidebar so the search row stays
   visually flush with the articles / documents list below. */
.help-search-fields .form-control {
  flex: 1;
  min-width: 0;
}

.help-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.help-support-body {
  flex: 1;
  position: relative; /* required by bootstrap-datetimepicker for dropdown positioning */
}

.help-sidebar {
  min-width: 200px;
  max-width: 600px;
  background: #fbfbfc;
  overflow-y: auto;
  padding: 10px;
  flex-shrink: 0;
}

.help-divider {
  width: 5px;
  cursor: col-resize;
  background: transparent;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  margin: 0 -2px;
}

.help-divider:hover,
.help-divider:active {
  background: rgba(26, 94, 168, 0.12);
}

.help-divider::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  width: 1px;
  background: #dce1e6;
}

.help-list-item {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: #fff;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.help-list-item:hover {
  background: #f0f4f8;
  border-color: #c8d6e0;
}

.help-list-item.active {
  background: #e8f0fe;
  border-color: #4a90d9;
}

.help-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 3px;
  line-height: 1.3;
}

.help-list-item.active .help-item-title {
  color: #1a5ea8;
}

.help-item-summary {
  font-size: 11.5px;
  color: #777;
  line-height: 1.35;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.help-item-meta {
  display: flex;
  gap: 4px;
  align-items: center;
}

/* view count indicator — subtle, muted gray with a small eye icon. rendered
   only when view_count > 0 so new / never-opened articles don't show "0 views".
   pulls from the view_count field populated by list_help_articles() (bumped
   atomically per unique reader-session via record_help_article_view). */
.help-item-views {
  font-size: 11px;
  color: #9098a1;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}
.help-item-views .fa {
  font-size: 10px;
}

.help-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.help-badge.type {
  background: #eef0f2;
  color: #5a6268;
}

.help-badge.tenant {
  background: #dbeafe;
  color: #1e5fa8;
}

.help-content-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  text-align: left;
}

.help-article-header {
  padding: 16px 0 12px;
  margin: 0 24px 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  /* transition padding/margin so the collapse feels smooth rather than snapped */
  transition: padding 0.22s ease, margin-bottom 0.22s ease;
}

.help-article-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
  /* keep title margin animatable for the collapsed state */
  transition: margin-bottom 0.22s ease;
}

.help-article-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 28px;
  /* max-height + opacity so the meta row can slide away smoothly when the
     header collapses. 48px generously accommodates a single-wrap badges row;
     set higher if any row wraps more often. */
  max-height: 48px;
  overflow: hidden;
  transition: max-height 0.22s ease, opacity 0.18s ease, margin 0.22s ease;
}

/* COLLAPSED state — triggered when the user scrolls the body past 40px.
   the title stays fully visible; meta + summary retract to 0 height and
   fade out, and the header itself tightens its padding to give the body
   more room. reverse happens when the user scrolls back to the top. */
.help-article-header--collapsed {
  padding-top: 10px;
  padding-bottom: 6px;
  margin-bottom: 8px;
}
.help-article-header--collapsed .help-article-title {
  margin-bottom: 0;
}
.help-article-header--collapsed .help-article-meta,
.help-article-header--collapsed .help-article-summary {
  max-height: 0;
  opacity: 0;
  margin: 0;
  min-height: 0;
  pointer-events: none;
}

.help-article-date {
  font-size: 11px;
  color: #888;
}

/* view count in the article detail header. mirrors the styling of
   .help-item-views on list items so the two surfaces share the same
   visual language. */
.help-article-views {
  font-size: 11px;
  color: #9098a1;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.help-article-views .fa {
  font-size: 10px;
}

.help-article-summary {
  margin: 6px 0 0;
  font-size: 13px;
  color: #555;
  font-style: italic;
  line-height: 1.4;
  /* matches .help-article-meta transitions so both retract together when
     the header collapses. generous max-height caps a ~4-line summary;
     most summaries are 1-2 lines and sit well under this. */
  max-height: 120px;
  overflow: hidden;
  transition: max-height 0.22s ease, opacity 0.18s ease, margin 0.22s ease;
}

.help-article-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 16px;
}

.help-content {
  white-space: normal;
  line-height: 1.6;
  font-size: 14px;
  color: #333;
}

.help-feedback {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 13px;
  color: #666;
}

.help-feedback-comment {
  margin-top: 10px;
  font-size: 13px;
  color: #666;
}

.help-feedback-comment-label {
  display: block;
  margin-bottom: 4px;
}

.help-feedback-comment-input {
  max-width: 480px;
  margin-bottom: 6px;
}

.help-feedback-comment-done {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
  font-style: italic;
}

.help-feedback-admin {
  margin-top: 10px;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
}

.help-feedback-summary {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fb-stat {
  font-weight: 600;
}

.fb-stat.yes {
  color: #28a745;
}

.fb-stat.no {
  color: #dc3545;
}

.fb-stat.pct {
  color: #555;
  font-weight: 400;
}

.fb-toggle {
  font-size: 11px;
  color: #007bff;
  margin-left: auto;
}

.help-feedback-detail {
  margin-top: 8px;
}

.fb-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.fb-detail-table th,
.fb-detail-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #e9ecef;
  text-align: left;
}

.fb-detail-table th {
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #dee2e6;
}

.fb-comment-cell {
  max-width: 240px;
  word-break: break-word;
}

.fb-vote-yes {
  color: #28a745;
  font-weight: 600;
}

.fb-vote-no {
  color: #dc3545;
  font-weight: 600;
}

.help-search-indicator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  margin-bottom: 6px;
  background: #e8f0fe;
  border: 1px solid #b8d4f0;
  border-radius: 5px;
  font-size: 12px;
  color: #1a5ea8;
}

.help-search-clear {
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 1;
  color: #1a5ea8;
  cursor: pointer;
  padding: 0 2px;
  font-weight: 600;
}

.help-search-clear:hover {
  color: #c00;
}

.help-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  color: #999;
  font-size: 14px;
}
/* wide variant for the rich no-results layout with two action paths */
.help-empty-state-wide {
  padding: 40px 24px;
  align-items: flex-start;
}

/* --- no-results pane: header + FA pill + topic suggestion form --- */
.help-no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 520px;
  margin: 0 auto;
  text-align: center;
  color: #333;
}
.help-no-results p {
  margin: 0;
}

/* header: search icon + headline + warm subhead */
.help-no-results-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.help-no-results-icon {
  font-size: 28px;
  color: #bbb;
  margin-bottom: 4px;
}
.help-no-results-title {
  font-size: 18px;
  font-weight: 500;
  color: #444;
}
.help-no-results-query {
  color: #1a5ea8;
  font-weight: 600;
}
.help-no-results-sub {
  font-size: 15px;
  color: #666;
}

/* primary path: Ask Fleet Assistant */
.help-try-fa {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
}
/* ghost pill — same visual family as .help-action-pill (topic-chip row below)
   so the empty-state reads as one coherent set of actions rather than a
   heavy primary CTA over a row of lighter secondaries. slightly more opaque
   background + darker border than .help-action-pill plus the bolder
   font-weight give it enough prominence to still read as the hero of the
   panel. */
.help-try-fa-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1px solid rgba(0, 122, 199, 0.45);
  background: rgba(0, 122, 199, 0.1);
  color: #007ac7;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.08s ease;
}
.help-try-fa-pill:hover {
  background: rgba(0, 122, 199, 0.18);
  border-color: rgba(0, 122, 199, 0.65);
}
.help-try-fa-pill:active {
  transform: translateY(1px);
}
.help-try-fa-pill .fa {
  font-size: 16px;
}
/* custom chat-bubble + typing-dots SVG used in place of fa-comment. vertical-
   align shifts the SVG down so the body rectangle's top/bottom align with the
   text cap-top/baseline and the tail dangles below the baseline. */
.help-try-fa-pill .fa-chat-svg {
  width: 22px;
  height: 22px;
  vertical-align: -0.5em;
  flex-shrink: 0;
}
.help-try-fa-hint {
  font-size: 12px;
  color: #888;
  max-width: 380px;
}

/* suggest-topic panel — pre-submission variant of the Uploads/FA Paused
   callout pattern. amber tones to fit the lightbulb/idea metaphor; same
   shape language (outer rounded-square anchor icon + uppercase badge +
   copy/form below) so suggest/submit/paused all read as one family. */
.suggest-topic-panel {
  width: 100%;
  max-width: 520px;
  margin-top: 8px;
  padding: 14px 18px;
  background: #fdf6e3;
  border: 1px solid #f0e4c2;
  border-radius: 8px;
  color: #6b5a1e;
  /* parent .help-no-results centers everything; the callout layout is
     icon+badge+copy+form and reads better flush-left. */
  text-align: left;
}
.suggest-topic-panel .callout-anchor-icon {
  color: #b58100;
}
.suggest-topic-panel .callout-body p {
  margin: 0 0 10px 0;
  font-size: 14px;
  line-height: 1.45;
}
.suggest-topic-panel .fa-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.suggest-topic-panel .fa-badge.idea {
  --badge-color: #b58100;
  --badge-bg: #fff4d1;
  color: #7a5400;
}
.suggest-topic-panel .help-suggest-textarea {
  margin-bottom: 10px;
}
.suggest-topic-action {
  margin-top: 6px;
}
.suggest-topic-action .callout-cta-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.help-suggest-textarea {
  width: 100%;
  /* iOS Safari auto-zooms any focused input / textarea whose font-size is
     less than 16px (an accessibility heuristic assuming the text is too
     small to read). bumping to 16px keeps the viewport steady when the
     user taps into this field on mobile. since this is a primary input
     (users actively type and read it) the larger size works on desktop
     too — no media query needed. */
  font-size: 16px;
}
/* suggestion-submitted callout — success-toned variant of the Uploads/FA Paused
   pattern: outer rounded-square anchor icon + uppercase badge on the top row,
   copy below. green palette so it reads as positive feedback rather than a
   warning. keeps the same shape language as the paused callouts. */
.suggestion-submitted-alert {
  max-width: 520px;
  padding: 14px 18px;
  background: #edf7ee;
  border: 1px solid #bde0c0;
  border-radius: 8px;
  color: #1f5727;
}
.suggestion-submitted-alert .callout-anchor-icon {
  color: #2f8a3d;
}
.suggestion-submitted-alert .callout-body p {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
}
/* local copy of the fa-badge pattern so the success badge gets the same
   chrome as the amber "Uploads Paused" / "Fleet Assistant Paused" badges. */
.suggestion-submitted-alert .fa-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.suggestion-submitted-alert .fa-badge.success {
  --badge-color: #2f8a3d;
  --badge-bg: #dcefdf;
  color: #1f5727;
}

.help-empty-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.help-action-pill {
  font-size: 14px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(0, 122, 199, 0.35);
  background: rgba(0, 122, 199, 0.08);
  color: #007ac7;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.help-action-pill:hover {
  background: rgba(0, 122, 199, 0.14);
  border-color: rgba(0, 122, 199, 0.5);
}

.help-highlight-flash {
  animation: help-flash 0.8s ease;
}

@keyframes help-flash {
  0%   { box-shadow: 0 0 0 4px rgba(255, 0, 0, 0.45); }
  16%  { box-shadow: 0 0 0 4px rgba(255, 165, 0, 0.45); }
  33%  { box-shadow: 0 0 0 4px rgba(255, 220, 0, 0.45); }
  50%  { box-shadow: 0 0 0 4px rgba(0, 180, 0, 0.45); }
  66%  { box-shadow: 0 0 0 4px rgba(0, 100, 255, 0.45); }
  83%  { box-shadow: 0 0 0 4px rgba(140, 0, 255, 0.45); }
  100% { box-shadow: 0 0 0 0 rgba(140, 0, 255, 0); }
}

.company-doc-header {
  margin-bottom: 16px;
  padding: 16px 0 12px;
  border-bottom: 1px solid #eee;
}

.company-doc-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.company-doc-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
}

.company-doc-download-btn {
  font-size: 12px;
  flex-shrink: 0;
}

.company-doc-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 28px;
}

.company-doc-date {
  font-size: 11px;
  color: #888;
}

.company-doc-purpose {
  margin-top: 6px;
  font-size: 13px;
  color: #555;
  font-style: italic;
}

.company-doc-text {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  color: #333;
  max-height: none;
  overflow: visible;
}

.help-directory-group {
  margin-bottom: 4px;
}

.help-directory-header {
  display: flex;
  align-items: center;
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5a6268;
  cursor: pointer;
  user-select: none;
}

.help-directory-header:hover {
  color: #1a5ea8;
}

.help-directory-header .fa {
  margin-right: 6px;
  font-size: 10px;
  width: 10px;
  text-align: center;
}

.help-directory-count {
  margin-left: auto;
  font-weight: 400;
  color: #999;
  font-size: 10px;
}

.help-manage-docs-btn {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  margin-left: auto;
}

.help-empty-welcome {
  text-align: center;
  max-width: 360px;
}

.help-empty-welcome-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 14px;
  line-height: 1.5;
}

.help-empty-actions {
  flex-wrap: wrap;
  justify-content: center;
}

/* admin toolbar */
/* .help-admin-toolbar removed — replaced by .help-toolbar */

/* feedback review tab body */
.help-feedback-review-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  position: relative;
}

.fb-review-subtabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.fb-review-filters {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.fb-filter-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fb-filter-group label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 0;
}

.fb-filter-group select,
.fb-filter-group input {
  min-width: 0;
}

.fb-review-stats {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f0f4f8;
  border-radius: 6px;
  font-size: 13px;
}

.fb-review-stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.fb-review-stat.positive {
  color: #28a745;
}

.fb-review-stat.negative {
  color: #b58100;
}

.fb-review-stat.concern {
  color: #dc3545;
}

.fb-review-table-wrap {
  margin-top: 8px;
}

/* .fb-review-table is the wrapper div (b-table responsive=true produces a
   wrapper). header / row / VIP-bar styles targeting the inner table live in
   the unscoped style block below so they can reach BV-rendered cells that
   don't carry this component's scoped data-v attribute. the scoped span
   styles for .stl-dt stay here since that span is declared in our template
   and does receive the data-v attribute. */
.fb-review-table .stl-dt {
  color: #555;
  white-space: nowrap;
  cursor: help;
}

.fb-context-preview {
  color: #555;
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.fb-comment-preview {
  color: #555;
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* tag chips for the feedback-review table. same pill geometry and color
   family as the priority tags on support_ticket_list so rating, priority,
   and any future tag-style column all read as one visual system.
   `.fb-tag` is the shared base; the -success / -danger / -warning / -muted
   modifier classes pick the pastel+border palette. */
.fb-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  border-radius: 4px;
  border: 1px solid;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.fb-tag .fa {
  font-size: 12px;
  width: 14px;
  text-align: center;
}
.fb-tag-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1;
}
.fb-tag-success {
  background: #edf7ee;
  border-color: #3a8144;
  color: #1f5727;
}
.fb-tag-danger {
  background: #fbe3e5;
  border-color: #dc3545;
  color: #dc3545;
}
.fb-tag-warning {
  background: #fff4d1;
  border-color: #b58100;
  color: #7a5400;
}
.fb-tag-muted {
  background: #f3f4f6;
  border-color: #c9ced6;
  color: #6b7280;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.fb-action-btns {
  display: flex;
  gap: 4px;
  white-space: nowrap;
  align-items: center;
}
.fb-trash-btn .fa {
  font-size: 14px;
  line-height: 1;
}

/* "Handled By" column: compact first-name pill — hover reveals full attribution */
.fb-handled-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 110px;
  height: 22px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: #6c757d;
  border-radius: 11px;
  cursor: help;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fb-handled-empty {
  color: #bbb;
  font-size: 13px;
}

/* soft-deleted marker: tiny pill next to status when Show deleted is on */
.fb-deleted-tag {
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #a94442;
  background: #fbeceb;
  border: 1px solid #e5b5b1;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.fb-review-pagination {
  margin-top: 8px;
  display: flex;
  justify-content: center;
}

/* conversation viewer */
.fb-conversation-viewer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.fb-chat-bubble-wrap {
  display: flex;
  flex-direction: column;
}

.fb-chat-bubble {
  padding: 10px 14px;
  max-width: 80%;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.fb-chat-meta {
  font-size: 11px;
  color: #999;
  margin-top: 3px;
  padding: 0 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fb-tool-calls {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.fb-tool-call-pill {
  font-size: 10px;
}

/* mobile overrides — must be after all base rules to win same-specificity cascade */
@media (max-width: 900px) {
  /* morph manage documents to gear icon and collapse spacer */
  .help-toolbar-spacer {
    display: none;
  }
  .help-toolbar .help-search-fields {
    flex: 1;
  }
  .help-toolbar .help-search-fields .form-control {
    flex: 1;
    width: auto;
  }
  .help-manage-docs-btn {
    padding: 4px 8px !important;
    flex-shrink: 0;
  }
  .help-manage-docs-btn .manage-label-full {
    display: none !important;
  }
  .help-manage-docs-btn .manage-icon-only {
    display: inline !important;
    font-size: 16px;
  }
  .help-leaderboard-btn {
    padding: 4px 8px !important;
    flex-shrink: 0;
  }
  .help-leaderboard-btn .leaderboard-label-full {
    display: none !important;
  }
  .help-leaderboard-btn .leaderboard-icon-only {
    display: inline !important;
    font-size: 16px;
  }
}

/* mobile layout — driven by .help-mobile class (JS window.innerWidth) */
.help-mobile .help-toolbar {
  flex-wrap: nowrap;
}
.help-mobile .help-toolbar-spacer {
  display: none;
}
.help-mobile .help-toolbar .help-search-fields {
  flex: 1;
}
.help-mobile .help-manage-docs-btn {
  margin-left: 0;
  padding: 4px 8px;
  flex-shrink: 0;
}
.help-mobile .help-manage-docs-btn .manage-label-full {
  display: none;
}
.help-mobile .help-manage-docs-btn .manage-icon-only {
  display: inline !important;
  font-size: 16px;
}
.help-mobile .help-body {
  flex-direction: column;
}
.help-mobile .help-divider {
  display: none;
}
.help-mobile .help-sidebar {
  width: 100% !important;
  max-width: none;
  flex-shrink: 1;
  border-right: none;
  border-bottom: 1px solid #dce1e6;
}
.help-mobile .help-content-pane {
  width: 100%;
  display: none;
}
.help-mobile .help-mobile-back {
  display: flex;
}
/* list/detail swap */
.help-mobile .help-body.mobile-show-detail .help-sidebar {
  display: none;
}
.help-mobile .help-body.mobile-show-detail .help-content-pane {
  display: flex;
}
/* prevent iOS auto-zoom on focus */
.help-mobile .help-search-fields .form-control {
  font-size: 16px;
}

/* uploads-disabled banner shown inside Manage Documents when tenant is over AI budget */
.uploads-disabled-alert {
  font-size: 13px;
  /* softer, more pastel background than the default bootstrap warning yellow */
  background-color: #fdf6e3;
  border-color: #f0e4c2;
  color: #6b5a1e;
}
.uploads-disabled-header {
  margin-bottom: 8px;
}
.uploads-disabled-alert p {
  margin: 0 0 10px 0;
}
.uploads-disabled-alert p:last-child {
  margin-bottom: 10px;
}
.uploads-disabled-action {
  margin-top: 6px;
}

/* callout layout: profile-card style.
   row 1: anchor icon + badge on the same line, vertically centered.
   row 2: body copy and actions, full width, left-aligned with the icon start. */
.callout-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.callout-anchor-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  color: #b58100;
}
.callout-body {
  /* full width, no indent — content starts at the icon's left edge */
}

/* callout CTA button — dark-amber fill on cream panel, echoes the badge text color.
   ~6.7:1 contrast on white text, WCAG AA/AAA pass. */
.callout-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #7a5400;
  color: #fff;
  border: 1px solid #7a5400;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
}
.callout-cta-btn:hover {
  background: #5f4100;
  border-color: #5f4100;
}
.callout-cta-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(122, 84, 0, 0.25);
}
.callout-cta-btn:active {
  background: #4a3200;
  border-color: #4a3200;
}

/* fa-badge pattern (matches fleet_assistant.vue) — local copy for the
   uploads-disabled banner inside Manage Documents */
.uploads-disabled-alert .fa-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.uploads-disabled-alert .fa-badge.disabled {
  --badge-color: #b58100;
  --badge-bg: #fff4d1;
  color: #7a5400;
}
.uploads-disabled-alert .fa-badge .fa {
  font-size: 11px;
}
/* inline-SVG icon (parking P variants) used inside the paused badge */
.callout-paused-icon {
  width: 1em;
  height: 1em;
  display: inline-block;
  vertical-align: -0.15em;
}
</style>

<style>
/* Feedback Review table: headers, sort indicators, compact cell padding, and
   the VIP left-edge bar. these live unscoped because the inner <table> that
   b-table renders doesn't carry this component's data-v attribute, so scoped
   selectors never reach the cells.
   descendant combinator `.fb-review-table .b-table` is deliberate: the responsive
   b-table wraps the <table> in a <div class="fb-review-table table-responsive">. */
.fb-review-table .b-table {
  font-size: 13px;
  border-collapse: collapse;
}
.fb-review-table .b-table th,
.fb-review-table .b-table td {
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 10px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.fb-review-table .b-table thead th {
  background: #f8f9fa;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}

/* sort arrows — same glyphs as the support ticket list so the two tables read
   as siblings when an admin flips between tabs. */
.fb-review-table .b-table th[aria-sort] {
  background-image: none !important;
  padding-right: 0.75rem !important;
  white-space: nowrap;
}
.fb-review-table .b-table th[aria-sort]::before {
  display: none !important;
}
.fb-review-table .b-table th[aria-sort]::after {
  display: inline-block !important;
  margin-left: 4px;
  font-size: 10px;
  color: #999;
  vertical-align: middle;
  float: none !important;
  width: auto !important;
  height: auto !important;
  opacity: 1 !important;
}
.fb-review-table .b-table th[aria-sort="none"]::after {
  content: "\2195" !important;
}
.fb-review-table .b-table th[aria-sort="ascending"]::after {
  content: "\25B2" !important;
  color: steelblue;
}
.fb-review-table .b-table th[aria-sort="descending"]::after {
  content: "\25BC" !important;
  color: steelblue;
}

/* VIP signal: thin gold bar on the row's leftmost cell, identical to the
   support ticket list. no "done" variant — feedback has no completion state
   equivalent to the ticket flow. */
.fb-review-table .b-table tbody tr.stl-row-vip td:first-child {
  position: relative;
}
.fb-review-table .b-table tbody tr.stl-row-vip td:first-child::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #d4a017;
  box-sizing: border-box;
}

.help-content h1,
.help-content h2,
.help-content h3,
.help-content h4,
.help-content h5,
.help-content h6 {
  margin: 14px 0 6px;
  line-height: 1.3;
  font-weight: 600;
  color: #1a1a1a;
}

.help-content h1 {
  font-size: 1.3em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}

.help-content h2 {
  font-size: 1.15em;
}

.help-content h3 {
  font-size: 1.05em;
}

.help-content h4,
.help-content h5,
.help-content h6 {
  font-size: 1em;
}

.help-content p {
  margin: 0 0 8px;
}

.help-content ol,
.help-content ul {
  margin: 6px 0;
  padding-left: 22px;
}

.help-content li {
  margin: 3px 0;
}

.help-content code {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.9em;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  padding: 1px 5px;
}

.help-content pre {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.45;
  background: #f6f8fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  overflow-x: auto;
  margin: 8px 0;
}

.help-content pre code {
  background: transparent;
  border: none;
  padding: 0;
}

.help-content table.markdown-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  font-size: 13px;
}

.help-content table.markdown-table th,
.help-content table.markdown-table td {
  border: 1px solid #dce1e6;
  padding: 7px 10px;
  vertical-align: top;
}

.help-content table.markdown-table th {
  background: #f5f7f9;
  font-weight: 600;
  color: #333;
}

.help-content table.markdown-table tr:nth-child(even) {
  background: #fafbfc;
}

.help-content strong {
  font-weight: 600;
}

.help-content img.md-inline-img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  vertical-align: middle;
}

/* standalone images (only child of a paragraph) render as block */
.help-content p > img.md-inline-img:only-child {
  display: block;
  margin: 8px 0;
}

/* document management modal styles (unscoped because b-modal renders outside component root) */
.company-docs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.company-docs-table th,
.company-docs-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.company-docs-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
}

.company-docs-table .doc-name {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-docs-table .doc-uploader {
  color: #666;
  font-size: 13px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-docs-table .doc-date {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.company-docs-table .doc-size {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.company-docs-table .doc-actions {
  text-align: right;
}

.company-docs-table .row-selected {
  background-color: #fff3cd;
}

.row-uploading {
  opacity: 0.7;
}

.row-failed {
  color: #b00020;
}

.row-deleting {
  opacity: 0.6;
  background-color: #fff5f5;
}

.doc-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
}

.doc-badge.upload {
  --badge-color: #28a745;
  --badge-bg: #e9f6ee;
  color: #155724;
}

.doc-badge.processing {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  --pulse-color: rgba(30, 126, 235, 0.35);
  color: #0b3d91;
}

.doc-badge.deleting {
  --badge-color: #dc3545;
  --badge-bg: #fde8ea;
  --pulse-color: rgba(220, 53, 69, 0.35);
  color: #721c24;
}

.doc-badge.failed {
  --badge-color: #b00020;
  --badge-bg: #fde8ea;
  color: #b00020;
}

.doc-badge.pulsing {
  animation: docBadgePulse 1.2s ease-in-out infinite;
}

@keyframes docBadgePulse {
  0% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
  50% {
    box-shadow: inset 0 0 6px 3px var(--pulse-color);
    opacity: 1;
  }
  100% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
}

.docs-summary {
  font-size: 13px;
  color: #555;
  padding: 6px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

/* leaderboard modal table */
.fb-leaderboard-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.fb-leaderboard-table th,
.fb-leaderboard-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.fb-leaderboard-table th {
  font-weight: 600;
  color: #555;
  font-size: 12px;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.fb-leaderboard-table a {
  color: #007bff;
  text-decoration: none;
}

.fb-leaderboard-table a:hover {
  text-decoration: underline;
}

.fb-pct-bad {
  color: #dc3545;
  font-weight: 600;
}

.fb-pct-warn {
  color: #e67e00;
  font-weight: 600;
}

.fb-pct-good {
  color: #28a745;
  font-weight: 600;
}

/* comment inbox modal table */
.fb-inbox-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.fb-inbox-table th,
.fb-inbox-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.fb-inbox-table th {
  font-weight: 600;
  color: #555;
  font-size: 12px;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.fb-inbox-table .row-reviewed {
  opacity: 0.6;
}

/* feedback status badges */
.fb-status-new {
  background: #fff3cd;
  color: #856404;
}

.fb-status-reviewed {
  background: #d4edda;
  color: #155724;
}

.fb-status-actioned {
  background: #cce5ff;
  color: #004085;
}

/* feedback detail modal */
.fb-detail-section {
  margin-bottom: 10px;
}

.fb-detail-section > label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #666;
  margin-bottom: 4px;
}

.fb-detail-box {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}

.fb-detail-meta-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #555;
  padding: 8px 0;
}

.fb-session-id {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 11px;
  cursor: pointer;
  padding: 1px 5px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
}

.fb-session-id:hover {
  background: #e5e7eb;
}

/* Assistant Insights (FI-1600) — flows with the page; no nested scroller
   so the long fleet table scrolls as one column with the rest of the
   page (matching .help-nc-body). The tables inside still scroll
   horizontally via b-table responsive when they overflow. */
.help-insights-body {
  padding: 16px 24px;
}
.fa-breadcrumb {
  font-size: 13px;
  margin-bottom: 12px;
  color: #555;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.fa-breadcrumb a {
  color: #007ac7;
  text-decoration: none;
}
.fa-breadcrumb a:hover { text-decoration: underline; }
.fa-bc-current {
  color: #333;
  font-weight: 600;
  cursor: default;
}
.fa-bc-current:hover { text-decoration: none; }
.fa-bc-sep {
  color: #bbb;
  margin: 0 6px;
}

/* month picker sits at the right edge of the breadcrumb row so the
   stats below answer to one persistent control. */
.fa-period-picker {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}
.fa-period-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}
.fa-period-select {
  width: auto;
  min-width: 180px;
}

/* FI-1432 Notification Center admin tab — flows with the page; no
   nested scroller so a long composer (editor, meta fields, stats)
   scrolls as one column. */
.help-nc-body {
  padding: 12px 16px 40px;
}

/* sub-tabs use the shared .help-tab look — same gap + spacing as
   feedback review so both admin pages feel identical. */
.nc-subtabs {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.nc-compose, .nc-stats {
  max-width: 900px;
}

.nc-field { margin-bottom: 14px; }
.nc-field > label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
}

/* email-style "To" row: the target radios + customer select sit side-by-
   side on one line, aligned like a mail client's To: header. the select
   reveals in place when "Specific customer" is chosen so the row expands
   without pushing the title/body around. */
.nc-to-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.nc-to-tenant-select {
  max-width: 320px;
  min-width: 220px;
}

/* inline help text that appears next to the Type radios when "Service
   alert" is selected — reminds admins what the flavor means without
   forcing them to the docs. amber to match the service-alert palette. */
.nc-kind-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  background: #fef3d5;
  color: #8a5a00;
  font-size: 12px;
  line-height: 1.3;
  border: 1px solid rgba(240, 169, 87, 0.4);
}

.nc-kind-hint .fa { font-size: 11px; }

.nc-kind-hint--resolved {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.nc-banner-hint {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.3;
  flex: 1 1 0;
  min-width: 200px;
}

/* icon picker — collapsed by default to a chip + label + change link.
   expanding shows a categorized grid of tiles. tile look mirrors the
   inverted-outline puck family (pastel fill, colored border, colored
   icon) so the picked glyph reads exactly the same as it will at
   runtime. picker grid colors flip purple↔amber as the kind toggles
   so the chosen icon previews against the puck color it'll actually
   render with. */
.nc-icon-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 0;
  min-width: 0;
}

.nc-icon-current-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nc-icon-current {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 2px solid;
  background: #f3f0ff;
  border-color: #d8cffb;
  color: #7c5dd0;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
  padding: 0;
}

.nc-icon-current:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(124, 93, 208, 0.18), 0 1px 2px rgba(124, 93, 208, 0.10);
}

.nc-icon-current--alert {
  background: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
}

.nc-icon-current--alert:hover {
  box-shadow: 0 2px 5px rgba(217, 119, 6, 0.18), 0 1px 2px rgba(217, 119, 6, 0.10);
}

.nc-icon-current--resolved {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

.nc-icon-current--resolved:hover {
  box-shadow: 0 2px 5px rgba(5, 150, 105, 0.18), 0 1px 2px rgba(5, 150, 105, 0.10);
}

.nc-icon-current .fa,
.nc-icon-current .ni-svg { font-size: 16px; }

.nc-icon-current-meta {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 12px;
  font-size: 13px;
  color: #475569;
}

.nc-icon-current-name {
  font-weight: 600;
  color: #1f2937;
}

.nc-icon-toggle-link,
.nc-icon-reset-link {
  background: none;
  border: 0;
  padding: 0;
  font-size: 12px;
  color: steelblue;
  cursor: pointer;
}

.nc-icon-reset-link { color: #94a3b8; }
.nc-icon-toggle-link:hover,
.nc-icon-reset-link:hover { text-decoration: underline; }

/* "Browse all icons" — small ghost button in the chip-meta row that
   opens the icon explorer modal. visually understated since it's a
   design-tool affordance, not a primary action. */
.nc-icon-explore-link {
  background: none;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: border-color 120ms ease, color 120ms ease;
}
.nc-icon-explore-link:hover {
  border-color: steelblue;
  color: steelblue;
}
.nc-icon-explore-link .fa { font-size: 10px; }

/* ICON EXPLORER MODAL — admin design tool. wide 56px tiles in a
   responsive grid with a sticky-feeling controls row at the top.
   color preview is driven by inline :style on each tile so swapping
   the palette buttons re-tints the whole grid instantly. */
/* sticky wrapper pins the intro + controls to the top of the modal-body
   so the search field and palette swatches stay reachable while the
   admin scrolls through 700+ tiles. negative top/x margins counter the
   modal-body's 1rem padding so the sticky covers the full edge — top: 0
   then pins flush to the header without a white gap during scroll.
   matching positive padding inside re-creates the original breathing
   room. */
.icon-explorer-sticky {
  position: sticky;
  top: -16px;  /* counters modal-body's 1rem padding-top so the sticky
                  pins flush to the header edge */
  z-index: 5;
  background: #fff;
  padding: 12px 16px 12px;
  margin: -16px -16px 12px;
  border-bottom: 1px solid #f1f5f9;
  box-shadow: 0 6px 8px -8px rgba(0, 0, 0, 0.10);
}

.icon-explorer-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  margin-bottom: 0;
}
.icon-explorer-search {
  max-width: 260px;
}
.icon-explorer-color-picker {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.icon-explorer-color-label {
  font-size: 12px;
  color: #6c757d;
}
.icon-explorer-color-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1.5px solid;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 120ms ease;
}
.icon-explorer-color-btn:hover { transform: translateY(-1px); }
.icon-explorer-color-btn.active {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
}
.icon-explorer-color-btn .fa { font-size: 8px; }
.icon-explorer-custom-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
}
.icon-explorer-color-input {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  padding: 0;
  background: none;
}

/* selected-icon inspector — pinned above the grid when an icon is
   clicked. shows the icon at BOTH puck sizes (detail + list) so admins
   can evaluate how a glyph reads at each, plus class name, allowlist
   badge, and copy/use actions. */
.icon-explorer-selected {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.icon-explorer-selected-pucks {
  display: inline-flex;
  align-items: flex-end;
  gap: 14px;
  flex-shrink: 0;
}
.icon-explorer-puck-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.icon-explorer-puck-size-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  font-weight: 600;
}
.icon-explorer-selected-meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1 1 0;
  min-width: 0;
}
.icon-explorer-selected-name {
  background: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  color: #1f2937;
}
.icon-explorer-allow-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.icon-explorer-allow-badge--yes {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.icon-explorer-allow-badge--no {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}
.icon-explorer-selected-actions {
  display: inline-flex;
  gap: 6px;
  flex-shrink: 0;
}

/* the grid itself — each tile is a transparent click target wrapping
   an inverted-outline puck that matches the existing puck family at
   detail-page size. browsing at the bigger size makes glyphs readable
   without zooming and aligns the explorer with what'll actually ship. */
.icon-explorer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 8px;
}
.icon-explorer-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1.5px solid transparent;
  border-radius: 14px;
  padding: 4px;
  cursor: pointer;
  transition: transform 0.12s ease, border-color 0.12s ease;
}
.icon-explorer-tile:hover .icon-explorer-puck--detail {
  transform: translateY(-1px);
}
.icon-explorer-tile.active {
  border-color: rgba(70, 120, 200, 0.50);
  background: rgba(70, 120, 200, 0.06);
}
/* allowlist tiles get a subtle dot in the bottom-right corner so the
   admin can see at a glance which tiles will actually adopt-on-click
   vs. inspect-only. */
.icon-explorer-tile--allowed {
  position: relative;
}
.icon-explorer-tile--allowed::after {
  content: '';
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1f2937;
  opacity: 0.55;
}

/* shared puck shape for the explorer — mirrors announcement_detail.vue's
   .announcement-puck and notifications_list.vue's .nl-item-puck so
   admins evaluating an icon see exactly the chip it'll render as. */
.icon-explorer-puck {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 2px solid;
  line-height: 1;
  box-sizing: border-box;
  transition: transform 0.12s ease;
}
.icon-explorer-puck--detail {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border-width: 2px;
}
.icon-explorer-puck--detail .fa,
.icon-explorer-puck--detail .ni-svg { font-size: 20px; }

.icon-explorer-puck--list {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border-width: 1.5px;
  box-shadow: none !important;  /* override the inline halo so the small
                                    puck stays flat — matches the dropdown
                                    + list page treatment. */
}
.icon-explorer-puck--list .fa,
.icon-explorer-puck--list .ni-svg { font-size: 14px; }

.icon-explorer-empty {
  text-align: center;
  padding: 30px;
  color: #94a3b8;
  font-size: 13px;
}
.icon-explorer-footer {
  text-align: right;
}

.nc-icon-picker {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px 14px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
}

.nc-icon-group-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}

.nc-icon-group-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #475569;
}

.nc-icon-group-hint {
  font-size: 11px;
  color: #94a3b8;
}

.nc-icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
}

.nc-icon-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 6px 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease;
}

.nc-icon-tile:hover {
  border-color: #c8c1ee;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.nc-icon-tile--alert:hover {
  border-color: #fde68a;
}

.nc-icon-tile--resolved:hover {
  border-color: #a7f3d0;
}

.nc-icon-tile--neutral:hover {
  border-color: #cbd5e1;
}

.nc-icon-tile--active {
  border-color: #7c5dd0;
  background: #f7f4ff;
  box-shadow: 0 0 0 2px rgba(124, 93, 208, 0.12);
}

.nc-icon-tile--active.nc-icon-tile--alert {
  border-color: #d97706;
  background: #fff8e8;
  box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.12);
}

.nc-icon-tile--active.nc-icon-tile--resolved {
  border-color: #059669;
  background: #ecfdf5;
  box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.12);
}

.nc-icon-tile--active.nc-icon-tile--neutral {
  border-color: #475569;
  background: #f1f5f9;
  box-shadow: 0 0 0 2px rgba(71, 85, 105, 0.12);
}

.nc-icon-tile-puck {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1.5px solid #d8cffb;
  background: #f3f0ff;
  color: #7c5dd0;
}

.nc-icon-tile--alert .nc-icon-tile-puck {
  border-color: #fde68a;
  background: #fef3c7;
  color: #d97706;
}

.nc-icon-tile--resolved .nc-icon-tile-puck {
  border-color: #a7f3d0;
  background: #ecfdf5;
  color: #059669;
}

/* neutral / integrations palette — slate. signals that this glyph
   describes WHAT the notification is about (which provider) rather
   than what state it's in. the actual rendered color comes from the
   chosen kind, so the chip up top reflects the real outcome. */
.nc-icon-tile--neutral .nc-icon-tile-puck {
  border-color: #cbd5e1;
  background: #f1f5f9;
  color: #475569;
}

.nc-icon-tile-puck .fa,
.nc-icon-tile-puck .ni-svg { font-size: 15px; }

.nc-icon-tile-label {
  font-size: 11px;
  color: #475569;
  line-height: 1.2;
  text-align: center;
}

/* compose sections — three soft-bordered cards (Audience, Settings,
   Content) with a numbered eyebrow, title, and a one-line hint at the
   top. fields inside continue to use the existing .nc-field grid so
   labels line up the same way they did pre-grouping. */
.nc-compose-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px 18px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.nc-compose-section + .nc-compose-section { margin-top: 14px; }
.nc-compose-section-header {
  margin: 0 0 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}
.nc-compose-section-eyebrow {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #6b7280;
}
.nc-compose-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 2px 0 4px;
  letter-spacing: -0.1px;
}
.nc-compose-section-hint {
  font-size: 12.5px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

/* banner-expiry stack: preset chips on top, datetime-local input
   underneath when a custom time is chosen. lives in the same column
   width as the other inline composer fields so the labels stay aligned. */
.nc-banner-expires-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}
.nc-banner-expires-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
/* preset chip: muted neutral, fills with FI-blue when active. matches
   the role-chip palette used elsewhere in the composer so the form
   reads as a single visual family. */
.nc-banner-expires-preset {
  background: #fff;
  border: 1px solid #d6dde6;
  color: #495057;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.nc-banner-expires-preset:hover {
  background: #f6f9fc;
  border-color: #b8c9da;
  color: #2c3e50;
}
.nc-banner-expires-preset.active {
  background: #1976d2;
  border-color: #1976d2;
  color: #fff;
}
.nc-banner-expires-preset.active:hover {
  background: #1565c0;
  border-color: #1565c0;
}
/* "Clear" reads destructive — give it a distinct neutral-muted look so
   it doesn't compete with the active-state primary fill. */
.nc-banner-expires-preset.nc-banner-expires-clear {
  background: transparent;
  border-style: dashed;
  color: #6c757d;
}
.nc-banner-expires-preset.nc-banner-expires-clear:hover {
  background: #f6f7f9;
  border-color: #adb5bd;
  color: #344;
}

.nc-banner-expires-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* datetime-local input — matches the size/density of the other inline
   composer inputs (b-form-select, b-form-input size="sm"). max-width
   keeps the picker compact next to the relative-time chip. */
.nc-banner-expires-input {
  max-width: 220px;
  min-width: 180px;
}

/* live "in 3h 45m" companion text. amber when the picked time is in
   the past — that's an invalid value the backend would reject; surface
   it before the admin hits Save. */
.nc-banner-expires-relative {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}
.nc-banner-expires-relative:has(*) {  /* harmless future-proof; CSS not strictly needed */ }

/* title cell stack: kind puck + title + (optional) banner-state sub-line.
   gives service_alert rows visual identity without spending a column. */
.nc-title-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.nc-title-row {
  display: flex;
  /* top-align the kind puck so it sits flush with the first line of a
     wrapped title rather than floating in the vertical middle of a
     2- or 3-line cell. */
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}
/* compact 22px kind puck inline with the title. inverted-outline style
   matches the larger pucks on /announcements/:id (service_alert = amber
   pastel, announcement = purple pastel), kept small here so the title
   stays the dominant visual in the row. */
.nc-title-puck {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1.5px solid transparent;
  font-size: 11px;
  line-height: 1;
}
.nc-title-puck-service-alert {
  background: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
}
.nc-title-puck-announcement {
  background: #f3f0ff;
  border-color: #ddd2f7;
  color: #7c5dd0;
}
.nc-title-puck-service-resolved {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

/* banner-state chip — small pill-shaped badge under the title. amber
   pastel for active banners, neutral slate once expired/cleared. align
   margin keeps it indented under the title (puck width + row gap)
   without inheriting the puck's flexbox seat. */
.nc-title-sub-wrap {
  margin-left: 30px;  /* puck width (22) + row gap (8) */
}
.nc-title-sub {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  line-height: 1.4;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid transparent;
}
.nc-title-sub .fa { font-size: 9.5px; }
.nc-banner-sub-badge-on {
  background: #fef3c7;
  border-color: #fde68a;
  color: #b45309;
}
.nc-banner-sub-badge-off {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #64748b;
}

/* "Roles" chips: toggleable pills that mirror the doc-badge family's
   muted pastel aesthetic. inactive = neutral outline; active = filled
   indigo to match other admin-scoped affordances (restore button,
   tenant pill, etc.). clicking toggles membership in the filter set. */
.nc-roles-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.nc-roles-hint {
  color: #8a8a8a;
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}

.nc-role-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 14px;
  border: 1px solid #cdd6e0;
  background: #fff;
  color: #4a5568;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}

.nc-role-chip .fa { font-size: 10px; color: #8a8a8a; }

.nc-role-chip:hover {
  border-color: #a0aec0;
  background: #f6f9fc;
}

.nc-role-chip.active {
  background: #eadfff;
  border-color: #7c5dd0;
  color: #2e1a5b;
}

.nc-role-chip.active .fa { color: #4b2c92; }

.nc-role-clear {
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
  border: 1px dashed #cdd6e0;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
}

.nc-role-clear:hover { color: #333; border-color: #a0aec0; }

/* Muggles shortcut: a preset-toggle button sibling to the chip set.
   inactive state is gray (not a selected preset); active state is
   dark purple so "Muggles is on" reads as a pressed toggle. clicking
   when inactive applies the preset (via ripple); clicking when
   active clears the whole selection. */
.nc-role-shortcut {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 14px;
  border: 1px solid #cdd6e0;
  background: #f1f3f5;
  color: #6c757d;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}

.nc-role-shortcut .fa { color: #8a8a8a; }

.nc-role-shortcut:hover {
  border-color: #a0aec0;
  background: #e9ecef;
  color: #495057;
}

.nc-role-shortcut:hover .fa { color: #6c757d; }

/* active = preset applied. deep purple so it reads as "pressed"
   and matches the chip-active visual language. */
.nc-role-shortcut.active {
  background: #5a3fa6;
  border-color: #4b2c92;
  color: #fff;
}

.nc-role-shortcut.active .fa { color: #fff; }

.nc-role-shortcut.active:hover {
  background: #4b2c92;
  border-color: #3a2275;
  color: #fff;
}

.nc-role-shortcut:disabled {
  opacity: 0.7;
  cursor: wait;
}

.nc-role-shortcut .fa { font-size: 11px; color: #7c5dd0; }

/* shortcut-mode pulse: a quick scale + ring-halo pop as each role
   chip flips active during the Muggles ripple. 260ms matches the
   pulse removal timer in selectMugglesRoles. */
@keyframes nc-role-chip-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(124, 93, 208, 0.55);
  }
  45% {
    transform: scale(1.09);
    box-shadow: 0 0 0 6px rgba(124, 93, 208, 0.18);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(124, 93, 208, 0);
  }
}

.nc-role-chip-pulsing {
  animation: nc-role-chip-pulse 260ms ease-out;
  z-index: 1;
}

.nc-editor-wrap {
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
}

/* body-label row: label sits at the left, format toggle (markdown vs html)
   hugs the right so it reads as a property of the Body field. keeps the
   toggle compact so it doesn't compete with the main actions row below. */
.nc-body-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  gap: 12px;
  flex-wrap: wrap;
}
.nc-format-toggle .btn {
  font-size: 11px;
  padding: 2px 10px;
}

/* html-mode body field: raw textarea with a monospace font so authors
   can eyeball tag structure. pairs with a hint line about the iframe
   isolation so the author knows their CSS won't leak. */
.nc-html-wrap {
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
}
.nc-html-source-input {
  width: 100%;
  min-height: 360px;
  padding: 12px;
  border: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #1d1d1f;
  background: #fafafa;
  resize: vertical;
  outline: none;
}
.nc-html-source-input:focus {
  background: #fff;
  box-shadow: inset 0 0 0 2px rgba(0, 122, 199, 0.15);
}
.nc-html-hint {
  font-size: 11.5px;
  color: #6c757d;
  padding: 6px 10px;
  background: #f3f5f8;
  border-top: 1px solid #e6e8eb;
}
.nc-html-hint .fa {
  margin-right: 4px;
  color: #8a8f97;
}

/* iframes for html-format announcements — both the preview modal and
   the read-only detail pane. width fills the container; height is set
   imperatively by _resizeAnnouncementIframe on load so the frame sits
   flush with its content instead of introducing a nested scrollbar. */
.nc-preview-iframe,
.nc-detail-iframe {
  width: 100%;
  min-height: 200px;
  border: none;
  background: #fff;
  display: block;
}

/* toast ui's toolbar is wider than a mobile viewport. make it
   horizontally scrollable on small screens so the full set of tools
   stays reachable without breaking layout. */
.nc-editor-wrap .toastui-editor-toolbar {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* iOS Safari auto-zooms when a focused input's computed font-size is
   smaller than 16px. force 16px on NC text inputs so tapping title/
   editor doesn't jolt the viewport. */
.nc-compose input.form-control,
.nc-compose select.custom-select,
.nc-compose .toastui-editor-md-container .ProseMirror,
.nc-compose .toastui-editor-ww-container .ProseMirror {
  font-size: 16px;
}

/* Publish anchors the left edge as the clear primary action;
   Preview / View Markdown / Reset-or-Restore cluster on the right
   inside .nc-actions-secondary, which absorbs the slack via
   margin-left: auto so the group stays flush right and wraps as a
   unit on narrow viewports. */
.nc-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.nc-actions-secondary {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

/* the Restore button is the undo affordance that replaces Reset for a
   brief window after the user clears the form. muted indigo so it
   reads distinctly from the neutral outline-secondary siblings
   (Preview, View Markdown) but doesn't compete with the primary
   Publish action. */
.nc-restore-btn.btn.btn-outline-primary {
  color: #4b2c92;
  border-color: #b9a7e6;
  background: #f6f1ff;
}

.nc-restore-btn.btn.btn-outline-primary:hover,
.nc-restore-btn.btn.btn-outline-primary:focus {
  color: #2e1a5b;
  border-color: #7c5dd0;
  background: #eadfff;
  box-shadow: none;
}

.nc-restore-btn .fa { margin-right: 2px; }

.nc-stats-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 14px;
}

.nc-stat {
  font-size: 13px;
  color: #4a5568;
}

.nc-stat strong {
  font-size: 15px;
  color: #1a202c;
  margin-right: 3px;
}

/* dense canonical-pattern table (see standard_table_pattern.md):
   support_ticket_list reference values — 13px body, 12px uppercase
   muted #555 sticky header with inset-shadow divider (border-bottom
   collapses under sticky), 3px 8px padding, horizontal separators
   only, edge padding 12px on first/last cells, soft #f0f5ff hover.
   sortable headers + draft amber-rail row class kept — they're
   announcement-specific affordances on top of the base pattern. */
.nc-announcement-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  text-align: left;
}
.nc-announcement-table th,
.nc-announcement-table td {
  padding: 3px 8px;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  vertical-align: middle;
}
.nc-announcement-table th:first-child,
.nc-announcement-table td:first-child { padding-left: 12px; }
.nc-announcement-table th:last-child,
.nc-announcement-table td:last-child { padding-right: 12px; }
.nc-announcement-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  /* sticky positioning kills the bottom border; replace it with an
     inset shadow to match the rest of the canonical-pattern tables. */
  box-shadow: inset 0 -2px 0 0 #ccc;
  white-space: nowrap;
}

.nc-detail-body {
  font-size: 14px;
  line-height: 1.5;
}

.nc-detail-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.nc-roles-cell { color: #4b2c92; font-weight: 600; }
.nc-roles-cell-empty { color: #8a8a8a; font-style: italic; }

/* sortable column headers: cursor + hover hint, plus a steelblue arrow
   when active (matches the canonical table pattern). idle arrow stays
   light gray so it reads as available-but-inactive. */
.nc-th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: color 120ms ease;
}
.nc-th-sortable:hover { color: #1a202c; }
.nc-th-sortable .fa {
  margin-left: 4px;
  font-size: 10px;
  color: steelblue;
}
.nc-th-sort-idle { color: #cdd6e0; }

/* date cell: primary timestamp + an optional "draft" hint underneath
   so admins can tell at a glance which date semantics this row uses
   (drafts show created_at, published rows show published_at). */
.nc-date-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.nc-date-primary {
  font-variant-numeric: tabular-nums;
  color: #1f2937;
}
.nc-date-hint {
  font-size: 10.5px;
  color: #b8742e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 700;
  margin-top: 1px;
}

/* title cell — gentle italic + muted color when the row has no title
   (untitled draft / data-quality issue). doesn't truncate; long titles
   wrap inside the cell since the table is the page's main content. */
.nc-title-cell { color: #1f2937; font-weight: 500; word-break: break-word; }
.nc-title-untitled { color: #9aa3b2; font-style: italic; font-weight: 400; }

/* author column: small initial dot + name. dot picks up the page's
   environment color so production reads with a slate tint, dev with
   green, etc. — matches the navbar profile-dd-avatar visual family. */
.nc-author-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.nc-author-dot {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eef4fb;
  border: 1.5px solid #b8d0e4;
  color: var(--environment-color-background, steelblue);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1;
}
.nc-author-name {
  color: #334155;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

/* target cell: small icon (globe / building) inline with the label. */
.nc-target-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nc-target-cell .fa { color: #94a3b8; font-size: 11px; }

/* roles pill: chip style instead of dense colored text. matches the
   doc-badge family used on help articles — neutral by default, soft
   "everyone" variant for the everyone-in-target case. */
.nc-role-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1px;
  border-radius: 999px;
  background: #f3f0ff;
  color: #5a3fb0;
  border: 1px solid #ddd2f2;
  white-space: nowrap;
}
.nc-role-pill-everyone {
  background: #f1f5f9;
  color: #64748b;
  border-color: #cbd5e1;
}

/* skeleton row primitives — gradient shimmer over a flat pill/text
   placeholder. duration intentionally slow so it doesn't pulse too
   aggressively when many rows render. */
.nc-skeleton-row td { border-bottom-color: #f3f4f6; }
.nc-skel {
  display: inline-block;
  background: linear-gradient(90deg, #eef0f3 0%, #f6f8fb 50%, #eef0f3 100%);
  background-size: 240px 100%;
  animation: nc-skel-shimmer 1.4s infinite linear;
  border-radius: 6px;
  vertical-align: middle;
}
.nc-skel-pill { width: 64px; height: 16px; border-radius: 999px; }
.nc-skel-text { height: 12px; border-radius: 4px; }
.nc-skel-btn { width: 48px; height: 22px; border-radius: 4px; margin-right: 6px; }
@keyframes nc-skel-shimmer {
  0% { background-position: -240px 0; }
  100% { background-position: 240px 0; }
}

/* empty state — friendly puck + headline + hint instead of one line of
   gray text. centers in the entire row so it reads as "this section is
   empty" rather than "this column has no value". */
.nc-empty-state {
  text-align: center;
  padding: 36px 16px 40px;
}
.nc-empty-puck {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #f3f0ff;
  border: 2px solid #ddd2f2;
  color: #7c5dd0;
  font-size: 22px;
  margin-bottom: 12px;
  box-shadow:
    0 2px 5px rgba(124, 93, 208, 0.12),
    0 1px 2px rgba(124, 93, 208, 0.06);
}
.nc-empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
  letter-spacing: -0.1px;
}
.nc-empty-hint {
  font-size: 12.5px;
  color: #6c757d;
  max-width: 480px;
  margin: 0 auto;
  line-height: 1.45;
}

/* per-row action buttons in the announcements list. `display:flex`
   keeps them on one line with a consistent gap regardless of label
   length (Edit/View is 4 chars, Retract is 7). `flex-wrap: nowrap`
   pins them together when the column is narrow — the cell will scroll
   or expand rather than stack one under the other. */
.nc-actions-cell {
  white-space: nowrap;
}
.nc-action-btn + .nc-action-btn {
  margin-left: 6px;
}

.nc-detail-body h1, .nc-detail-body h2, .nc-detail-body h3 { margin-top: 1em; }
.nc-detail-body p { margin: 0.5em 0; }

/* FI-1432 list-as-landing: header bar over the table that holds the
   primary New Announcement CTA on the left and the system-wide stats
   row on the right. collapses on narrow screens so the CTA wraps above
   the stats rather than colliding with them. */
.nc-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nc-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 18px;
}

.nc-list-stats {
  margin-bottom: 0;
}

/* filter pill row — sits between the header and the table. compact
   chips with inline counts, neutral by default, FI-blue when active.
   "Active banners" carries an amber accent so it pulls the eye during
   an incident without needing the admin to know the filter taxonomy. */
.nc-list-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  margin-bottom: 8px;
  padding-bottom: 6px;
}
.nc-list-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #d6dde6;
  color: #495057;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.nc-list-filter:hover {
  background: #f6f9fc;
  border-color: #b8c9da;
  color: #2c3e50;
}
.nc-list-filter.active {
  background: #1976d2;
  border-color: #1976d2;
  color: #fff;
}
.nc-list-filter.active:hover {
  background: #1565c0;
  border-color: #1565c0;
}
.nc-list-filter .fa { font-size: 11px; }
.nc-list-filter-count {
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
  padding: 1px 6px;
  border-radius: 999px;
  margin-left: 2px;
  min-width: 18px;
  text-align: center;
}
.nc-list-filter.active .nc-list-filter-count {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}
/* "Active banners" amber accent — only when not selected. when active,
   the FI-blue fill takes over so the selection state stays consistent. */
.nc-list-filter--accent-warning:not(.active) {
  border-color: #fde68a;
  color: #b45309;
  background: #fffbeb;
}
.nc-list-filter--accent-warning:not(.active):hover {
  background: #fef3c7;
  border-color: #fcd34d;
  color: #92400e;
}
.nc-list-filter--accent-warning:not(.active) .fa {
  color: #d97706;
}

/* rows in the list are clickable (opens detail). keep the hover subtle
   so it doesn't fight with the zebra-ish background and doesn't make
   the Actions links look inert. drafts get a soft amber bar on the
   left so they stand out from published rows at a glance. */
.nc-row-clickable {
  cursor: pointer;
  transition: background 120ms ease;
}

.nc-row-clickable:hover {
  background: #f0f5ff;
}

.nc-row-draft {
  box-shadow: inset 3px 0 0 #f0b23a;
}

.nc-row-draft:hover {
  background: #fff8ec;
}

.nc-th-status {
  width: 1%;
  white-space: nowrap;
}

/* status badge: same visual family as nc-preview-badge so everything
   on this page reads as part of one system. two variants: draft (warm
   amber) and published (cool slate). */
.nc-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.nc-status-badge .fa {
  font-size: 10px;
}

.nc-status-draft {
  background: #fdf3dc;
  color: #8a5a05;
  border: 1px solid #f1d48b;
}

.nc-status-published {
  background: #e8edf3;
  color: #3a4a63;
  border: 1px solid #c9d3e1;
}

/* detail subpage: circular back button (matches support ticket detail
   pattern) + title row + optional "Saved N seconds ago" hint. keep the
   heading weight/size in line with other help-center detail screens. */
.nc-detail {
  max-width: 980px;
}

/* read-only mode adopts the user-facing announcement_detail.vue shell:
   tighter content column (960px), centered, with the back button
   absolute-positioned 46px left of the column so it lives in the
   page margin and the puck aligns flush with the content edge. */
.nc-detail--read {
  position: relative;
  max-width: 960px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 16px;
}

.nc-readonly-back-btn {
  position: absolute;
  /* 46 = 34 (btn) + 12 (gap) — matches announcement_detail.vue's
     back-btn float so the two pages align pixel-for-pixel. */
  left: -46px;
  top: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #d4d8dc;
  background: #fff;
  color: #495057;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.nc-readonly-back-btn:hover,
.nc-readonly-back-btn:focus {
  background: #f0f5ff;
  border-color: #4a7ba7;
  color: #1f4a73;
  outline: none;
}
.nc-readonly-back-btn .fa { line-height: 1; }

/* below 1060px there isn't enough page margin to float the back button
   outside the content. drop it inline as the first item above the hero
   so it stays usable on tablets/laptops without horizontal scroll. */
@media (max-width: 1060px) {
  .nc-readonly-back-btn {
    position: static;
    margin-bottom: 12px;
  }
  .nc-detail--read {
    padding: 0 12px;
  }
}

.nc-detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.nc-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f4f6f9;
  border: 1px solid #d9dfe6;
  color: #4a5568;
  font-size: 13px;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
  flex: 0 0 auto;
}

.nc-back-btn:hover {
  background: #e6ecf3;
  color: #1a202c;
  border-color: #b9c3d0;
}

.nc-detail-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1 1 auto;
  min-width: 0;
}

.nc-detail-heading {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  line-height: 1.2;
  word-break: break-word;
  flex: 1 1 auto;
  min-width: 0;
}

.nc-draft-saved {
  font-size: 12px;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.nc-draft-saved .fa {
  color: #4caf50;
  font-size: 11px;
}

/* admin-only operational section in the read-only detail view. wraps
   stats + banner-state and visually marks the whole block as admin
   chrome (amber border + soft tint) so it doesn't blend into the
   announcement content recipients see. eyebrow row pairs the shield
   marker with a one-line hint about the muggle-preview toggle, since
   that's how an admin verifies what recipients actually get. */
.nc-readonly-admin {
  position: relative;
  background: linear-gradient(180deg, #fff8ec 0%, #fef3e2 100%);
  border: 1px solid #f0c88a;
  border-radius: 12px;
  padding: 14px 18px 18px;
  box-shadow: 0 1px 2px rgba(196, 94, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.nc-readonly-admin-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #f0c88a;
}
.nc-readonly-admin-marker {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fef3e2;
  color: #8a4500;
  border: 1px solid #e0a866;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.nc-readonly-admin-marker .fa { font-size: 10px; }
.nc-readonly-admin-hint {
  font-size: 12px;
  color: #8a4500;
  line-height: 1.4;
  flex: 1 1 0;
  min-width: 200px;
}
.nc-readonly-admin-hint em {
  font-style: italic;
  font-weight: 600;
}
/* shared chip used inside the hero meta-line author block AND on the
   admin-only footer Retract row. matches the .help-tab-admin amber
   palette so admin chrome reads as one family across the page. */
.nc-readonly-admin-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fef3e2;
  color: #c45e00;
  border: 1px solid #f0c88a;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 999px;
  white-space: nowrap;
  vertical-align: middle;
}
.nc-readonly-admin-tag .fa { font-size: 9px; }
/* author span in the hero meta line gets a softer amber tint when
   admin-gated so it reads as "operational metadata" alongside the
   public timestamp. inherit from .nc-readonly-meta sizing. */
.nc-readonly-meta-author-admin {
  background: #fff8ec;
  border: 1px solid #f6d8a6;
  border-radius: 999px;
  padding: 1px 8px 1px 4px;
  color: #8a4500;
  gap: 6px !important;
}
.nc-readonly-meta-author-admin .fa-user-o { color: #c45e00 !important; }
.nc-readonly-meta-author-admin strong { color: #6b3700; font-weight: 700; }
/* admin-only footer actions row — left-aligns the admin tag, right-
   aligns the destructive button. dashed top border separates it from
   the body content above. */
.nc-readonly-actions-admin {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0 4px;
  border-top: 1px dashed #f0c88a;
}
.nc-readonly-actions-admin > .btn { margin-left: auto; }

/* read-only detail: hero, stats, banner state, body, footer. tighter
   typography rhythm + a stack of cards rather than a flat run of
   elements so each section reads as a discrete piece of information.
   max-width matches the user-facing /announcements/:id page. */
.nc-readonly {
  max-width: 980px;
}
.nc-readonly-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* HERO — kind-specific elevated puck on the left, title + meta + pills
   on the right. mirrors announcement_detail.vue but compacted slightly
   for the admin context (no extra page chrome around it). */
.nc-readonly-hero {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.nc-readonly-puck {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 2px solid;
  font-size: 20px;
  line-height: 1;
  box-sizing: border-box;
}
.nc-readonly-puck--ann {
  background: #f3f0ff;
  border-color: #c8bce8;
  color: #7c5dd0;
  box-shadow:
    0 2px 5px rgba(124, 93, 208, 0.16),
    0 1px 2px rgba(124, 93, 208, 0.08);
}
.nc-readonly-puck--alert {
  background: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
  box-shadow:
    0 2px 5px rgba(217, 119, 6, 0.16),
    0 1px 2px rgba(217, 119, 6, 0.08);
}
.nc-readonly-puck--resolved {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
  box-shadow:
    0 2px 5px rgba(5, 150, 105, 0.16),
    0 1px 2px rgba(5, 150, 105, 0.08);
}
.nc-readonly-hero-stack {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.nc-readonly-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  line-height: 1.25;
  word-break: break-word;
  letter-spacing: -0.1px;
}
.nc-readonly-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #6c757d;
  line-height: 1.3;
}
.nc-readonly-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.nc-readonly-meta-item .fa { font-size: 11px; color: #94a3b8; }
.nc-readonly-meta-sep { color: #cbd5e1; }
.nc-readonly-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

/* extra preview-badge variants used only in the read-only hero. soft
   pastels, same visual weight as the existing tenant/global/roles set. */
.nc-preview-badge-announcement {
  background: #f3f0ff;
  color: #5a3fb0;
  border: 1px solid #c8bce8;
}
.nc-preview-badge-service-alert {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}
.nc-preview-badge-service-resolved {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.nc-preview-badge-email {
  background: #ecfdf5;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

/* STATS card — three slots separated by thin vertical rules. headline
   numbers in graphite, fractions + bars muted. progress bars use the
   same green/blue accent pair already in the FA chat surface. */
.nc-readonly-stats {
  display: flex;
  align-items: stretch;
  gap: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 18px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.nc-readonly-stat {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 10px;
}
.nc-readonly-stat-with-bar {
  gap: 6px;
}
.nc-readonly-stat-divider {
  flex: 0 0 auto;
  width: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}
.nc-readonly-stat-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.nc-readonly-stat-num {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.1;
  letter-spacing: -0.4px;
  font-variant-numeric: tabular-nums;
}
.nc-readonly-stat-pct-suffix {
  font-size: 16px;
  font-weight: 600;
  color: #475569;
  margin-left: 1px;
}
.nc-readonly-stat-fraction {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.nc-readonly-stat-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}
.nc-readonly-stat-bar {
  position: relative;
  height: 4px;
  border-radius: 2px;
  background: #f1f5f9;
  overflow: hidden;
  margin-top: 4px;
}
.nc-readonly-stat-bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: 2px;
  transition: width 200ms ease;
}
.nc-readonly-stat-bar-fill--read {
  background: linear-gradient(90deg, #34a3ff, #1a78d6);
}
.nc-readonly-stat-bar-fill--actioned {
  background: linear-gradient(90deg, #10b981, #059669);
}

/* BANNER STATE panel — the canonical place to read banner status and
   clear it. amber while active, neutral once expired or off. icon
   left, two-line stack center, action button right. responsive: stacks
   on narrow viewports so the button doesn't get squeezed. */
.nc-readonly-banner-panel {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid;
  font-size: 13px;
}
.nc-readonly-banner-panel--on {
  background: linear-gradient(180deg, #fef3d5 0%, #fce9b9 100%);
  border-color: #f0a957;
  color: #6b3f00;
}
.nc-readonly-banner-panel--off {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #475569;
}
.nc-readonly-banner-icon {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.nc-readonly-banner-panel--on .nc-readonly-banner-icon {
  background: #fef3c7;
  border: 1.5px solid #fde68a;
  color: #d97706;
}
.nc-readonly-banner-panel--off .nc-readonly-banner-icon {
  background: #f1f5f9;
  border: 1.5px solid #cbd5e1;
  color: #64748b;
}
.nc-readonly-banner-stack {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nc-readonly-banner-status {
  font-weight: 700;
  font-size: 13px;
  line-height: 1.3;
}
/* dedicated time line — sits between status and hint. tabular numerals
   keep "in 3h 45m" / dates aligned and slightly heavier than the prose
   hint so it reads as a primary data point. */
.nc-readonly-banner-time {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  opacity: 0.95;
  margin-top: 1px;
}
.nc-readonly-banner-time .fa { font-size: 11px; opacity: 0.7; }
.nc-readonly-banner-hint {
  font-size: 12px;
  line-height: 1.35;
  opacity: 0.9;
}
.nc-readonly-banner-actions {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
}
.nc-readonly-banner-btn .fa { margin-right: 4px; }

@media (max-width: 640px) {
  .nc-readonly-banner-panel {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .nc-readonly-banner-actions { justify-content: flex-end; }
  .nc-readonly-stats {
    flex-direction: column;
    padding: 10px 14px;
  }
  .nc-readonly-stat { padding: 10px 0; }
  .nc-readonly-stat-divider { width: auto; height: 1px; margin: 0; }
}

/* BODY wrapper — gives the markdown/iframe content its own gentle
   surface so the long-form read isn't bumped against the stats card. */
.nc-readonly-body-wrap {
  padding-top: 4px;
  border-top: 1px solid #eef2f6;
}

.nc-readonly-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 6px;
}

/* compose actions row: Publish + Save draft + secondary actions.
   secondary (Preview/Markdown/Reset) sit on the right on wide screens,
   wrap below on narrow screens so they don't collide with the primary
   CTAs. */
.nc-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.nc-actions-secondary {
  margin-left: auto;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.nc-markdown-source {
  font-family: Menlo, Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.45;
  background: #f6f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  padding: 10px 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 480px;
  overflow-y: auto;
  color: #24292e;
}

/* banner preview block — shown above the notification card when the
   admin has service_alert + banner checked. mirrors the live banner in
   service_alert_banner.vue (amber gradient bg, amber border, icon puck)
   so the admin can sanity-check how the top-of-page strip will read
   before publishing. */
.nc-banner-preview-wrap {
  margin-bottom: 14px;
}

.nc-preview-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin-bottom: 6px;
}

.nc-preview-section-label--card {
  margin-top: 12px;
}

.nc-banner-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: linear-gradient(180deg, #fef3d5 0%, #fce9b9 100%);
  border: 1px solid #f0a957;
  border-radius: 6px;
  color: #7a4e00;
  font-size: 13px;
}

.nc-banner-preview-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 2px solid #fde68a;
  background: #fef3c7;
  color: #d97706;
  font-size: 18px;
  line-height: 1;
  box-sizing: border-box;
  box-shadow: 0 2px 5px rgba(217, 119, 6, 0.16), 0 1px 2px rgba(217, 119, 6, 0.08);
}

.nc-banner-preview-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nc-banner-preview-title {
  font-size: 15px;
  font-weight: 700;
  color: #6b3f00;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nc-banner-preview-text {
  color: #7a4e00;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.3;
}

.nc-banner-preview-read {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid #f0a957;
  color: #6b3f00;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

/* announcement preview: mirrors FA chat / help article reading surface.
   card shell, muted-pastel badges for target + author (doc-badge family),
   then delegates body typography to .markdown-content. */
.nc-preview-card {
  border: 1px solid #dce1e6;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.nc-preview-header {
  padding: 14px 18px 10px;
  border-bottom: 1px solid #edf0f3;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.nc-preview-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.3;
  margin-bottom: 8px;
  word-break: break-word;
}

.nc-preview-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
}

.nc-preview-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
}

.nc-preview-badge .fa { font-size: 10px; }

.nc-preview-badge-global {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  color: #0b3d91;
}

.nc-preview-badge-tenant {
  --badge-color: #6f42c1;
  --badge-bg: #f1ebff;
  color: #4b2c92;
}

.nc-preview-badge-author {
  --badge-color: #6c757d;
  --badge-bg: #f1f3f5;
  color: #495057;
}

.nc-preview-badge-roles {
  --badge-color: #d97706;
  --badge-bg: #fff4e0;
  color: #7c3a03;
}

.nc-preview-time {
  color: #8a8a8a;
  font-size: 11px;
  margin-left: auto;
  font-style: italic;
}

.nc-preview-body {
  padding: 14px 18px 18px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  text-align: left;
}

.nc-preview-empty {
  padding: 26px 18px;
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
  font-style: italic;
}

.nc-preview-empty .fa {
  display: block;
  font-size: 22px;
  margin-bottom: 6px;
  color: #b5b9bd;
}

.nc-preview-hint {
  margin-top: 10px;
  padding: 8px 12px;
  background: #f6f9fc;
  border: 1px dashed #cdd6e0;
  border-radius: 6px;
  color: #4a5568;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nc-preview-hint .fa { color: #6c757d; }

/* markdown variants (.markdown-page, .markdown-compact) moved to
   src/assets/css/markdown-variants.css so they apply to v-html content
   rendered outside this component (announcement_detail, future help
   article reader, etc.). imported from main.js. */

</style>
